import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/billingService';
import { authService } from '../services/authService';
import Sidebar from '../components/Sidebar';
import Dashboard from './Dashboard';
import AdminPanel from './AdminPanel';
import GraphExplorer from './GraphExplorer';
import Eikon from './Eikon';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';
import fcose from 'cytoscape-fcose';

// Register layouts - fcose for blockchain analytics style clustering
cytoscape.use(cola);
cytoscape.use(fcose);

const UnifiedDashboard = () => {
  const [activeTab, setActiveTab] = useState('console');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [networkUsers, setNetworkUsers] = useState([]);
  const [networkUsersLoading, setNetworkUsersLoading] = useState(false);
  const [selectedNetworkUser, setSelectedNetworkUser] = useState(null);
  const [selectedApiUser, setSelectedApiUser] = useState(null);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Fetch users when Network Explorer or API tab is active
  useEffect(() => {
    if (activeTab === 'network' || activeTab === 'api') {
      fetchNetworkUsers();
    }
  }, [activeTab]);

  const fetchNetworkUsers = async () => {
    setNetworkUsersLoading(true);
    try {
      const result = await authService.adminGetUsers();
      if (result.ok) {
        setNetworkUsers(result.data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users for network explorer:', err);
    } finally {
      setNetworkUsersLoading(false);
    }
  };

  // Handle selecting a contract for detailed view
  const handleContractSelect = (contractAddress) => {
    setSelectedContract(contractAddress);
  };

  // Handle going back from contract detail view
  const handleBackToSybil = () => {
    setSelectedContract(null);
  };

  // Redirect non-admin users to customer dashboard
  React.useEffect(() => {
    if (!loading && user && !user.is_admin) {
      navigate('/customer-dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="bg-dark text-light d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // If not admin, show nothing while redirecting
  if (!user?.is_admin) {
    return null;
  }

  const handleManageBilling = async () => {
    try {
      const result = await billingService.getPortalUrl();
      if (result.ok && result.url) {
        window.location.href = result.url;
      } else {
        alert('Failed to open billing portal.');
      }
    } catch (error) {
      console.error('Billing portal error:', error);
      alert('Failed to open billing portal.');
    }
  };

  const tier = user?.tier || 'free';
  const isAdmin = user?.is_admin || false;
  const displayTier = isAdmin ? 'admin' : tier;

  // Calculate left margin based on sidebar
  const mainSidebarWidth = sidebarCollapsed ? 80 : 260;

  return (
    <div className="d-flex bg-dark text-light" style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onManageBilling={tier !== 'free' && tier !== 'admin' && !isAdmin ? handleManageBilling : null}
        networkUsers={networkUsers}
        networkUsersLoading={networkUsersLoading}
        selectedNetworkUser={selectedNetworkUser}
        setSelectedNetworkUser={setSelectedNetworkUser}
        selectedApiUser={selectedApiUser}
        setSelectedApiUser={setSelectedApiUser}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      {/* Main Content Area */}
      <div
        className="flex-grow-1"
        style={{
          marginLeft: `${mainSidebarWidth}px`,
          transition: 'margin-left 0.3s ease',
          minHeight: '100vh',
          backgroundColor: '#000000'
        }}
      >
        {/* Top Header Bar - hidden on Eikon tab so the iframe owns the full top strip */}
        {activeTab !== 'eikon' && (
        <div className="bg-black border-bottom border-secondary sticky-top" style={{ zIndex: 100 }}>
          <div className="container-fluid py-3 px-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>
                  {activeTab === 'console' && 'Query Console'}
                  {activeTab === 'sybil' && !selectedContract && 'Sybil Defender'}
                  {activeTab === 'sybil' && selectedContract && (
                    <>
                      <button
                        className="btn btn-link text-secondary p-0 me-2"
                        onClick={handleBackToSybil}
                        style={{ fontSize: '1.2rem' }}
                      >
                        <i className="bi bi-arrow-left"></i>
                      </button>
                      Contract Analysis
                    </>
                  )}
                  {activeTab === 'network' && (selectedNetworkUser ? 'user interface' : 'User Dashboards')}
                  {activeTab === 'plume' && 'Plume Dashboard'}
                  {activeTab === 'graph' && 'Graph Explorer'}
                  {activeTab === 'api' && (selectedApiUser ? (networkUsers.find(u => u.id === selectedApiUser)?.email === 'signxy@proton.me' ? 'Demo API' : 'API Documentation') : 'API & Documentation')}
                </h4>
                <p className="text-secondary small mb-0">
                  {activeTab === 'console' && 'Ask natural language questions about blockchain data'}
                  {activeTab === 'sybil' && !selectedContract && 'Real-time Sybil detection and fraud analysis'}
                  {activeTab === 'sybil' && selectedContract && `Detailed sybil analysis for ${selectedContract}`}
                  {activeTab === 'network' && (selectedNetworkUser ? `Viewing dashboard for ${networkUsers.find(u => u.id === selectedNetworkUser)?.display_name || (networkUsers.find(u => u.id === selectedNetworkUser)?.email === 'signxy@proton.me' ? 'demo' : networkUsers.find(u => u.id === selectedNetworkUser)?.email) || 'user'}` : 'Manage all user accounts and billing')}
                  {activeTab === 'plume' && 'Plume network sybil cluster analysis and visualization'}
                  {activeTab === 'graph' && 'Interactive graph visualization and network analysis'}
                  {activeTab === 'api' && (selectedApiUser ? `Endpoints and documentation for ${networkUsers.find(u => u.id === selectedApiUser)?.email === 'signxy@proton.me' ? 'demo' : networkUsers.find(u => u.id === selectedApiUser)?.email?.split('@')[0] || 'user'}` : 'Select a user from the sidebar to view their API documentation')}
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span className={`badge ${displayTier === 'admin' ? 'bg-danger text-white' :
                  displayTier === 'enterprise' ? 'bg-warning text-dark' :
                    displayTier === 'basic' ? 'bg-primary' :
                      'bg-secondary'
                  }`}>
                  {displayTier.toUpperCase()} {displayTier === 'admin' ? '' : 'PLAN'}
                </span>
                {tier === 'free' && !isAdmin && (
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => window.location.href = '/pricing.html#/pricing'}
                  >
                    <i className="bi bi-arrow-up-circle me-1"></i>
                    Upgrade
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Content */}
        {activeTab === 'eikon' && (
          <Eikon />
        )}
        {activeTab === 'console' && (
          <Dashboard embedded={true} />
        )}
        {activeTab === 'sybil' && !selectedContract && (
          <SybilDefenderDashboardContent onContractSelect={handleContractSelect} />
        )}
        {activeTab === 'sybil' && selectedContract && (
          <ContractClusterViewContent contractAddress={selectedContract} onBack={handleBackToSybil} />
        )}
        {activeTab === 'network' && (
          selectedNetworkUser ? (
            <NetworkExplorerContent
              selectedUserId={selectedNetworkUser}
              selectedUserEmail={networkUsers.find(u => u.id === selectedNetworkUser)?.email}
              selectedUserDisplayName={networkUsers.find(u => u.id === selectedNetworkUser)?.display_name}
            />
          ) : (
            <AdminPanel />
          )
        )}
        {activeTab === 'plume' && (
          <PlumeDashboardContent />
        )}
        {activeTab === 'graph' && (
          <GraphExplorer />
        )}
        {activeTab === 'api' && (
          <ApiDashboardContent
            selectedUserId={selectedApiUser}
            selectedUserEmail={networkUsers.find(u => u.id === selectedApiUser)?.email}
            networkUsers={networkUsers}
          />
        )}
      </div>
    </div>
  );
};

// Embedded Sybil Admin Dashboard without its own navbar
const SybilDefenderDashboardContent = ({ onContractSelect }) => {
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  React.useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3000'
        : '/api'; // Use /api/ prefix for nginx proxy

      // Fetch all admin endpoints in parallel
      const [statsRes, contractsRes, methodsRes] = await Promise.all([
        fetch(`${baseUrl}/v1/sybil/admin/stats`, { credentials: 'include' }),
        fetch(`${baseUrl}/v1/sybil/admin/contracts`, { credentials: 'include' }),
        fetch(`${baseUrl}/v1/sybil/admin/methods`, { credentials: 'include' })
      ]);

      if (!statsRes.ok || !contractsRes.ok || !methodsRes.ok) {
        throw new Error('Failed to fetch admin data');
      }

      const [statsData, contractsData, methodsData] = await Promise.all([
        statsRes.json(),
        contractsRes.json(),
        methodsRes.json()
      ]);

      setStats(statsData);
      setContracts(contractsData);
      setMethods(methodsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const filteredContracts = selectedCategory === 'all'
    ? contracts
    : contracts.filter(c => c.category === selectedCategory);

  const categories = [...new Set(contracts.map(c => c.category))].sort();

  return (
    <div className="container-fluid my-4 px-4">
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary">Loading sybil detection data...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error loading data: {error}
        </div>
      ) : (
        <>
          {/* Hero Stats */}
          <div className="row mb-4">
            <div className="col-12">
              <h2 className="mb-3">
                <i className="bi bi-shield-exclamation text-danger me-2"></i>
                Sybil Detection Admin Dashboard
              </h2>
              <p className="text-secondary">Real-time view of sybil activity across Ethereum mainnet</p>
            </div>
          </div>

          {/* Key Metrics */}
          {stats && (
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="card bg-secondary-subtle text-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Total Sybil Wallets</h6>
                        <h3 className="mb-0">{formatNumber(stats.total_sybil_wallets)}</h3>
                      </div>
                      <i className="bi bi-wallet2 text-danger" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-secondary-subtle text-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Sybil Clusters</h6>
                        <h3 className="mb-0">{formatNumber(stats.total_clusters)}</h3>
                      </div>
                      <i className="bi bi-diagram-3 text-warning" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-secondary-subtle text-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Affected Contracts</h6>
                        <h3 className="mb-0">{formatNumber(stats.total_contracts_affected)}</h3>
                      </div>
                      <i className="bi bi-file-earmark-code text-info" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-secondary-subtle text-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Total Interactions</h6>
                        <h3 className="mb-0">{formatNumber(stats.total_interactions)}</h3>
                      </div>
                      <i className="bi bi-arrow-left-right text-success" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Typologies Detected */}
          {stats && stats.typologies && stats.typologies.length > 0 && (
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="bi bi-tags text-warning me-2"></i>
                  Detected Typologies
                </h5>
                <div className="row g-3">
                  {stats.typologies.map((typ, idx) => (
                    <div key={idx} className="col-md-4">
                      <div className="p-3 bg-dark rounded">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0">{typ.typology || 'Unknown'}</h6>
                          <span className="badge bg-danger">{formatNumber(typ.wallet_count)}</span>
                        </div>
                        <div className="text-secondary small">
                          <div><strong>{formatNumber(typ.wallet_count)}</strong> wallets</div>
                          <div><strong>{formatNumber(typ.cluster_count)}</strong> clusters</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Most Called Methods */}
          {methods && methods.length > 0 && (
            <div className="card bg-secondary-subtle text-light mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="bi bi-code-slash text-info me-2"></i>
                  Top Methods Called by Sybils
                </h5>
                <p className="text-secondary small mb-3">Most frequently called contract methods</p>
                <div className="table-responsive">
                  <table className="table table-dark table-hover table-sm">
                    <thead>
                      <tr>
                        <th>Method Signature</th>
                        <th>Method Name</th>
                        <th className="text-end">Total Calls</th>
                        <th className="text-end">Unique Contracts</th>
                        <th className="text-end">Unique Wallets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {methods.slice(0, 15).map((method, idx) => (
                        <tr key={idx}>
                          <td><code className="text-warning">{method.method_signature}</code></td>
                          <td className="small">{method.method_name}</td>
                          <td className="text-end">
                            <span className="badge bg-primary">{formatNumber(method.call_count)}</span>
                          </td>
                          <td className="text-end text-secondary">{formatNumber(method.unique_contracts)}</td>
                          <td className="text-end text-secondary">{formatNumber(method.unique_wallets)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Most Affected Contracts */}
          <div className="card bg-secondary-subtle text-light mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="card-title mb-1">
                    <i className="bi bi-bullseye text-danger me-2"></i>
                    Most Affected Contracts by Sybil Activity
                  </h5>
                  <p className="text-secondary small mb-0">Sorted by total interaction volume</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <label className="text-secondary small mb-0">Filter:</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {filteredContracts.length === 0 ? (
                <div className="text-center py-4 text-secondary">
                  <p>No contracts found for this category</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <table className="table table-dark table-hover table-sm">
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#212529', zIndex: 1 }}>
                      <tr>
                        <th>Contract</th>
                        <th>Category</th>
                        <th className="text-end">Sybil Wallets</th>
                        <th className="text-end">Clusters</th>
                        <th className="text-end">Total Calls</th>
                        <th>Typologies</th>
                        <th>First/Last Seen</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContracts.map((contract, idx) => (
                        <tr
                          key={idx}
                          style={{ cursor: 'pointer' }}
                          onClick={() => onContractSelect(contract.contract)}
                          className="contract-row-clickable"
                        >
                          <td>
                            <code className="text-info">{contract.contract}</code>
                            <button
                              className="btn btn-link btn-sm p-0 ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(contract.contract);
                              }}
                              title="Copy full address"
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </td>
                          <td>
                            <span className={`badge ${contract.category === 'dex_router' ? 'bg-warning text-dark' :
                              contract.category === 'usdc' ? 'bg-success' :
                                'bg-secondary'
                              }`}>
                              {contract.category}
                            </span>
                          </td>
                          <td className="text-end">
                            <span className="badge bg-danger">{formatNumber(contract.total_sybil_wallets)}</span>
                          </td>
                          <td className="text-end">
                            <span className="badge bg-warning text-dark">{formatNumber(contract.total_clusters)}</span>
                          </td>
                          <td className="text-end">
                            <strong className="text-primary">{formatNumber(contract.total_calls)}</strong>
                          </td>
                          <td className="small">
                            {contract.typologies && contract.typologies.length > 0
                              ? contract.typologies.slice(0, 2).join(', ')
                              : '-'
                            }
                          </td>
                          <td className="small text-secondary">
                            {new Date(contract.first_seen).toLocaleDateString()}
                            {' → '}
                            {new Date(contract.last_seen).toLocaleDateString()}
                          </td>
                          <td>
                            <i className="bi bi-chevron-right text-secondary"></i>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredContracts.length > 0 && (
                <div className="text-center mt-3">
                  <small className="text-secondary">
                    Showing {formatNumber(filteredContracts.length)} contracts
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Customer Targeting Insights */}
          <div className="card bg-secondary-subtle text-light mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-bar-chart text-success me-2"></i>
                Customer Targeting Insights
              </h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 bg-dark rounded">
                    <h6 className="mb-3">Top DEX Protocols Affected</h6>
                    {(() => {
                      const dexStats = {};
                      contracts.forEach(c => {
                        if (c.dex_protocol) {
                          if (!dexStats[c.dex_protocol]) {
                            dexStats[c.dex_protocol] = { calls: 0, contracts: 0 };
                          }
                          dexStats[c.dex_protocol].calls += c.total_calls;
                          dexStats[c.dex_protocol].contracts += 1;
                        }
                      });
                      return Object.entries(dexStats)
                        .sort((a, b) => b[1].calls - a[1].calls)
                        .slice(0, 5)
                        .map(([protocol, data]) => (
                          <div key={protocol} className="d-flex justify-content-between mb-2">
                            <span>{protocol}</span>
                            <span className="text-secondary">
                              {formatNumber(data.calls)} calls across {data.contracts} contracts
                            </span>
                          </div>
                        ));
                    })()}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 bg-dark rounded">
                    <h6 className="mb-3">High-Priority Contract Categories</h6>
                    {(() => {
                      const categoryStats = {};
                      contracts.forEach(c => {
                        if (!categoryStats[c.category]) {
                          categoryStats[c.category] = { calls: 0, count: 0 };
                        }
                        categoryStats[c.category].calls += c.total_calls;
                        categoryStats[c.category].count += 1;
                      });
                      return Object.entries(categoryStats)
                        .sort((a, b) => b[1].calls - a[1].calls)
                        .slice(0, 5)
                        .map(([category, data]) => (
                          <div key={category} className="d-flex justify-content-between mb-2">
                            <span className="badge bg-secondary">{category}</span>
                            <span className="text-secondary">
                              {formatNumber(data.calls)} calls ({data.count} contracts)
                            </span>
                          </div>
                        ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Contract Cluster View Content (embedded within dashboard)
const ContractClusterViewContent = ({ contractAddress, onBack }) => {
  const [contractData, setContractData] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [methods, setMethods] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterGraph, setClusterGraph] = useState(null);
  const [graphStats, setGraphStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [methodInputs, setMethodInputs] = useState({});
  const [loadingMethodInputs, setLoadingMethodInputs] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null); // Active filter: { methodSig, methodName, targetAddress, paramLabel }
  const [filteredClusters, setFilteredClusters] = useState(null); // Clusters matching the active filter
  const [loadingFilteredClusters, setLoadingFilteredClusters] = useState(false); // Loading state for filtered clusters
  const [highlightInteracting, setHighlightInteracting] = useState(false); // Highlight wallets that interacted with contract
  const [actualInteractingWallets, setActualInteractingWallets] = useState(null); // Actual wallets that called the contract (from sybil_tx_history)
  const [loadingInteracting, setLoadingInteracting] = useState(false); // Loading state for interacting wallets

  const containerRef = useRef(null);
  const cyRef = useRef(null);

  const GRAPH_API_URL = process.env.REACT_APP_GRAPH_API_URL ||
    (window.location.hostname === 'localhost' && window.location.port === '5173' ? 'http://localhost:3001' : '/graph-api');

  const baseUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : '/api';

  useEffect(() => {
    if (contractAddress) {
      fetchContractData();
    }
  }, [contractAddress]);

  const fetchContractData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsRes, clustersRes, methodsRes] = await Promise.all([
        fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/stats`, { credentials: 'include' }),
        fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/clusters`, { credentials: 'include' }),
        fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setContractData(statsData);
      }

      if (clustersRes.ok) {
        const clustersData = await clustersRes.json();
        const clusterList = (clustersData.clusters || []).map(c => ({
          id: c.cluster_id,
          walletCount: c.wallet_count,
          callCount: c.call_count || 0,
          typologies: c.typologies || (c.typology ? [c.typology] : []),
          firstSeen: c.first_seen,
          lastSeen: c.last_seen
        }));
        setClusters(clusterList);

        if (clusterList.length > 0) {
          setSelectedCluster(clusterList[0]);
          fetchClusterGraph(clusterList[0]);
        }
      }

      if (methodsRes.ok) {
        const methodsData = await methodsRes.json();
        setMethods(methodsData.methods || []);
      }
    } catch (err) {
      console.error('Error fetching contract data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClusterGraph = async (cluster) => {
    if (!cluster || !cluster.id) return;

    setLoadingGraph(true);
    setClusterGraph(null);
    setGraphStats(null);
    setSelectedNode(null);

    try {
      // Fetch full community graph - high max_nodes to get ALL nodes and edges
      // The API will return all edges within the community when community is specified
      const response = await fetch(
        `${GRAPH_API_URL}/api/graph/full?community=${cluster.id}&chain=mainnet&max_nodes=50000`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.graph) {
          const nodes = data.graph.nodes || [];
          const edges = data.graph.edges || [];

          // Calculate graph stats
          const samplingInfo = data.sampling || {};
          const totalCommunityNodes = samplingInfo.total_nodes || data.stats?.full_community_size || nodes.length;

          setGraphStats({
            displayedNodes: nodes.length,
            displayedEdges: edges.length,
            totalNodes: totalCommunityNodes,
            sampled: samplingInfo.enabled || (totalCommunityNodes > nodes.length),
            sybilInCluster: cluster.walletCount,
            callCount: cluster.callCount,
            firstSeen: cluster.firstSeen,
            lastSeen: cluster.lastSeen
          });

          setClusterGraph({ nodes, edges });
        }
      }
    } catch (err) {
      console.error('Error fetching cluster graph:', err);
    } finally {
      setLoadingGraph(false);
    }
  };

  const handleClusterSelect = (cluster) => {
    setSelectedCluster(cluster);
    setHighlightInteracting(false); // Reset highlight when switching clusters
    setActualInteractingWallets(null); // Clear cached interacting wallets
    fetchClusterGraph(cluster);
  };

  // Fetch actual interacting wallets from sybil_tx_history
  // This returns ONLY the wallets that truly called the contract
  const fetchActualInteractingWallets = async (clusterId) => {
    if (!clusterId || !contractAddress) return;

    setLoadingInteracting(true);
    try {
      const response = await fetch(
        `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/clusters/${encodeURIComponent(clusterId)}/interacting-wallets`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        // Convert to a Set of wallet addresses for fast lookup
        const walletSet = new Set((data.interacting_wallets || []).map(w => w.wallet.toLowerCase()));
        setActualInteractingWallets({
          wallets: walletSet,
          count: data.interacting_count || 0,
          totalInCluster: data.total_in_cluster || 0
        });
      }
    } catch (err) {
      console.error('Error fetching actual interacting wallets:', err);
    } finally {
      setLoadingInteracting(false);
    }
  };

  // Fetch method input data (parameter breakdown) when expanding a method row
  const fetchMethodInputs = async (methodSig) => {
    if (methodInputs[methodSig]) {
      return methodInputs[methodSig];
    }

    setLoadingMethodInputs(true);
    try {
      const response = await fetch(
        `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods/${encodeURIComponent(methodSig)}/inputs`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setMethodInputs(prev => ({
          ...prev,
          [methodSig]: data
        }));
        return data;
      }
    } catch (err) {
      console.error('Error fetching method inputs:', err);
    } finally {
      setLoadingMethodInputs(false);
    }
    return null;
  };

  // Handle method row click to expand/collapse
  const handleMethodClick = async (method) => {
    const methodSig = method.method_signature;

    if (expandedMethod === methodSig) {
      setExpandedMethod(null);
    } else {
      setExpandedMethod(methodSig);
      await fetchMethodInputs(methodSig);
    }
  };

  // Format address for display
  const formatAddress = (addr) => {
    if (!addr || addr.length < 10) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Fetch clusters filtered by method and target address
  const fetchFilteredClusters = async (methodSig, targetAddress, methodName, paramLabel) => {
    setLoadingFilteredClusters(true);
    setHighlightInteracting(false);
    setActualInteractingWallets(null);
    try {
      const response = await fetch(
        `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods/${encodeURIComponent(methodSig)}/inputs/${encodeURIComponent(targetAddress)}/clusters`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const clusterList = (data.clusters || []).map(c => ({
          id: c.cluster_id,
          walletCount: c.wallet_count,
          matchingWallets: c.matching_wallets,
          callCount: c.matching_wallets || 0,
          typologies: c.typologies || [],
          firstSeen: c.first_seen,
          lastSeen: c.last_seen
        }));
        setFilteredClusters(clusterList);

        // Set active filter info
        setActiveFilter({
          methodSig,
          methodName: methodName || methodSig,
          targetAddress,
          paramLabel: paramLabel || 'target'
        });

        // Select first filtered cluster if available
        if (clusterList.length > 0) {
          setSelectedCluster(clusterList[0]);
          fetchClusterGraph(clusterList[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching filtered clusters:', err);
    } finally {
      setLoadingFilteredClusters(false);
    }
  };

  // Fetch clusters filtered by bridge destination chain
  // Fetch clusters filtered by bridge destination chain
  const fetchFilteredClustersByChain = async (methodSig, chainId, chainName, methodName) => {
    setLoadingFilteredClusters(true);
    setHighlightInteracting(false);
    setActualInteractingWallets(null);
    try {
      const response = await fetch(
        `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods/${encodeURIComponent(methodSig)}/chains/${encodeURIComponent(chainId)}/clusters`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const clusterList = (data.clusters || []).map(c => ({
          id: c.cluster_id,
          walletCount: c.wallet_count,
          matchingWallets: c.matching_wallets,
          callCount: c.matching_wallets || 0,
          typologies: c.typologies || [],
          firstSeen: c.first_seen,
          lastSeen: c.last_seen
        }));
        setFilteredClusters(clusterList);

        // Set active filter info with chain details
        setActiveFilter({
          methodSig,
          methodName: methodName || methodSig,
          targetAddress: chainId.toString(),
          paramLabel: `→ ${chainName}`,
          isBridgeFilter: true,
          chainId: chainId,
          chainName: chainName
        });

        // Select first filtered cluster if available
        if (clusterList.length > 0) {
          setSelectedCluster(clusterList[0]);
          fetchClusterGraph(clusterList[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching filtered clusters by chain:', err);
    } finally {
      setLoadingFilteredClusters(false);
    }
  };

  // Fetch wallets that bridged to a specific chain (for highlighting)
  const fetchWalletsByChain = async (clusterId, chainId) => {
    setLoadingInteracting(true);
    try {
      const response = await fetch(
        `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/clusters/${encodeURIComponent(clusterId)}/chains/${encodeURIComponent(chainId)}/wallets`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const walletSet = new Set((data.wallets || []).map(w => w.wallet.toLowerCase()));
        setActualInteractingWallets({
          wallets: walletSet,
          count: walletSet.size,
          totalClusterSize: data.total_cluster_size || 0
        });
      }
    } catch (err) {
      console.error('Error fetching wallets by chain:', err);
    } finally {
      setLoadingInteracting(false);
    }
  };

  // Handle clicking on an input parameter row to filter clusters
  const handleInputClick = (e, input, method) => {
    e.stopPropagation(); // Prevent collapsing the method row

    // Check if this is a bridge destination (has chain_id)
    if (input.is_bridge_destination && input.chain_id) {
      fetchFilteredClustersByChain(
        method.method_signature,
        input.chain_id,
        input.chain_name || 'Unknown',
        method.method_name
      );
    } else {
      fetchFilteredClusters(
        method.method_signature,
        input.address,
        method.method_name,
        input.param_label
      );
    }
  };

  // Clear the active filter and show all clusters
  const clearFilter = () => {
    setActiveFilter(null);
    setFilteredClusters(null);
    setHighlightInteracting(false);
    setActualInteractingWallets(null);
    // Re-select first cluster from full list
    if (clusters.length > 0) {
      setSelectedCluster(clusters[0]);
      fetchClusterGraph(clusters[0]);
    }
  };

  // Render cytoscape graph
  useEffect(() => {
    if (!clusterGraph || !containerRef.current) return;

    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }

    const elements = [];

    (clusterGraph.nodes || []).forEach(node => {
      // Check if this node ACTUALLY interacted with the current contract
      // Uses actualInteractingWallets from sybil_tx_history (accurate wallet-level data)
      // rather than node.interacting_contracts (cluster-level aggregated data that's copied to all nodes)
      let interactsWithContract = false;
      if (actualInteractingWallets && actualInteractingWallets.wallets) {
        // Use the accurate data from sybil_tx_history
        interactsWithContract = actualInteractingWallets.wallets.has(node.id.toLowerCase());
      }

      elements.push({
        data: {
          id: node.id,
          label: node.id.substring(0, 8) + '...',
          fullAddress: node.id,
          isSybil: (node.is_sybil || (node.status && node.status.includes('Sybil'))) ? 'yes' : 'no',
          typology: node.typology || '',
          interactsWithContract: interactsWithContract,
          // Use string 'yes' for highlighted to work with cytoscape selectors
          highlighted: (highlightInteracting && interactsWithContract) ? 'yes' : 'no'
        }
      });
    });

    (clusterGraph.edges || []).forEach(edge => {
      elements.push({
        data: {
          id: `${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target
        }
      });
    });

    if (elements.length === 0) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      minZoom: 0.2,
      maxZoom: 3,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#6c757d',
            'label': 'data(label)',
            'font-size': 8,
            'color': '#fff',
            'text-valign': 'bottom',
            'text-margin-y': 5,
            'width': 30,
            'height': 30,
            'border-width': 2,
            'border-color': '#6c757d',
            'border-style': 'dashed'
          }
        },
        {
          selector: 'node[isSybil = "yes"]',
          style: {
            'background-color': '#da4b4b',
            'border-color': '#da4b4b',
            'border-width': 3,
            'border-style': 'dashed'
          }
        },
        {
          selector: 'edge',
          style: {
            'line-color': '#6c757d',
            'width': 1.5,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#6c757d',
            'curve-style': 'bezier',
            'arrow-scale': 0.8
          }
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 4,
            'border-color': '#ffffff'
          }
        },
        {
          selector: 'node[highlighted = "yes"]',
          style: {
            'border-width': 4,
            'border-color': '#ffffff',
            'border-style': 'solid',
            'background-color': '#ffffff',
            'z-index': 999
          }
        }
      ],
      // FCOSE: Industry-standard for blockchain analytics (Chainalysis/TRM style)
      layout: {
        name: 'fcose',
        animate: false,
        fit: true,
        padding: 40,
        quality: 'default',
        randomize: true,
        nodeSeparation: 100,
        idealEdgeLength: 80,
        edgeElasticity: 0.45,
        nestingFactor: 0.1,
        gravity: 0.25,
        gravityRange: 3.8,
        nodeRepulsion: 6500,
        numIter: 2500,
        tile: true,
        packComponents: true
      }
    });

    cy.on('tap', 'node', function (evt) {
      const node = evt.target;
      const data = node.data();
      setSelectedNode({
        address: data.fullAddress,
        isSybil: data.isSybil,
        typology: data.typology,
        connections: node.degree()
      });
    });

    cy.on('tap', function (evt) {
      if (evt.target === cy) {
        setSelectedNode(null);
      }
    });

    cy.ready(() => {
      cy.fit(undefined, 30);
    });

    cyRef.current = cy;

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [clusterGraph, highlightInteracting, contractAddress, actualInteractingWallets]);

  const formatNumber = (num) => (num || 0).toLocaleString();

  if (loading) {
    return (
      <div className="container-fluid my-4 px-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary">Loading contract data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid my-4 px-4">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid my-4 px-4">
      {/* Contract Info Card */}
      <div className="card bg-secondary-subtle text-light mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="text-secondary mb-2">Contract Address</h6>
              <code className="text-warning fs-5">{contractAddress}</code>
              <button
                className="btn btn-link btn-sm ms-2"
                onClick={() => navigator.clipboard.writeText(contractAddress)}
              >
                <i className="bi bi-clipboard text-secondary"></i>
              </button>
            </div>
            <a
              href={`https://etherscan.io/address/${contractAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-info btn-sm"
            >
              <i className="bi bi-box-arrow-up-right me-1"></i>
              Etherscan
            </a>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      {contractData && (
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card bg-secondary-subtle text-light h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-secondary mb-1">Sybil Wallets</h6>
                    <h3 className="mb-0 text-danger">
                      {formatNumber(contractData.total_wallets || contractData.total_sybil_wallets)}
                    </h3>
                  </div>
                  <i className="bi bi-wallet2 text-danger" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-secondary-subtle text-light h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-secondary mb-1">Sybil Clusters</h6>
                    <h3 className="mb-0 text-warning">{formatNumber(contractData.total_clusters)}</h3>
                  </div>
                  <i className="bi bi-diagram-3 text-warning" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-secondary-subtle text-light h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-secondary mb-1">Total Interactions</h6>
                    <h3 className="mb-0 text-info">
                      {formatNumber(contractData.total_calls || contractData.total_interactions)}
                    </h3>
                  </div>
                  <i className="bi bi-arrow-left-right text-info" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-secondary-subtle text-light h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-secondary mb-1">Activity Period</h6>
                    <p className="mb-0 small text-light">
                      {contractData.first_seen ? new Date(contractData.first_seen).toLocaleDateString() : '-'}
                      {' → '}
                      {contractData.last_seen ? new Date(contractData.last_seen).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <i className="bi bi-calendar-range text-success" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detected Typologies */}
      {contractData?.typologies && contractData.typologies.length > 0 && (
        <div className="card bg-secondary-subtle text-light mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="bi bi-tags text-warning me-2"></i>
              Detected Attack Typologies
            </h5>
            <div className="d-flex flex-wrap gap-2">
              {contractData.typologies.map((typ, idx) => (
                <span key={idx} className="badge bg-danger fs-6 px-3 py-2">
                  {typ}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Methods Called by Sybils */}
      {methods.length > 0 && (
        <div className="card bg-secondary-subtle text-light mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="bi bi-code-slash text-info me-2"></i>
              Methods Called by Sybils ({methods.length})
            </h5>
            <p className="text-secondary small mb-3">Click a method row to view input parameter breakdown</p>
            <div className="table-responsive">
              <table className="table table-dark table-hover table-sm">
                <thead>
                  <tr>
                    <th style={{ width: '24px' }}></th>
                    <th>Method Signature</th>
                    <th>Method Name</th>
                    <th className="text-end">Total Calls</th>
                    <th className="text-end">Unique Wallets</th>
                    <th className="text-end">Unique Clusters</th>
                    <th>Activity Range</th>
                  </tr>
                </thead>
                <tbody>
                  {methods.map((method, idx) => (
                    <React.Fragment key={idx}>
                      <tr
                        onClick={() => handleMethodClick(method)}
                        style={{ cursor: 'pointer' }}
                        className={expandedMethod === method.method_signature ? 'table-active' : ''}
                      >
                        <td>
                          <i className={`bi bi-chevron-${expandedMethod === method.method_signature ? 'down' : 'right'} text-secondary`}></i>
                        </td>
                        <td><code className="text-warning">{method.method_signature}</code></td>
                        <td className="small">{method.method_name || '-'}</td>
                        <td className="text-end">
                          <span className="badge bg-primary">{formatNumber(method.call_count)}</span>
                        </td>
                        <td className="text-end text-secondary">{formatNumber(method.unique_wallets)}</td>
                        <td className="text-end text-secondary">{formatNumber(method.unique_clusters)}</td>
                        <td className="small text-secondary">
                          {method.first_seen ? new Date(method.first_seen).toLocaleDateString() : '-'}
                          {' → '}
                          {method.last_seen ? new Date(method.last_seen).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                      {/* Expanded row showing input data breakdown */}
                      {expandedMethod === method.method_signature && (
                        <tr>
                          <td colSpan="7" className="p-0">
                            <div className="bg-dark p-3 border-top border-bottom border-secondary">
                              {loadingMethodInputs ? (
                                <div className="text-center py-3">
                                  <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                  <span className="text-secondary">Loading input data...</span>
                                </div>
                              ) : methodInputs[method.method_signature] ? (
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="text-info mb-0">
                                      {methodInputs[method.method_signature].is_bridge_method ? (
                                        <>
                                          <i className="bi bi-arrow-left-right me-2"></i>
                                          Destination Chains
                                          <span className="badge ms-2 bg-primary">BRIDGE</span>
                                        </>
                                      ) : methodInputs[method.method_signature].is_uniswap_swap ? (
                                        <>
                                          <i className="bi bi-coin me-2"></i>
                                          Target Tokens {methodInputs[method.method_signature].swap_direction === 'buy' ? 'Bought' : 'Sold'}
                                          {methodInputs[method.method_signature].swap_direction && (
                                            <span className={`badge ms-2 ${methodInputs[method.method_signature].swap_direction === 'buy' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                              {methodInputs[method.method_signature].swap_direction === 'buy' ? '↓ BUY' : '↑ SELL'}
                                            </span>
                                          )}
                                        </>
                                      ) : methodInputs[method.method_signature].is_liquidity_method ? (
                                        <>
                                          <i className="bi bi-droplet me-2"></i>
                                          Liquidity Pool Tokens
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-diagram-2 me-2"></i>
                                          Input Parameters for {methodInputs[method.method_signature].method_name}
                                        </>
                                      )}
                                    </h6>
                                    <span className="badge bg-secondary">
                                      {methodInputs[method.method_signature].input_count} unique {
                                        methodInputs[method.method_signature].is_bridge_method ? 'chains' :
                                          methodInputs[method.method_signature].is_uniswap_swap ? 'tokens' : 'targets'
                                      }
                                    </span>
                                  </div>
                                  {methodInputs[method.method_signature].is_bridge_method && (
                                    <div className="alert alert-primary py-2 px-3 mb-3" style={{ fontSize: '0.75rem' }}>
                                      <i className="bi bi-info-circle me-1"></i>
                                      These are the destination chains sybil wallets are bridging funds to. This can reveal cross-chain sybil activity patterns and coordination.
                                    </div>
                                  )}
                                  {methodInputs[method.method_signature].is_uniswap_swap && (
                                    <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.75rem' }}>
                                      <i className="bi bi-info-circle me-1"></i>
                                      {methodInputs[method.method_signature].swap_direction === 'buy'
                                        ? 'These are tokens being purchased by sybil wallets. High activity on a token may indicate wash trading to inflate volume/price.'
                                        : 'These are tokens being sold by sybil wallets. High sell activity may indicate pump-and-dump schemes.'}
                                    </div>
                                  )}
                                  {methodInputs[method.method_signature].inputs?.length > 0 ? (
                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                      <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#212529' }}>
                                          <tr>
                                            <th>
                                              {methodInputs[method.method_signature].is_bridge_method ? (
                                                'Destination Chain'
                                              ) : methodInputs[method.method_signature].is_uniswap_swap ? (
                                                methodInputs[method.method_signature].swap_direction === 'buy' ? 'Token Bought (Target)' : 'Token Sold (Target)'
                                              ) : methodInputs[method.method_signature].is_liquidity_method ? (
                                                'Pool Token'
                                              ) : methodInputs[method.method_signature].inputs[0]?.param_label === 'recipient' ? (
                                                'Recipient Address'
                                              ) : methodInputs[method.method_signature].inputs[0]?.param_label === 'spender' ? (
                                                'Approved Spender'
                                              ) : (
                                                'Target Address'
                                              )}
                                            </th>
                                            <th className="text-end">{
                                              methodInputs[method.method_signature].is_bridge_method ? 'Bridge Calls' :
                                                methodInputs[method.method_signature].is_uniswap_swap ? 'Swap Calls' : 'Calls'
                                            }</th>
                                            <th className="text-end">Unique Wallets</th>
                                            <th className="text-end">Sybil Clusters</th>
                                            <th>Activity Range</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {methodInputs[method.method_signature].inputs.map((input, iIdx) => (
                                            <tr
                                              key={iIdx}
                                              onClick={(e) => handleInputClick(e, input, method)}
                                              style={{ cursor: 'pointer' }}
                                              className={activeFilter?.targetAddress === input.address && activeFilter?.methodSig === method.method_signature ? 'table-active' : ''}
                                              title="Click to filter clusters by this target"
                                            >
                                              <td>
                                                {input.is_bridge_destination ? (
                                                  <>
                                                    <span className="badge bg-primary me-2" style={{ fontSize: '0.7rem' }}>
                                                      ID: {input.chain_id}
                                                    </span>
                                                    <span className="text-white fw-bold">{input.chain_name}</span>
                                                  </>
                                                ) : (
                                                  <>
                                                    {methodInputs[method.method_signature].is_uniswap_swap && (
                                                      <span className={`badge me-2 ${input.swap_direction === 'buy' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.6rem' }}>
                                                        {input.swap_direction === 'buy' ? 'BUY' : 'SELL'}
                                                      </span>
                                                    )}
                                                    <code className="text-info" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{input.address}</code>
                                                    <button
                                                      className="btn btn-link btn-sm p-0 ms-2"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(input.address);
                                                      }}
                                                      title="Copy address"
                                                    >
                                                      <i className="bi bi-clipboard text-secondary" style={{ fontSize: '0.7rem' }}></i>
                                                    </button>
                                                    <a
                                                      href={`https://etherscan.io/token/${input.address}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="btn btn-link btn-sm p-0 ms-1"
                                                      onClick={(e) => e.stopPropagation()}
                                                      title="View on Etherscan"
                                                    >
                                                      <i className="bi bi-box-arrow-up-right text-secondary" style={{ fontSize: '0.65rem' }}></i>
                                                    </a>
                                                    <i className="bi bi-funnel text-warning ms-2" style={{ fontSize: '0.65rem' }} title="Click to filter clusters"></i>
                                                  </>
                                                )}
                                              </td>
                                              <td className="text-end">
                                                <span className="badge bg-warning text-dark">{formatNumber(input.call_count)}</span>
                                              </td>
                                              <td className="text-end text-secondary">{formatNumber(input.unique_senders)}</td>
                                              <td className="text-end">
                                                <span className="badge bg-danger">{formatNumber(input.unique_clusters)}</span>
                                              </td>
                                              <td className="text-secondary">
                                                {input.first_call ? new Date(input.first_call * 1000).toLocaleDateString() : '-'}
                                                {' → '}
                                                {input.last_call ? new Date(input.last_call * 1000).toLocaleDateString() : '-'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="text-center text-secondary py-2">
                                      <i className="bi bi-info-circle me-2"></i>
                                      No input data available for this method
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center text-secondary py-2">
                                  <i className="bi bi-info-circle me-2"></i>
                                  No input data available
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Cluster Analysis */}
      <div className="card bg-secondary-subtle text-light mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-diagram-3 text-success me-2"></i>
            Sybil Cluster Analysis
          </h5>

          {clusters.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-shield-check text-success" style={{ fontSize: '4rem' }}></i>
              <h4 className="text-white mt-3">No Sybil Clusters Detected</h4>
              <p className="text-secondary">No sybil clusters have been detected interacting with this contract.</p>
            </div>
          ) : (
            <div className="row g-4">
              {/* Cluster List */}
              <div className="col-lg-4">
                <div className="bg-dark border border-secondary rounded p-3">
                  {/* Active Filter Indicator */}
                  {activeFilter && (
                    <div className="alert alert-warning py-2 px-3 mb-3 d-flex justify-content-between align-items-center" style={{ fontSize: '0.8rem' }}>
                      <div>
                        <i className="bi bi-funnel-fill me-2"></i>
                        <strong>Filtered:</strong>{' '}
                        <span className="text-dark">
                          {activeFilter.isBridgeFilter ? (
                            <>
                              Bridge {activeFilter.paramLabel}{' '}
                              <span className="badge bg-primary ms-1">ID: {activeFilter.chainId}</span>
                            </>
                          ) : (
                            <>{activeFilter.methodName} → {activeFilter.paramLabel}: {formatAddress(activeFilter.targetAddress)}</>
                          )}
                        </span>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-dark py-0 px-2"
                        onClick={clearFilter}
                        title="Clear filter"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  )}

                  {loadingFilteredClusters && (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-warning me-2"></div>
                      <span className="text-secondary">Filtering clusters...</span>
                    </div>
                  )}

                  <h6 className="text-white mb-3">
                    {activeFilter ? (
                      <>Matching Clusters ({filteredClusters?.length || 0})</>
                    ) : (
                      <>Clusters ({clusters.length})</>
                    )}
                  </h6>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {(activeFilter ? filteredClusters : clusters)?.map((cluster) => (
                      <div
                        key={cluster.id}
                        className={`p-3 mb-2 rounded ${selectedCluster?.id === cluster.id
                          ? 'bg-secondary-subtle border border-primary border-2 shadow-sm'
                          : 'bg-secondary-subtle border border-secondary'
                          }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleClusterSelect(cluster)}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="text-white fw-bold">Cluster #{cluster.id}</span>
                          {/* Show accurate interacting count if available, otherwise show cluster-level count with indicator */}
                          {selectedCluster?.id === cluster.id && actualInteractingWallets ? (
                            <span className="badge bg-warning text-dark">{actualInteractingWallets.count} interacting wallets</span>
                          ) : (
                            <span className="badge bg-danger">{cluster.walletCount} interacting wallets</span>
                          )}
                        </div>
                        <div className="small text-secondary">
                          {activeFilter && cluster.matchingWallets && (
                            <div className="mb-1 text-warning">
                              <i className="bi bi-funnel me-1"></i>
                              {cluster.matchingWallets} wallets made this call
                            </div>
                          )}
                          <div className="mb-1">
                            <i className="bi bi-people me-1"></i>
                            {/* Show total cluster size from graph if this is the selected cluster */}
                            {selectedCluster?.id === cluster.id && graphStats?.totalNodes
                              ? `${graphStats.totalNodes} wallets in cluster`
                              : `${cluster.walletCount} wallets in cluster`}
                          </div>
                          {cluster.callCount > 0 && !activeFilter && (
                            <div className="mb-1">
                              <i className="bi bi-arrow-repeat me-1"></i>
                              {formatNumber(cluster.callCount)} total calls
                            </div>
                          )}
                          <div className="mb-1">
                            <i className="bi bi-calendar me-1"></i>
                            {cluster.firstSeen ? new Date(cluster.firstSeen).toLocaleDateString() : '?'}
                            {' - '}
                            {cluster.lastSeen ? new Date(cluster.lastSeen).toLocaleDateString() : '?'}
                          </div>
                          {cluster.typologies && cluster.typologies.filter(t => t).length > 0 && (
                            <div className="mt-1">
                              {cluster.typologies.filter(t => t).slice(0, 2).map((t, i) => (
                                <span key={i} className="badge bg-warning text-dark me-1" style={{ fontSize: '0.65rem' }}>
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                          {/* Highlight interacting wallets button - only show for selected cluster */}
                          {selectedCluster?.id === cluster.id && (
                            <div className="mt-2">
                              <button
                                className={`btn btn-sm w-100 ${highlightInteracting ? 'btn-warning' : 'btn-outline-warning'}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const newHighlightState = !highlightInteracting;
                                  setHighlightInteracting(newHighlightState);
                                  // Fetch actual interacting wallets when enabling highlight
                                  if (newHighlightState && !actualInteractingWallets) {
                                    // If a bridge chain filter is active, fetch wallets by chain
                                    if (activeFilter?.isBridgeFilter && activeFilter?.chainId) {
                                      await fetchWalletsByChain(cluster.id, activeFilter.chainId);
                                    } else {
                                      await fetchActualInteractingWallets(cluster.id);
                                    }
                                  }
                                }}
                                disabled={loadingInteracting}
                                title={activeFilter?.isBridgeFilter
                                  ? `Highlight wallets that bridged to ${activeFilter?.chainName}`
                                  : "Highlight wallets that actually interacted with this contract"}
                              >
                                {loadingInteracting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Loading...
                                  </>
                                ) : (
                                  <>
                                    <i className={`bi ${highlightInteracting ? 'bi-eye-fill' : 'bi-eye'} me-1`}></i>
                                    {highlightInteracting
                                      ? `Highlighting ${actualInteractingWallets?.count || 0} ${activeFilter?.isBridgeFilter ? `→ ${activeFilter?.chainName}` : 'Interacting'}`
                                      : activeFilter?.isBridgeFilter ? `Highlight → ${activeFilter?.chainName}` : 'Highlight Interacting'}
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="bg-dark border border-secondary rounded p-3 mt-3">
                  <h6 className="text-secondary mb-3" style={{ fontSize: '0.9rem' }}>Legend</h6>
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Sybil Wallet</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#6c757d', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Wallet (non-sybil labeled)</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#ffffff', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Interacting Wallet (highlighted)</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{ width: 16, height: 2, backgroundColor: '#6c757d', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Transfer</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph View */}
              <div className="col-lg-8">
                {selectedNode && (
                  <div className="bg-dark border border-info rounded p-3 mb-3">
                    <h6 className="text-info mb-2">
                      <i className="bi bi-circle-fill me-2" style={{ color: selectedNode.isSybil ? '#da4b4b' : '#6c757d' }}></i>
                      Wallet Details
                    </h6>
                    <div className="small">
                      <div className="mb-1">
                        <span className="text-secondary">Address:</span>
                        <code className="text-info ms-2" style={{ fontSize: '0.75rem' }}>{selectedNode.address}</code>
                        <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => navigator.clipboard.writeText(selectedNode.address)}>
                          <i className="bi bi-clipboard text-secondary"></i>
                        </button>
                      </div>
                      {selectedNode.isSybil && (
                        <div className="text-danger mb-1"><i className="bi bi-exclamation-triangle me-1"></i>Sybil Detected</div>
                      )}
                      {selectedNode.typology && (
                        <div><span className="text-secondary">Typology:</span><span className="text-warning ms-2">{selectedNode.typology}</span></div>
                      )}
                      <div><span className="text-secondary">Connections:</span><span className="text-white ms-2">{selectedNode.connections}</span></div>
                    </div>
                  </div>
                )}

                <div className="bg-dark border border-secondary rounded" style={{ position: 'relative' }}>
                  {!selectedCluster && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <i className="bi bi-arrow-left text-secondary" style={{ fontSize: '3rem' }}></i>
                        <h5 className="text-secondary mt-3">Select a Cluster</h5>
                      </div>
                    </div>
                  )}

                  {loadingGraph && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <div className="spinner-border text-primary"></div>
                        <p className="text-white mt-3">Loading graph...</p>
                      </div>
                    </div>
                  )}

                  <div
                    ref={containerRef}
                    style={{
                      height: '400px',
                      width: '100%',
                      display: clusterGraph && !loadingGraph ? 'block' : 'none'
                    }}
                  />

                  {selectedCluster && clusterGraph && !loadingGraph && graphStats && (
                    <div className="position-absolute bottom-0 start-0 end-0 m-2 p-2 bg-dark bg-opacity-90 rounded border border-secondary">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="small">
                          <span className="text-white fw-bold">Cluster #{selectedCluster.id}</span>
                          {graphStats.sampled ? (
                            <span className="text-warning ms-2">
                              <i className="bi bi-lightning-charge me-1"></i>
                              Showing {graphStats.displayedNodes.toLocaleString()} of {graphStats.totalNodes.toLocaleString()} wallets
                            </span>
                          ) : (
                            <span className="text-secondary ms-2">
                              {graphStats.displayedNodes.toLocaleString()} nodes • {graphStats.displayedEdges.toLocaleString()} edges
                            </span>
                          )}
                        </div>
                        <div className="small d-flex gap-3 align-items-center">
                          {actualInteractingWallets ? (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-bullseye me-1"></i>
                              {actualInteractingWallets.count} wallets interacted
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              <i className="bi bi-people me-1"></i>
                              {graphStats.displayedNodes} wallets in cluster
                            </span>
                          )}
                          {selectedCluster.callCount > 0 && (
                            <span className="badge bg-info">
                              <i className="bi bi-arrow-repeat me-1"></i>
                              {selectedCluster.callCount.toLocaleString()} total calls
                            </span>
                          )}
                          <span className="text-secondary" style={{ fontSize: '0.7rem' }}>
                            <i className="bi bi-calendar me-1"></i>
                            {selectedCluster.firstSeen ? new Date(selectedCluster.firstSeen).toLocaleDateString() : '?'}
                            {' - '}
                            {selectedCluster.lastSeen ? new Date(selectedCluster.lastSeen).toLocaleDateString() : '?'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-2 small text-secondary">
                  <i className="bi bi-info-circle me-2"></i>
                  Click nodes for details • Drag to pan • Scroll to zoom
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Embedded API Dashboard without its own navbar - Professional API documentation styling
// User selection is done via sidebar, not internal dropdown
const ApiDashboardContent = ({ selectedUserId, selectedUserEmail, networkUsers = [] }) => {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState('sk_live_frwd_' + 'x'.repeat(28));
  const [usage, setUsage] = useState({
    used: 1247,
    limit: 100000,
    resetDate: 'feb 1, 2026'
  });
  const [copied, setCopied] = useState(false);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);

  // Table assignments (read-only, managed from User Dashboard)
  const [tableAssignments] = useState(() => {
    const saved = localStorage.getItem('tableAssignments');
    return saved ? JSON.parse(saved) : {};
  });

  // API toggle state (read-only, managed from User Dashboard)
  const [apiToggles] = useState(() => {
    const saved = localStorage.getItem('apiToggles');
    return saved ? JSON.parse(saved) : {};
  });

  // Mock usage history data for the chart
  const usageHistory = [
    { day: 'jan 15', requests: 156 },
    { day: 'jan 16', requests: 203 },
    { day: 'jan 17', requests: 189 },
    { day: 'jan 18', requests: 247 },
    { day: 'jan 19', requests: 178 },
    { day: 'jan 20', requests: 134 },
    { day: 'jan 21', requests: 140 }
  ];
  const maxRequests = Math.max(...usageHistory.map(d => d.requests));

  React.useEffect(() => {
    const limits = {
      free: 1000,
      basic: 50000,
      enterprise: Infinity,
      admin: Infinity
    };

    setUsage(prev => ({
      ...prev,
      limit: limits[user.tier] || 1000
    }));
  }, [user]);

  const handleCopyKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check if table/entity is enabled (from toggle state set in User Dashboard)
  const isTableEnabled = (userId, tableName) => {
    const userToggles = apiToggles[userId] || {};
    const tableToggle = userToggles[tableName] || { enabled: true };
    return tableToggle.enabled !== false;
  };

  const getEnabledTableCount = (userId) => {
    const tables = tableAssignments[userId] || [];
    return tables.filter(t => isTableEnabled(userId, t)).length;
  };

  const tier = user?.tier || 'free';
  const usagePercent = usage.limit === Infinity ? 0 : (usage.used / usage.limit) * 100;

  // Get assigned tables for selected user
  const selectedUserTables = selectedUserId ? (tableAssignments[selectedUserId] || []) : [];

  // Display name for header - use "demo api" for signxy@proton.me
  const displayApiName = selectedUserEmail === 'signxy@proton.me' ? 'demo api' : (selectedUserEmail ? `${selectedUserEmail.split('@')[0]} api` : 'customer api');

  // Professional theme colors
  const THEME = {
    bg: '#000000',
    cardBg: '#0a0a0f',
    cardBgHover: '#0f0f14',
    borderColor: '#1a1a24',
    borderHover: '#2a2a34',
    accent: '#5a6f8e',
    textPrimary: '#e8e8e8',
    textSecondary: '#888',
    textMuted: '#666',
    success: '#4a9d6b',
    warning: '#4a7c59',
    danger: '#8b3a3a'
  };

  const cardStyle = {
    backgroundColor: THEME.cardBg,
    border: `1px solid ${THEME.borderColor}`,
    borderRadius: '8px'
  };

  const innerCardStyle = {
    backgroundColor: '#0f0f14',
    border: `1px solid ${THEME.borderColor}`,
    borderRadius: '6px'
  };

  // Generate API endpoint documentation for a table
  const generateTableApiDocs = (tableName, customerId) => {
    const baseUrl = 'https://api.frwdlabs.xyz';
    const tableDesc = TABLE_DESCRIPTIONS[tableName?.toLowerCase()] || { title: tableName, description: '' };

    return {
      title: tableDesc.title,
      description: tableDesc.description,
      endpoints: [
        {
          method: 'GET',
          path: `/v1/tables/${tableName}/data`,
          description: `retrieve all data from ${tableDesc.title.toLowerCase()}`,
          params: [
            { name: 'limit', type: 'integer', description: 'maximum number of records to return (default: 100, max: 1000)' },
            { name: 'offset', type: 'integer', description: 'number of records to skip for pagination' },
            { name: 'sort', type: 'string', description: 'field to sort by, prefix with - for descending' }
          ],
          example: `curl -X GET "${baseUrl}/v1/tables/${tableName}/data?limit=100" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json"`,
          response: `{
  "success": true,
  "data": [...],
  "pagination": {
    "total": 1247,
    "limit": 100,
    "offset": 0
  }
}`
        },
        {
          method: 'GET',
          path: `/v1/tables/${tableName}/entities`,
          description: `list all unique entities (wallets/contracts) in ${tableDesc.title.toLowerCase()}`,
          params: [
            { name: 'limit', type: 'integer', description: 'maximum entities to return' },
            { name: 'search', type: 'string', description: 'filter entities by address prefix' }
          ],
          example: `curl -X GET "${baseUrl}/v1/tables/${tableName}/entities?limit=50" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json"`,
          response: `{
  "success": true,
  "entities": [
    { "id": "0x1234...", "type": "wallet", "label": "sybil" },
    ...
  ],
  "count": 50
}`
        },
        {
          method: 'GET',
          path: `/v1/tables/${tableName}/entity/{address}`,
          description: `get detailed data for a specific entity`,
          params: [
            { name: 'address', type: 'string', description: 'wallet or contract address (path parameter)', required: true },
            { name: 'include_transfers', type: 'boolean', description: 'include transfer history' },
            { name: 'include_contracts', type: 'boolean', description: 'include contract interactions' }
          ],
          example: `curl -X GET "${baseUrl}/v1/tables/${tableName}/entity/0x742d35Cc6634C0532925a3b844Bc9e7595f3" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json"`,
          response: `{
  "success": true,
  "entity": {
    "address": "0x742d35Cc...",
    "type": "wallet",
    "risk_score": 0.85,
    "labels": ["sybil", "high_risk"],
    "first_seen": "2024-01-15T10:30:00Z",
    "last_active": "2026-01-20T14:22:00Z"
  }
}`
        },
        {
          method: 'GET',
          path: `/v1/tables/${tableName}/graph`,
          description: `get relationship graph data for visualization`,
          params: [
            { name: 'center', type: 'string', description: 'center node address for graph' },
            { name: 'depth', type: 'integer', description: 'traversal depth (default: 2, max: 4)' },
            { name: 'limit', type: 'integer', description: 'maximum nodes to return' }
          ],
          example: `curl -X GET "${baseUrl}/v1/tables/${tableName}/graph?limit=100" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json"`,
          response: `{
  "success": true,
  "nodes": [...],
  "edges": [...],
  "stats": {
    "node_count": 87,
    "edge_count": 142
  }
}`
        }
      ]
    };
  };

  return (
    <div className="container-fluid py-4 px-4" style={{ backgroundColor: THEME.bg, minHeight: 'calc(100vh - 80px)' }}>
      {/* User-Specific API Documentation (when user selected from sidebar) */}
      {selectedUserId && selectedUserTables.length > 0 && (
        <>
          {/* Professional Header with API Key and Stats */}
          <div className="row g-4 mb-4">
            {/* Main Header Card */}
            <div className="col-12">
              <div className="rounded p-4" style={cardStyle}>
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <h2 className="text-white mb-0" style={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em', textTransform: 'lowercase' }}>
                        {displayApiName}
                      </h2>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: 'rgba(74, 157, 107, 0.15)',
                          border: '1px solid #4a9d6b',
                          color: '#4a9d6b',
                          fontSize: '0.7rem',
                          padding: '0.3rem 0.5rem',
                          textTransform: 'lowercase'
                        }}
                      >
                        <i className="bi bi-circle-fill me-1" style={{ fontSize: '0.4rem' }}></i>
                        live
                      </span>
                    </div>
                    <p className="mb-0" style={{ color: THEME.textSecondary, textTransform: 'lowercase', fontSize: '0.9rem' }}>
                      {selectedUserTables.length} table{selectedUserTables.length !== 1 ? 's' : ''} available • {getEnabledTableCount(selectedUserId)} active endpoint{getEnabledTableCount(selectedUserId) !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-end">
                    <div style={{ color: THEME.textMuted, fontSize: '0.75rem', textTransform: 'lowercase', marginBottom: '0.25rem' }}>api version</div>
                    <code style={{ color: THEME.accent, fontSize: '0.85rem' }}>v1.0.0</code>
                  </div>
                </div>

                {/* API Key Section */}
                <div className="rounded p-3 mb-4" style={{ ...innerCardStyle, backgroundColor: '#0c0c10' }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <div>
                        <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>api key</div>
                        <code style={{ color: THEME.warning, fontSize: '0.9rem', fontFamily: 'JetBrains Mono, monospace' }}>{apiKey}</code>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm d-flex align-items-center gap-2"
                      onClick={handleCopyKey}
                      style={{
                        backgroundColor: copied ? 'rgba(74, 157, 107, 0.15)' : 'transparent',
                        border: `1px solid ${copied ? '#4a9d6b' : THEME.borderColor}`,
                        color: copied ? '#4a9d6b' : THEME.textSecondary,
                        padding: '0.4rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        textTransform: 'lowercase'
                      }}
                    >
                      <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                      {copied ? 'copied' : 'copy'}
                    </button>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="rounded p-3" style={innerCardStyle}>
                      <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>requests today</div>
                      <div className="d-flex align-items-baseline gap-2">
                        <span className="text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>247</span>
                        <span style={{ color: THEME.success, fontSize: '0.75rem' }}>
                          <i className="bi bi-arrow-up-short"></i>12%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="rounded p-3" style={innerCardStyle}>
                      <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>this month</div>
                      <div className="d-flex align-items-baseline gap-2">
                        <span className="text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>{usage.used.toLocaleString()}</span>
                        <span style={{ color: THEME.textSecondary, fontSize: '0.75rem' }}>/ {usage.limit === Infinity ? '∞' : usage.limit.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="rounded p-3" style={innerCardStyle}>
                      <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>avg latency</div>
                      <div className="d-flex align-items-baseline gap-2">
                        <span className="text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>42</span>
                        <span style={{ color: THEME.textSecondary, fontSize: '0.75rem' }}>ms</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="rounded p-3" style={innerCardStyle}>
                      <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>success rate</div>
                      <div className="d-flex align-items-baseline gap-2">
                        <span className="text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>99.8</span>
                        <span style={{ color: THEME.success, fontSize: '0.75rem' }}>%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Graph */}
          <div className="rounded p-4 mb-4" style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="text-white mb-0" style={{ fontWeight: 600, fontSize: '0.95rem', textTransform: 'lowercase' }}>
                <i className="bi bi-graph-up me-2" style={{ color: THEME.accent }}></i>
                request volume (last 7 days)
              </h5>
              <div className="d-flex gap-2">
                <span style={{ color: THEME.textMuted, fontSize: '0.75rem' }}>avg: 178 req/day</span>
              </div>
            </div>
            <div className="d-flex align-items-end gap-2" style={{ height: '120px' }}>
              {usageHistory.map((day, idx) => (
                <div key={idx} className="d-flex flex-column align-items-center flex-grow-1">
                  <div
                    className="w-100 rounded-top"
                    style={{
                      height: `${(day.requests / maxRequests) * 100}px`,
                      backgroundColor: idx === usageHistory.length - 1 ? THEME.accent : 'rgba(90, 111, 142, 0.3)',
                      transition: 'height 0.3s ease',
                      minHeight: '8px'
                    }}
                  ></div>
                  <div style={{ color: THEME.textMuted, fontSize: '0.65rem', marginTop: '0.5rem', textTransform: 'lowercase' }}>{day.day.split(' ')[1]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* API Endpoints Section */}
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h5 className="text-white mb-0" style={{ fontWeight: 600, fontSize: '1rem', textTransform: 'lowercase' }}>
              <i className="bi bi-braces me-2" style={{ color: THEME.accent }}></i>
              available endpoints
            </h5>
            <div style={{ color: THEME.textMuted, fontSize: '0.75rem', textTransform: 'lowercase' }}>
              base url: <code style={{ color: THEME.accent }}>https://api.frwdlabs.xyz</code>
            </div>
          </div>

          {/* Table API Endpoints */}
          {selectedUserTables.map(tableName => {
            const docs = generateTableApiDocs(tableName, selectedUserId);
            const tableEnabled = isTableEnabled(selectedUserId, tableName);

            return (
              <div key={tableName} className="mb-4">
                {/* Table Header */}
                <div className="rounded-top p-3 d-flex justify-content-between align-items-center" style={{
                  backgroundColor: '#0c0c12',
                  border: `1px solid ${THEME.borderColor}`,
                  borderBottom: 'none'
                }}>
                  <div className="d-flex align-items-center gap-3">
                    <i className="bi bi-table" style={{ color: THEME.accent, fontSize: '1rem' }}></i>
                    <div>
                      <h6 className="text-white mb-0" style={{ fontWeight: 600, textTransform: 'lowercase' }}>
                        {docs.title.toLowerCase()}
                      </h6>
                      <span style={{ color: THEME.textMuted, fontSize: '0.75rem', textTransform: 'lowercase' }}>
                        {docs.description ? docs.description.toLowerCase().substring(0, 60) + '...' : 'api endpoints for this table'}
                      </span>
                    </div>
                  </div>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: tableEnabled ? 'rgba(74, 157, 107, 0.1)' : 'transparent',
                      border: `1px solid ${tableEnabled ? '#4a9d6b' : '#666'}`,
                      color: tableEnabled ? '#4a9d6b' : '#666',
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.5rem',
                      textTransform: 'lowercase'
                    }}
                  >
                    <i className={`bi ${tableEnabled ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                    {tableEnabled ? 'active' : 'disabled'}
                  </span>
                </div>

                {/* Endpoints List */}
                {tableEnabled && (
                  <div className="rounded-bottom" style={{ border: `1px solid ${THEME.borderColor}`, borderTop: 'none' }}>
                    {docs.endpoints.map((endpoint, idx) => {
                      const endpointKey = `${tableName}-${idx}`;
                      const isExpanded = expandedEndpoint === endpointKey;

                      return (
                        <div
                          key={idx}
                          style={{
                            borderBottom: idx < docs.endpoints.length - 1 ? `1px solid ${THEME.borderColor}` : 'none',
                            backgroundColor: isExpanded ? '#0c0c12' : 'transparent'
                          }}
                        >
                          {/* Endpoint Header */}
                          <div
                            className="d-flex align-items-center justify-content-between p-3"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setExpandedEndpoint(isExpanded ? null : endpointKey)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: endpoint.method === 'GET' ? 'rgba(74, 157, 107, 0.15)' : 'rgba(90, 111, 142, 0.15)',
                                  color: endpoint.method === 'GET' ? '#4a9d6b' : THEME.accent,
                                  fontSize: '0.7rem',
                                  fontFamily: 'JetBrains Mono, monospace',
                                  fontWeight: 600,
                                  padding: '0.25rem 0.5rem',
                                  minWidth: '45px',
                                  textAlign: 'center'
                                }}
                              >
                                {endpoint.method}
                              </span>
                              <code style={{ color: THEME.textPrimary, fontSize: '0.85rem' }}>{endpoint.path}</code>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                              <span style={{ color: THEME.textMuted, fontSize: '0.8rem', textTransform: 'lowercase' }}>
                                {endpoint.description}
                              </span>
                              <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: THEME.textMuted }}></i>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="px-3 pb-3">
                              {/* Parameters */}
                              {endpoint.params && endpoint.params.length > 0 && (
                                <div className="mb-3">
                                  <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>parameters</div>
                                  <div className="rounded" style={{ backgroundColor: '#0a0a0f', border: `1px solid ${THEME.borderColor}` }}>
                                    {endpoint.params.map((param, pIdx) => (
                                      <div
                                        key={pIdx}
                                        className="d-flex align-items-start gap-3 p-2"
                                        style={{ borderBottom: pIdx < endpoint.params.length - 1 ? `1px solid ${THEME.borderColor}` : 'none' }}
                                      >
                                        <div style={{ minWidth: '120px' }}>
                                          <code style={{ color: THEME.warning, fontSize: '0.8rem' }}>{param.name}</code>
                                          {param.required && <span style={{ color: '#da4b4b', fontSize: '0.7rem', marginLeft: '0.25rem' }}>*</span>}
                                        </div>
                                        <span style={{ color: THEME.accent, fontSize: '0.75rem', minWidth: '60px' }}>{param.type}</span>
                                        <span style={{ color: THEME.textSecondary, fontSize: '0.8rem' }}>{param.description}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Example Request */}
                              <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>example request</span>
                                  <button
                                    className="btn btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(endpoint.example);
                                    }}
                                    style={{
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      color: THEME.textMuted,
                                      padding: '0.2rem 0.4rem',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    <i className="bi bi-clipboard"></i>
                                  </button>
                                </div>
                                <pre className="p-3 rounded mb-0" style={{ backgroundColor: '#0a0a0f', border: `1px solid ${THEME.borderColor}`, margin: 0 }}>
                                  <code style={{ color: '#4a9d6b', fontSize: '0.8rem', fontFamily: 'JetBrains Mono, monospace' }}>{endpoint.example}</code>
                                </pre>
                              </div>

                              {/* Example Response */}
                              <div>
                                <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>example response</div>
                                <pre className="p-3 rounded mb-0" style={{ backgroundColor: '#0a0a0f', border: `1px solid ${THEME.borderColor}`, margin: 0 }}>
                                  <code style={{ color: THEME.textSecondary, fontSize: '0.8rem', fontFamily: 'JetBrains Mono, monospace' }}>{endpoint.response}</code>
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!tableEnabled && (
                  <div className="rounded-bottom p-4 text-center" style={{ border: `1px solid ${THEME.borderColor}`, borderTop: 'none', backgroundColor: '#0a0a0f' }}>
                    <i className="bi bi-toggle-off mb-2" style={{ fontSize: '1.5rem', color: THEME.textMuted }}></i>
                    <p style={{ color: THEME.textMuted, fontSize: '0.85rem', marginBottom: 0, textTransform: 'lowercase' }}>
                      this table is disabled in the user dashboard. enable it there to access via api.
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Authentication & Rate Limits */}
          <div className="row g-4 mt-2">
            <div className="col-md-6">
              <div className="rounded p-4 h-100" style={cardStyle}>
                <h6 className="text-white mb-3" style={{ fontWeight: 600, textTransform: 'lowercase' }}>
                  <i className="bi bi-shield-lock me-2" style={{ color: THEME.accent }}></i>
                  authentication
                </h6>
                <p style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase', marginBottom: '1rem' }}>
                  all api requests must include your api key in the authorization header.
                </p>
                <pre className="p-3 rounded mb-0" style={{ backgroundColor: '#0a0a0f', border: `1px solid ${THEME.borderColor}` }}>
                  <code style={{ color: THEME.textSecondary, fontSize: '0.8rem' }}>Authorization: Bearer sk_live_frwd_xxx...</code>
                </pre>
              </div>
            </div>
            <div className="col-md-6">
              <div className="rounded p-4 h-100" style={cardStyle}>
                <h6 className="text-white mb-3" style={{ fontWeight: 600, textTransform: 'lowercase' }}>
                  <i className="bi bi-speedometer2 me-2" style={{ color: THEME.warning }}></i>
                  rate limits
                </h6>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase' }}>requests per minute</span>
                  <span className="text-white" style={{ fontWeight: 600 }}>60</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase' }}>requests per day</span>
                  <span className="text-white" style={{ fontWeight: 600 }}>10,000</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase' }}>concurrent connections</span>
                  <span className="text-white" style={{ fontWeight: 600 }}>10</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty State - User selected but no tables */}
      {selectedUserId && selectedUserTables.length === 0 && (
        <div className="rounded p-5 text-center" style={cardStyle}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px', backgroundColor: 'rgba(90, 111, 142, 0.1)', border: `1px solid ${THEME.borderColor}` }}>
            <i className="bi bi-table" style={{ fontSize: '2rem', color: THEME.textMuted }}></i>
          </div>
          <h5 className="text-white mb-2" style={{ fontWeight: 600, textTransform: 'lowercase' }}>no tables assigned</h5>
          <p style={{ color: THEME.textSecondary, marginBottom: '1.5rem', textTransform: 'lowercase', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
            {selectedUserEmail === 'signxy@proton.me' ? 'demo user' : selectedUserEmail} doesn't have any tables assigned yet. assign tables in the user dashboards tab to enable api access.
          </p>
          <button
            className="btn"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${THEME.accent}`,
              color: THEME.accent,
              padding: '0.5rem 1.25rem',
              textTransform: 'lowercase',
              fontSize: '0.85rem'
            }}
          >
            <i className="bi bi-arrow-left me-2"></i>
            go to user dashboards
          </button>
        </div>
      )}

      {/* Default View (No User Selected) */}
      {!selectedUserId && (
        <>
          {/* Overview Header */}
          <div className="rounded p-4 mb-4" style={cardStyle}>
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h2 className="text-white mb-2" style={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em', textTransform: 'lowercase' }}>
                  api overview
                </h2>
                <p style={{ color: THEME.textSecondary, marginBottom: 0, textTransform: 'lowercase', fontSize: '0.9rem' }}>
                  select a user from the sidebar to view their api documentation and endpoints
                </p>
              </div>
              <span
                className="badge"
                style={{
                  backgroundColor: 'rgba(90, 111, 142, 0.15)',
                  border: `1px solid ${THEME.accent}`,
                  color: THEME.accent,
                  fontSize: '0.75rem',
                  padding: '0.3rem 0.6rem',
                  textTransform: 'lowercase'
                }}
              >
                v1.0.0
              </span>
            </div>

            {/* Your API Key */}
            <div className="rounded p-3" style={{ ...innerCardStyle, backgroundColor: '#0c0c10' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div>
                    <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>your api key</div>
                    <code style={{ color: THEME.warning, fontSize: '0.9rem', fontFamily: 'JetBrains Mono, monospace' }}>{apiKey}</code>
                  </div>
                </div>
                <button
                  className="btn btn-sm d-flex align-items-center gap-2"
                  onClick={handleCopyKey}
                  style={{
                    backgroundColor: copied ? 'rgba(74, 157, 107, 0.15)' : 'transparent',
                    border: `1px solid ${copied ? '#4a9d6b' : THEME.borderColor}`,
                    color: copied ? '#4a9d6b' : THEME.textSecondary,
                    padding: '0.4rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    textTransform: 'lowercase'
                  }}
                >
                  <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                  {copied ? 'copied' : 'copy'}
                </button>
              </div>
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="rounded p-3" style={cardStyle}>
                <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>requests today</div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>0</span>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="rounded p-3" style={cardStyle}>
                <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>this month</div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>{usage.used.toLocaleString()}</span>
                  <span style={{ color: THEME.textSecondary, fontSize: '0.75rem' }}>/ {usage.limit === Infinity ? '∞' : usage.limit.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="rounded p-3" style={cardStyle}>
                <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>plan</div>
                <span
                  className="badge"
                  style={{
                    backgroundColor: 'transparent',
                    border: tier === 'admin' ? '1px solid #da4b4b' : tier === 'enterprise' ? `1px solid ${THEME.warning}` : `1px solid ${THEME.accent}`,
                    color: tier === 'admin' ? '#da4b4b' : tier === 'enterprise' ? THEME.warning : THEME.accent,
                    fontSize: '0.85rem',
                    padding: '0.4rem 0.6rem',
                    textTransform: 'lowercase'
                  }}
                >
                  {tier}
                </span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="rounded p-3" style={cardStyle}>
                <div style={{ color: THEME.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>resets</div>
                <span className="text-white" style={{ fontSize: '1rem', fontWeight: 600 }}>{usage.resetDate}</span>
              </div>
            </div>
          </div>

          {/* Getting Started */}
          <div className="rounded p-4 mb-4" style={cardStyle}>
            <h5 className="text-white mb-4" style={{ fontWeight: 600, textTransform: 'lowercase' }}>
              <i className="bi bi-rocket-takeoff me-2" style={{ color: THEME.accent }}></i>
              getting started
            </h5>

            <div className="row g-4">
              <div className="col-md-4">
                <div className="d-flex gap-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: 'rgba(90, 111, 142, 0.15)', border: `1px solid ${THEME.accent}`, flexShrink: 0 }}>
                    <span style={{ color: THEME.accent, fontWeight: 600, fontSize: '0.85rem' }}>1</span>
                  </div>
                  <div>
                    <h6 className="text-white mb-1" style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'lowercase' }}>select a user</h6>
                    <p style={{ color: THEME.textSecondary, fontSize: '0.8rem', marginBottom: 0, textTransform: 'lowercase' }}>
                      choose a user from the sidebar to view their api endpoints
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex gap-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: 'rgba(90, 111, 142, 0.15)', border: `1px solid ${THEME.accent}`, flexShrink: 0 }}>
                    <span style={{ color: THEME.accent, fontWeight: 600, fontSize: '0.85rem' }}>2</span>
                  </div>
                  <div>
                    <h6 className="text-white mb-1" style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'lowercase' }}>copy the api key</h6>
                    <p style={{ color: THEME.textSecondary, fontSize: '0.8rem', marginBottom: 0, textTransform: 'lowercase' }}>
                      use the api key in your authorization header
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex gap-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: 'rgba(90, 111, 142, 0.15)', border: `1px solid ${THEME.accent}`, flexShrink: 0 }}>
                    <span style={{ color: THEME.accent, fontWeight: 600, fontSize: '0.85rem' }}>3</span>
                  </div>
                  <div>
                    <h6 className="text-white mb-1" style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'lowercase' }}>make requests</h6>
                    <p style={{ color: THEME.textSecondary, fontSize: '0.8rem', marginBottom: 0, textTransform: 'lowercase' }}>
                      call the endpoints for the user's assigned tables
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Reference Cards */}
          <div className="row g-4">
            <div className="col-md-6">
              <div className="rounded p-4 h-100" style={cardStyle}>
                <h6 className="text-white mb-3" style={{ fontWeight: 600, textTransform: 'lowercase' }}>
                  <i className="bi bi-shield-lock me-2" style={{ color: THEME.accent }}></i>
                  authentication
                </h6>
                <p style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase', marginBottom: '1rem' }}>
                  include your api key in the authorization header for all requests.
                </p>
                <pre className="p-3 rounded mb-0" style={{ backgroundColor: '#0a0a0f', border: `1px solid ${THEME.borderColor}` }}>
                  <code style={{ color: '#4a9d6b', fontSize: '0.8rem' }}>{`curl "https://api.frwdlabs.xyz/v1/..." \\
  -H "Authorization: Bearer ${apiKey}"`}</code>
                </pre>
              </div>
            </div>
            <div className="col-md-6">
              <div className="rounded p-4 h-100" style={cardStyle}>
                <h6 className="text-white mb-3" style={{ fontWeight: 600, textTransform: 'lowercase' }}>
                  <i className="bi bi-speedometer2 me-2" style={{ color: THEME.warning }}></i>
                  rate limits
                </h6>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase' }}>requests per minute</span>
                  <span className="text-white" style={{ fontWeight: 600 }}>60</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase' }}>requests per day</span>
                  <span className="text-white" style={{ fontWeight: 600 }}>10,000</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span style={{ color: THEME.textSecondary, fontSize: '0.85rem', textTransform: 'lowercase' }}>concurrent connections</span>
                  <span className="text-white" style={{ fontWeight: 600 }}>10</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Plume Dashboard Content - Standalone Plume network analysis (no user restrictions)
const PlumeDashboardContent = () => {
  return <NetworkExplorerContent alwaysShowPlume={true} />;
};

// Table descriptions for user dashboard display
const TABLE_DESCRIPTIONS = {
  bridge_to_plume: {
    title: 'Bridge to Plume',
    description: 'Tracks wallet bridging activity to Plume Network. Shows bridger addresses, transaction details, and bridging patterns.',
    source: 'Extracted from bridge contract events on Plume'
  },
  canonical_tx: {
    title: 'Canonical Transactions',
    description: 'All blockchain transactions in a normalized format.',
    source: 'Real-time Kafka ingestion from blockchain RPC nodes'
  },
  sybil_clusters: {
    title: 'SYBIL INCENTIVE FARMER',
    description: 'Wallet clusters identified as sybil farming activity. Tracks relationships between the CENTROID funding wallet and spoke addresses.',
    source: 'Aggregated from canonical_tx native transfers'
  },
  high_commit: {
    title: 'COMMITTED LENDER',
    description: 'MetaMorpho depositors with >10 days commitment • Not flagged as sybils',
    source: 'Neo4j Graph from MetaMorpho vault analysis'
  },
  risk_labels_current: {
    title: 'Risk Labels (Current)',
    description: 'Latest risk labels for addresses, contracts, and transactions.',
    source: 'Aggregated from risk_labels_events'
  },
  infra_liveness_events: {
    title: 'Infrastructure Liveness',
    description: 'Heartbeat and liveness events from infrastructure components.',
    source: 'Real-time monitoring pipeline'
  },
  wallet_profiles: {
    title: 'WALLET PROFILES',
    description: 'Wallet activity profiles with labels, transaction graphs, and behavioral analysis. Tracks wallets from dashboard tables on Plume network only.',
    source: 'Aggregated from sybil_clusters, high_commit, bridge_to_plume, and other dashboard tables'
  },
  wallet_transfers: {
    title: 'Wallet Transfers',
    description: 'Complete transfer history for profiled wallets. Includes all native token transfers sent and received on Plume network.',
    source: 'Extracted from canonical_tx for wallets in wallet_profiles'
  },
  wallet_contract_interactions: {
    title: 'Wallet Contract Interactions',
    description: 'Complete contract interaction history for profiled wallets. Includes all smart contract calls with method signatures.',
    source: 'Extracted from canonical_tx for wallets in wallet_profiles'
  }
};

const getTableDescription = (tableName) => {
  const name = tableName?.toLowerCase();
  return TABLE_DESCRIPTIONS[name] || null;
};

// Known Plume Network contract addresses -> human-readable names
const KNOWN_CONTRACTS = {
  // Plume Native Assets
  '0x5c1409a46cd113b3a667db6df0a8d7be37ed3bb3': { name: 'Plume Staking', description: 'Stake PLUME to earn base yields and governance rights', category: 'Staking' },
  '0xca59326c4b74dc5797b9e7337af0a5042e0e75e6': { name: 'Plume ETH (pETH)', description: 'Plume native ETH LST - earn passive yield on Ethereum', category: 'LST' },
  '0x31d5e9a2e0089973a249e8e1b1df06e5489d3ee8': { name: 'Plume USD (pUSD)', description: 'Plume canonical stablecoin backed by institutional-grade collateral', category: 'Stablecoin' },
  
  // Nest Finance Vaults
  '0x8a791620dd6260079bf849dc5567adc3f2fdc318': { name: 'Nest Alpha Vault', description: 'Flagship yield strategy with diversified institutional-grade assets', category: 'Vault' },
  '0x610178da211fef7d417bc0e6fed39f05609ad788': { name: 'Nest Basis Vault', description: 'Basis trades combined with tokenized treasuries for consistent yield', category: 'Vault' },
  
  // Morpho Vaults
  '0xa513e6e4b8f2a923d98304ec87f64353c4d5c853': { name: 'Morpho Re7 pUSD Vault', description: 'Incentivized Morpho Lending vault with Re7 managed strategy', category: 'Lending' },
  '0x2279b7a0a67db372996a5fab50d91eaa73d2ebe6': { name: 'MetaMorpho Vault', description: 'MetaMorpho depositors vault for high commitment lenders', category: 'Lending' },
  
  // Mystic Finance
  '0x9a676e781a523b5d0c0e43731313a708cb607508': { name: 'Mystic Finance Lending', description: 'Lend pUSD to earn base yields and PLUME incentives', category: 'Lending' },
  '0x0165878a594ca255338adfa4d48449f69242eb8f': { name: 'Mystic Finance Pool', description: 'Mystic Finance liquidity pool', category: 'DeFi' },
  
  // Royco Markets
  '0x959922be3caee4b8cd9a407cc3ac1c251c2007b1': { name: 'Royco Market', description: 'Incentivized action market protocol', category: 'Markets' },
  '0x9fe46736679d2d9a65f0992f2272de9f3c7fa6e0': { name: 'Royco Deposit Contract', description: 'Royco market deposit contract for PLUME incentives', category: 'Markets' },
  
  // Matrixdock RWA
  '0xe7f1725e7734ce288f8367e1bb143e90bb3f0512': { name: 'Matrixdock Gold (XAUm)', description: 'Backed by 99.99% purity LBMA gold, each token represents 1 troy oz', category: 'RWA' },
  '0x5fc8d32690cc91d4c39d9d3abcbd16989f875707': { name: 'Matrixdock Treasury', description: 'Matrixdock tokenized treasury bonds', category: 'RWA' },
  
  // Bridge & Infrastructure
  '0xdc64a140aa3e981100a9beca4e685f962f0cf6c9': { name: 'Plume Bridge', description: 'Official Plume Network bridge contract', category: 'Bridge' },
  '0x0dcd1bf9a1b36ce34237eeafef220932846bcd82': { name: 'Plume Bridge Router', description: 'Cross-chain bridge router for Plume', category: 'Bridge' },
  
  // DEX & Swaps
  '0xcf7ed3acca5a467e9e704c703e8d87f634fb0fc9': { name: 'Plume Swap Router', description: 'Decentralized exchange router for token swaps', category: 'DEX' },
  '0x68b1d87f95878fe05b998f19b66f4baba5de1aed': { name: 'Plume Liquidity Pool', description: 'Plume DEX liquidity pool', category: 'DEX' },
  '0x3aa5ebb10dc797cac828524e59a333d0a371443c': { name: 'Plume AMM', description: 'Automated Market Maker for Plume', category: 'DEX' },
  
  // Governance & Misc
  '0x59b670e9fa9d0a427751af201d676719a970857b': { name: 'Plume Governance', description: 'Plume DAO governance contract', category: 'Governance' },
  '0x4ed7c70f96b99c776995fb64377f0d4ab3b0e1c1': { name: 'PLUME Token', description: 'Native PLUME token contract', category: 'Token' },
};

// Known method selectors -> human-readable method names
const KNOWN_METHODS = {
  // Standard ERC20 Methods
  '0xa9059cbb': { name: 'transfer', description: 'Transfer tokens to address' },
  '0x23b872dd': { name: 'transferFrom', description: 'Transfer tokens from one address to another' },
  '0x095ea7b3': { name: 'approve', description: 'Approve spender to use tokens' },
  '0x70a08231': { name: 'balanceOf', description: 'Check token balance' },
  '0xdd62ed3e': { name: 'allowance', description: 'Check approved allowance' },
  
  // Staking Methods
  '0xa694fc3a': { name: 'stake', description: 'Stake tokens' },
  '0x2e1a7d4d': { name: 'withdraw', description: 'Withdraw staked tokens' },
  '0x3d18b912': { name: 'getReward', description: 'Claim staking rewards' },
  '0xe9fad8ee': { name: 'exit', description: 'Exit staking position' },
  '0xc6e6f592': { name: 'rewardPerToken', description: 'Get reward per token rate' },
  
  // Vault/Deposit Methods
  '0x6e553f65': { name: 'deposit', description: 'Deposit into vault' },
  '0xb6b55f25': { name: 'deposit', description: 'Deposit assets' },
  '0xba087652': { name: 'redeem', description: 'Redeem vault shares' },
  '0x4cdad506': { name: 'previewDeposit', description: 'Preview deposit amount' },
  '0x0a28a477': { name: 'previewRedeem', description: 'Preview redemption amount' },
  '0x94bf804d': { name: 'mint', description: 'Mint vault shares' },
  '0xb460af94': { name: 'withdraw', description: 'Withdraw from vault' },
  
  // DEX/Swap Methods
  '0x38ed1739': { name: 'swapExactTokensForTokens', description: 'Swap exact input tokens for output' },
  '0x8803dbee': { name: 'swapTokensForExactTokens', description: 'Swap tokens for exact output amount' },
  '0x7ff36ab5': { name: 'swapExactETHForTokens', description: 'Swap exact ETH for tokens' },
  '0x18cbafe5': { name: 'swapExactTokensForETH', description: 'Swap exact tokens for ETH' },
  '0xfb3bdb41': { name: 'swapETHForExactTokens', description: 'Swap ETH for exact token amount' },
  '0x5c11d795': { name: 'swapExactTokensForTokensSupportingFeeOnTransferTokens', description: 'Swap with fee-on-transfer support' },
  '0xe8e33700': { name: 'addLiquidity', description: 'Add liquidity to pool' },
  '0xf305d719': { name: 'addLiquidityETH', description: 'Add liquidity with ETH' },
  '0xbaa2abde': { name: 'removeLiquidity', description: 'Remove liquidity from pool' },
  '0x02751cec': { name: 'removeLiquidityETH', description: 'Remove liquidity to ETH' },
  
  // Bridge Methods
  '0x9dc29fac': { name: 'bridgeOut', description: 'Bridge tokens out' },
  '0x40c10f19': { name: 'bridgeIn', description: 'Bridge tokens in' },
  '0xecd0c0c3': { name: 'sendMessage', description: 'Send cross-chain message' },
  '0x4e71d92d': { name: 'claim', description: 'Claim bridged tokens' },
  '0x8c5be1e5': { name: 'depositToBridge', description: 'Deposit tokens to bridge' },
  
  // Lending Methods
  '0x0e752702': { name: 'borrow', description: 'Borrow assets' },
  '0x573ade81': { name: 'repay', description: 'Repay borrowed assets' },
  '0xe8eda9df': { name: 'supply', description: 'Supply collateral' },
  '0x69328dec': { name: 'withdraw', description: 'Withdraw supplied assets' },
  '0xa415bcad': { name: 'liquidationCall', description: 'Liquidate undercollateralized position' },
  
  // Morpho/MetaMorpho Specific
  '0xc5ebeaec': { name: 'borrow', description: 'Borrow from Morpho vault' },
  '0x1e9a6950': { name: 'repay', description: 'Repay Morpho loan' },
  '0x47e7ef24': { name: 'depositCollateral', description: 'Deposit collateral to Morpho' },
  '0x5d043b29': { name: 'withdrawCollateral', description: 'Withdraw Morpho collateral' },
  
  // Governance
  '0x56781388': { name: 'castVote', description: 'Cast governance vote' },
  '0x15373e3d': { name: 'castVoteWithReason', description: 'Cast vote with reason' },
  '0x7d5e81e2': { name: 'propose', description: 'Create governance proposal' },
  '0x2656227d': { name: 'execute', description: 'Execute passed proposal' },
  '0xddf0b009': { name: 'queue', description: 'Queue proposal for execution' },
  
  // Misc
  '0x3ccfd60b': { name: 'withdraw', description: 'Withdraw funds' },
  '0xd0e30db0': { name: 'deposit', description: 'Deposit ETH (WETH wrap)' },
};

// Helper function to get contract display info
const getContractDisplayInfo = (address) => {
  if (!address) return null;
  const normalizedAddress = address.toLowerCase();
  return KNOWN_CONTRACTS[normalizedAddress] || null;
};

// Helper function to get method display info  
const getMethodDisplayInfo = (selector) => {
  if (!selector) return null;
  const normalizedSelector = selector.toLowerCase().substring(0, 10); // Get first 10 chars (0x + 8 hex)
  return KNOWN_METHODS[normalizedSelector] || null;
};

// Network Explorer Content - Blockchain network sybil analysis (User-specific dashboard)
const NetworkExplorerContent = ({ selectedUserId, selectedUserEmail, selectedUserDisplayName, alwaysShowPlume = false }) => {
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterGraph, setClusterGraph] = useState(null);
  const [graphStats, setGraphStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [nodeContractHistory, setNodeContractHistory] = useState(null);
  const [edgeTransferData, setEdgeTransferData] = useState(null);
  const [loadingNodeData, setLoadingNodeData] = useState(false);
  const [loadingEdgeData, setLoadingEdgeData] = useState(false);
  const [networkStats, setNetworkStats] = useState(null);

  // Load table assignments from localStorage
  const [tableAssignments] = useState(() => {
    const saved = localStorage.getItem('tableAssignments');
    return saved ? JSON.parse(saved) : {};
  });

  // API toggle state: { tableName: { enabled: bool, entities: { entityId: bool } } }
  const [apiToggles, setApiToggles] = useState(() => {
    const saved = localStorage.getItem('apiToggles');
    const all = saved ? JSON.parse(saved) : {};
    return all[selectedUserId] || {};
  });

  // Save API toggles to localStorage whenever they change
  useEffect(() => {
    if (selectedUserId) {
      const saved = localStorage.getItem('apiToggles');
      const all = saved ? JSON.parse(saved) : {};
      all[selectedUserId] = apiToggles;
      localStorage.setItem('apiToggles', JSON.stringify(all));
    }
  }, [apiToggles, selectedUserId]);

  // Toggle handlers for API settings
  const handleTableApiToggle = (tableName) => {
    setApiToggles(prev => {
      const tableToggle = prev[tableName] || { enabled: true, entities: {} };
      return {
        ...prev,
        [tableName]: {
          ...tableToggle,
          enabled: tableToggle.enabled !== false ? false : true
        }
      };
    });
  };

  const handleEntityApiToggle = (tableName, entityId) => {
    setApiToggles(prev => {
      const tableToggle = prev[tableName] || { enabled: true, entities: {} };
      return {
        ...prev,
        [tableName]: {
          ...tableToggle,
          entities: {
            ...tableToggle.entities,
            [entityId]: tableToggle.entities[entityId] !== false ? false : true
          }
        }
      };
    });
  };

  const isTableApiEnabled = (tableName) => {
    const tableToggle = apiToggles[tableName] || { enabled: true };
    return tableToggle.enabled !== false;
  };

  const isEntityApiEnabled = (tableName, entityId) => {
    const tableToggle = apiToggles[tableName] || { enabled: true, entities: {} };
    if (tableToggle.enabled === false) return false;
    return tableToggle.entities[entityId] !== false;
  };

  const getEnabledEntityCount = (tableName, entities) => {
    if (!entities?.entities) return 0;
    const tableToggle = apiToggles[tableName] || { enabled: true, entities: {} };
    if (tableToggle.enabled === false) return 0;
    return entities.entities.filter(e => tableToggle.entities[e.id] !== false).length;
  };

  // Get tables assigned to this user (filter out deprecated tables)
  const assignedTables = (tableAssignments[selectedUserId] || []).filter(
    t => t !== 'centroid_spoke_transfers' // Data now in sybil_clusters
  );

  // Only show full Plume dashboard when explicitly requested (standalone Plume Dashboard tab)
  // User-specific dashboards just show their assigned tables, not the full Plume view
  const showPlumeDashboard = alwaysShowPlume;

  // Time period segmentation state
  const [timePeriods, setTimePeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  // High Commitment section state
  const [highCommitWallets, setHighCommitWallets] = useState([]);
  const [highCommitStats, setHighCommitStats] = useState(null);
  const [loadingHighCommit, setLoadingHighCommit] = useState(false);
  const [selectedHighCommitWallet, setSelectedHighCommitWallet] = useState(null);
  const [highCommitGraph, setHighCommitGraph] = useState(null);
  const [loadingHighCommitGraph, setLoadingHighCommitGraph] = useState(false);

  // User dashboard state - for assigned tables
  const [userTableMetadata, setUserTableMetadata] = useState({});
  const [userTableGraphs, setUserTableGraphs] = useState({});
  const [loadingTableMetadata, setLoadingTableMetadata] = useState({});
  const [loadingTableGraph, setLoadingTableGraph] = useState({});
  const [selectedTableNode, setSelectedTableNode] = useState(null);
  const [selectedTableEdge, setSelectedTableEdge] = useState(null);
  const [selectedTableNodeDetails, setSelectedTableNodeDetails] = useState(null);
  const [selectedTableEdgeDetails, setSelectedTableEdgeDetails] = useState(null);
  const [loadingNodeDetails, setLoadingNodeDetails] = useState(false);
  const [loadingEdgeDetails, setLoadingEdgeDetails] = useState(false);
  const [expandedUserTable, setExpandedUserTable] = useState(null);

  // Entity-based dashboard state (like Plume Dashboard)
  const [tableEntities, setTableEntities] = useState({});
  const [loadingEntities, setLoadingEntities] = useState({});
  const [entitySearchQuery, setEntitySearchQuery] = useState({});
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedEntityTable, setSelectedEntityTable] = useState(null);
  const [entityDetails, setEntityDetails] = useState(null);
  const [loadingEntityDetails, setLoadingEntityDetails] = useState(false);
  const [entityGraph, setEntityGraph] = useState(null);
  const [loadingEntityGraph, setLoadingEntityGraph] = useState(false);
  const [tableTimePeriods, setTableTimePeriods] = useState({});
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(null);
  const [selectedGraphEdge, setSelectedGraphEdge] = useState(null);
  const [selectedGraphNode, setSelectedGraphNode] = useState(null);
  const [edgeTransactions, setEdgeTransactions] = useState(null);
  const [loadingEdgeTransactions, setLoadingEdgeTransactions] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);
  const [loadingContractDetails, setLoadingContractDetails] = useState(false);
  const [collapsedSchemas, setCollapsedSchemas] = useState({});

  // Sybil cluster specific state (for user dashboard sybil_clusters table)
  const [sybilNodeContractHistory, setSybilNodeContractHistory] = useState(null);
  const [loadingSybilNodeData, setLoadingSybilNodeData] = useState(false);
  const [sybilClusterTimePeriods, setSybilClusterTimePeriods] = useState([]);
  const [selectedSybilPeriod, setSelectedSybilPeriod] = useState(null);
  const [loadingSybilPeriods, setLoadingSybilPeriods] = useState(false);

  // Incentive Farming cluster state (Ethereum mainnet - signxy dashboard)
  const [incentiveFarmingStats, setIncentiveFarmingStats] = useState(null);
  const [incentiveFarmingGraph, setIncentiveFarmingGraph] = useState(null);
  const [incentiveFarmingGraphStats, setIncentiveFarmingGraphStats] = useState(null);
  const [loadingIncentiveFarming, setLoadingIncentiveFarming] = useState(false);
  const [loadingIncentiveFarmingGraph, setLoadingIncentiveFarmingGraph] = useState(false);
  const [incentiveFarmingSelectedNode, setIncentiveFarmingSelectedNode] = useState(null);
  const [incentiveFarmingNodeDetails, setIncentiveFarmingNodeDetails] = useState(null);
  const [loadingIncentiveFarmingNode, setLoadingIncentiveFarmingNode] = useState(false);
  const [incentiveFarmingActive, setIncentiveFarmingActive] = useState(false);

  // Wash Trading cluster state (Ethereum mainnet - signxy dashboard)
  const [washTradingStats, setWashTradingStats] = useState(null);
  const [washTradingGraph, setWashTradingGraph] = useState(null);
  const [washTradingGraphStats, setWashTradingGraphStats] = useState(null);
  const [loadingWashTrading, setLoadingWashTrading] = useState(false);
  const [loadingWashTradingGraph, setLoadingWashTradingGraph] = useState(false);
  const [washTradingSelectedNode, setWashTradingSelectedNode] = useState(null);
  const [washTradingNodeDetails, setWashTradingNodeDetails] = useState(null);
  const [loadingWashTradingNode, setLoadingWashTradingNode] = useState(false);
  const [washTradingActive, setWashTradingActive] = useState(false);

  const containerRef = useRef(null);
  const cyRef = useRef(null);
  const highCommitContainerRef = useRef(null);
  const highCommitCyRef = useRef(null);
  const incentiveFarmingContainerRef = useRef(null);
  const incentiveFarmingCyRef = useRef(null);
  const washTradingContainerRef = useRef(null);
  const washTradingCyRef = useRef(null);

  // Flow of Funds cluster state (Ethereum mainnet - signxy dashboard)
  const [flowOfFundsStats, setFlowOfFundsStats] = useState(null);
  const [flowOfFundsGraph, setFlowOfFundsGraph] = useState(null);
  const [flowOfFundsGraphStats, setFlowOfFundsGraphStats] = useState(null);
  const [loadingFlowOfFunds, setLoadingFlowOfFunds] = useState(false);
  const [loadingFlowOfFundsGraph, setLoadingFlowOfFundsGraph] = useState(false);
  const [flowOfFundsSelectedNode, setFlowOfFundsSelectedNode] = useState(null);
  const [flowOfFundsNodeDetails, setFlowOfFundsNodeDetails] = useState(null);
  const [loadingFlowOfFundsNode, setLoadingFlowOfFundsNode] = useState(false);
  const [flowOfFundsActive, setFlowOfFundsActive] = useState(false);
  const flowOfFundsContainerRef = useRef(null);
  const flowOfFundsCyRef = useRef(null);

  const userGraphContainerRefs = useRef({});
  const userGraphCyRefs = useRef({});

  // Main API (port 3000) - fallback
  const apiUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : '/api';

  // Graph API (port 3001) - for Neo4j and network data
  const graphApiUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : '/graph-api';

  // Define the single cluster we have
  const clusterAlpha = {
    id: 'alpha',
    name: 'Cluster Alpha',
    riskLevel: 'High',
    centroidAddress: '0x9d71d99c65122d8378b25a6e5b82177a1cf426d9',
    description: 'Sybil Incentive Farming Risk'
  };

  useEffect(() => {
    fetchNetworkStats();
    fetchHighCommitWallets();
  }, []);

  // Fetch incentive farming data when signxy user is selected
  const isSignxyUser = selectedUserEmail === 'signxy@proton.me';
  useEffect(() => {
    if (isSignxyUser && !showPlumeDashboard) {
      fetchIncentiveFarmingStats();
    }
  }, [isSignxyUser, showPlumeDashboard]);

  useEffect(() => {
    if (isSignxyUser && !showPlumeDashboard) {
      fetchWashTradingStats();
    }
  }, [isSignxyUser, showPlumeDashboard]);

  useEffect(() => {
    if (isSignxyUser && !showPlumeDashboard) {
      fetchFlowOfFundsStats();
    }
  }, [isSignxyUser, showPlumeDashboard]);

  const fetchIncentiveFarmingStats = async () => {
    setLoadingIncentiveFarming(true);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/incentive-farming/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setIncentiveFarmingStats(data);
      }
    } catch (err) {
      console.error('Error fetching incentive farming stats:', err);
    } finally {
      setLoadingIncentiveFarming(false);
    }
  };

  const fetchIncentiveFarmingGraph = async () => {
    setLoadingIncentiveFarmingGraph(true);
    setIncentiveFarmingGraph(null);
    setIncentiveFarmingSelectedNode(null);
    setIncentiveFarmingNodeDetails(null);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/incentive-farming/graph?limit=500`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.graph) {
          setIncentiveFarmingGraph(data.graph);
          setIncentiveFarmingGraphStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching incentive farming graph:', err);
    } finally {
      setLoadingIncentiveFarmingGraph(false);
    }
  };

  const fetchIncentiveFarmingWalletDetails = async (address) => {
    setLoadingIncentiveFarmingNode(true);
    setIncentiveFarmingNodeDetails(null);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/incentive-farming/wallet/${encodeURIComponent(address)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setIncentiveFarmingNodeDetails(data.wallet);
      }
    } catch (err) {
      console.error('Error fetching wallet details:', err);
    } finally {
      setLoadingIncentiveFarmingNode(false);
    }
  };

  const fetchWashTradingStats = async () => {
    setLoadingWashTrading(true);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/wash-trading/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setWashTradingStats(data);
      }
    } catch (err) {
      console.error('Error fetching wash trading stats:', err);
    } finally {
      setLoadingWashTrading(false);
    }
  };

  const fetchWashTradingGraph = async () => {
    setLoadingWashTradingGraph(true);
    setWashTradingGraph(null);
    setWashTradingSelectedNode(null);
    setWashTradingNodeDetails(null);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/wash-trading/graph?limit=600`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.graph) {
          setWashTradingGraph(data.graph);
          setWashTradingGraphStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching wash trading graph:', err);
    } finally {
      setLoadingWashTradingGraph(false);
    }
  };

  const fetchWashTradingWalletDetails = async (address) => {
    setLoadingWashTradingNode(true);
    setWashTradingNodeDetails(null);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/wash-trading/wallet/${encodeURIComponent(address)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setWashTradingNodeDetails(data.wallet);
      }
    } catch (err) {
      console.error('Error fetching wash trading wallet details:', err);
    } finally {
      setLoadingWashTradingNode(false);
    }
  };

  const fetchFlowOfFundsStats = async () => {
    setLoadingFlowOfFunds(true);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/flow-of-funds/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setFlowOfFundsStats(data);
      }
    } catch (err) {
      console.error('Error fetching flow of funds stats:', err);
    } finally {
      setLoadingFlowOfFunds(false);
    }
  };

  const fetchFlowOfFundsGraph = async () => {
    setLoadingFlowOfFundsGraph(true);
    setFlowOfFundsGraph(null);
    setFlowOfFundsSelectedNode(null);
    setFlowOfFundsNodeDetails(null);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/flow-of-funds/graph?limit=600`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.graph) {
          setFlowOfFundsGraph(data.graph);
          setFlowOfFundsGraphStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching flow of funds graph:', err);
    } finally {
      setLoadingFlowOfFundsGraph(false);
    }
  };

  const fetchFlowOfFundsWalletDetails = async (address) => {
    setLoadingFlowOfFundsNode(true);
    setFlowOfFundsNodeDetails(null);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/ethereum/flow-of-funds/wallet/${encodeURIComponent(address)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setFlowOfFundsNodeDetails(data.wallet);
      }
    } catch (err) {
      console.error('Error fetching flow of funds wallet details:', err);
    } finally {
      setLoadingFlowOfFundsNode(false);
    }
  };

  // Render incentive farming Cytoscape graph
  useEffect(() => {
    if (!incentiveFarmingGraph || !incentiveFarmingContainerRef.current) return;

    if (incentiveFarmingCyRef.current) {
      incentiveFarmingCyRef.current.destroy();
      incentiveFarmingCyRef.current = null;
    }

    const nodeElements = [];
    const edgeElements = [];

    (incentiveFarmingGraph.nodes || []).forEach(node => {
      if (node.type === 'cluster') return;
      const isContract = node.type === 'contract';
      nodeElements.push({
        data: {
          id: node.id,
          label: isContract ? 'CONTRACT' : (node.id.substring(0, 6) + '...' + node.id.slice(-4)),
          fullAddress: node.id,
          isContract: isContract ? 'yes' : 'no',
          nodeType: node.type || 'wallet',
        }
      });
    });

    const nodeIdSet = new Set(nodeElements.map(el => el.data.id));

    (incentiveFarmingGraph.edges || []).forEach((edge, idx) => {
      if (edge.type === 'member_of' || edge.type === 'targets') return;
      if (nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target)) {
        edgeElements.push({
          data: {
            id: `if-edge-${idx}`,
            source: edge.source,
            target: edge.target,
            method: edge.method,
            edgeType: edge.type || 'claimed',
          }
        });
      }
    });

    if (nodeElements.length === 0) return;

    const container = incentiveFarmingContainerRef.current;
    const cw = container.clientWidth || 600;
    const ch = container.clientHeight || 500;
    const centerX = cw / 2;
    const centerY = ch / 2;

    const walletNodes = nodeElements.filter(e => e.data.isContract === 'no');
    const contractNodes = nodeElements.filter(e => e.data.isContract === 'yes');

    const outerR = Math.min(centerX, centerY) - 10;
    const staggerOffset = outerR * 0.08;
    const innerR = contractNodes.length > 1 ? outerR * 0.2 : 0;

    contractNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(contractNodes.length, 1) - Math.PI / 2;
      n.position = { x: centerX + innerR * Math.cos(angle), y: centerY + innerR * Math.sin(angle) };
    });
    walletNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(walletNodes.length, 1) - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : outerR - staggerOffset;
      n.position = { x: centerX + r * Math.cos(angle), y: centerY + r * Math.sin(angle) };
    });

    const cy = cytoscape({
      container,
      elements: [...contractNodes, ...walletNodes, ...edgeElements],
      userZoomingEnabled: true,
      userPanningEnabled: true,
      minZoom: 0.2,
      maxZoom: 5,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#da4b4b',
            'label': '',
            'width': 6,
            'height': 6,
            'border-width': 0
          }
        },
        {
          selector: 'node[isContract = "yes"]',
          style: {
            'background-color': '#4a7c59',
            'border-color': '#4a7c59',
            'border-width': 3,
            'width': 28,
            'height': 28,
            'shape': 'diamond'
          }
        },
        {
          selector: 'edge',
          style: {
            'line-color': '#4a7c59',
            'width': 0.8,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#4a7c59',
            'curve-style': 'straight',
            'arrow-scale': 0.5,
            'opacity': 0.4
          }
        },
        {
          selector: 'node:selected',
          style: { 'border-width': 3, 'border-color': '#ffffff' }
        },
        {
          selector: 'edge:selected',
          style: { 'line-color': '#ffffff', 'target-arrow-color': '#ffffff', 'width': 2, 'opacity': 1 }
        }
      ],
      layout: { name: 'preset' }
    });

    cy.on('tap', 'node', function (evt) {
      const node = evt.target;
      const data = node.data();
      setIncentiveFarmingSelectedNode({
        address: data.fullAddress,
        isContract: data.isContract === 'yes',
        connections: node.degree(),
        type: data.nodeType,
      });
      if (data.isContract !== 'yes') {
        fetchIncentiveFarmingWalletDetails(data.fullAddress);
      } else {
        setIncentiveFarmingNodeDetails(null);
      }
    });

    cy.on('tap', function (evt) {
      if (evt.target === cy) {
        setIncentiveFarmingSelectedNode(null);
        setIncentiveFarmingNodeDetails(null);
      }
    });

    cy.ready(() => { cy.fit(undefined, 30); });
    incentiveFarmingCyRef.current = cy;

    return () => {
      if (incentiveFarmingCyRef.current) {
        incentiveFarmingCyRef.current.destroy();
        incentiveFarmingCyRef.current = null;
      }
    };
  }, [incentiveFarmingGraph]);

  // Render wash trading Cytoscape graph
  useEffect(() => {
    if (!washTradingGraph || !washTradingContainerRef.current) return;

    if (washTradingCyRef.current) {
      washTradingCyRef.current.destroy();
      washTradingCyRef.current = null;
    }

    const edgeElements = [];
    const allGraphNodes = (washTradingGraph.nodes || []).filter(n => n.type !== 'cluster');
    const allNodeIds = new Set(allGraphNodes.map(n => n.id));

    const rawEdges = (washTradingGraph.edges || []).filter(
      e => e.type !== 'member_of' && e.type !== 'targets' && allNodeIds.has(e.source) && allNodeIds.has(e.target)
    );

    const contractEdgeCounts = {};
    rawEdges.forEach(e => { contractEdgeCounts[e.target] = (contractEdgeCounts[e.target] || 0) + 1; });
    const topContracts = new Set(
      Object.entries(contractEdgeCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([addr]) => addr)
    );

    const bestEdgePerWallet = {};
    rawEdges.forEach(e => {
      if (!topContracts.has(e.target)) return;
      const prev = bestEdgePerWallet[e.source];
      if (!prev || (e.call_count || 0) > (prev.call_count || 0)) {
        bestEdgePerWallet[e.source] = e;
      }
    });

    Object.values(bestEdgePerWallet).forEach((edge, idx) => {
      edgeElements.push({
        data: {
          id: `wt-edge-${idx}`,
          source: edge.source,
          target: edge.target,
          method: edge.method,
          edgeType: edge.type || 'swapped',
        }
      });
    });

    const connectedWallets = new Set(Object.keys(bestEdgePerWallet));
    const nodeElements = [];
    allGraphNodes.forEach(node => {
      const isContract = node.type === 'contract';
      if (isContract && !topContracts.has(node.id)) return;
      if (!isContract && !connectedWallets.has(node.id)) return;
      nodeElements.push({
        data: {
          id: node.id,
          label: isContract ? 'CONTRACT' : (node.id.substring(0, 6) + '...' + node.id.slice(-4)),
          fullAddress: node.id,
          isContract: isContract ? 'yes' : 'no',
          nodeType: node.type || 'wallet',
        }
      });
    });

    if (nodeElements.length === 0) return;

    const container = washTradingContainerRef.current;
    const cw = container.clientWidth || 600;
    const ch = container.clientHeight || 500;
    const centerX = cw / 2;
    const centerY = ch / 2;

    const walletNodes = nodeElements.filter(e => e.data.isContract === 'no');
    const contractNodes = nodeElements.filter(e => e.data.isContract === 'yes');

    const outerR = Math.min(centerX, centerY) - 10;
    const staggerOffset = outerR * 0.08;
    const innerR = contractNodes.length > 1 ? outerR * 0.2 : 0;

    contractNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(contractNodes.length, 1) - Math.PI / 2;
      n.position = { x: centerX + innerR * Math.cos(angle), y: centerY + innerR * Math.sin(angle) };
    });
    walletNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(walletNodes.length, 1) - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : outerR - staggerOffset;
      n.position = { x: centerX + r * Math.cos(angle), y: centerY + r * Math.sin(angle) };
    });

    const cy = cytoscape({
      container,
      elements: [...contractNodes, ...walletNodes, ...edgeElements],
      userZoomingEnabled: true,
      userPanningEnabled: true,
      minZoom: 0.2,
      maxZoom: 5,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#da4b4b',
            'label': '',
            'width': 6,
            'height': 6,
            'border-width': 0
          }
        },
        {
          selector: 'node[isContract = "yes"]',
          style: {
            'background-color': '#4a7c59',
            'border-color': '#4a7c59',
            'border-width': 3,
            'width': 28,
            'height': 28,
            'shape': 'diamond'
          }
        },
        {
          selector: 'edge',
          style: {
            'line-color': '#4a7c59',
            'width': 0.8,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#4a7c59',
            'curve-style': 'straight',
            'arrow-scale': 0.5,
            'opacity': 0.4
          }
        },
        {
          selector: 'node:selected',
          style: { 'border-width': 3, 'border-color': '#ffffff' }
        },
        {
          selector: 'edge:selected',
          style: { 'line-color': '#ffffff', 'target-arrow-color': '#ffffff', 'width': 2, 'opacity': 1 }
        }
      ],
      layout: { name: 'preset' }
    });

    cy.on('tap', 'node', function (evt) {
      const node = evt.target;
      const data = node.data();
      setWashTradingSelectedNode({
        address: data.fullAddress,
        isContract: data.isContract === 'yes',
        connections: node.degree(),
        type: data.nodeType,
      });
      if (data.isContract !== 'yes') {
        fetchWashTradingWalletDetails(data.fullAddress);
      } else {
        setWashTradingNodeDetails(null);
      }
    });

    cy.on('tap', function (evt) {
      if (evt.target === cy) {
        setWashTradingSelectedNode(null);
        setWashTradingNodeDetails(null);
      }
    });

    cy.ready(() => { cy.fit(undefined, 30); });
    washTradingCyRef.current = cy;

    return () => {
      if (washTradingCyRef.current) {
        washTradingCyRef.current.destroy();
        washTradingCyRef.current = null;
      }
    };
  }, [washTradingGraph]);

  // Render flow of funds Cytoscape graph
  useEffect(() => {
    if (!flowOfFundsGraph || !flowOfFundsContainerRef.current) return;

    if (flowOfFundsCyRef.current) {
      flowOfFundsCyRef.current.destroy();
      flowOfFundsCyRef.current = null;
    }

    const STABLECOINS = new Set([
      '0xdac17f958d2ee523a2206206994597c13d831ec7',
      '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
      '0x6b175474e89094c44da98b954eedeac495271d0f',
    ]);
    const STABLECOIN_LABELS = {
      '0xdac17f958d2ee523a2206206994597c13d831ec7': 'USDT',
      '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': 'USDC',
      '0x6b175474e89094c44da98b954eedeac495271d0f': 'DAI',
    };

    const allGraphNodes = (flowOfFundsGraph.nodes || []).filter(n => n.type !== 'cluster');
    const allNodeIds = new Set(allGraphNodes.map(n => n.id));

    const rawEdges = (flowOfFundsGraph.edges || []).filter(
      e => e.type !== 'member_of' && e.type !== 'targets'
        && allNodeIds.has(e.source) && allNodeIds.has(e.target)
        && STABLECOINS.has(e.target)
    );

    const edgeElements = [];
    const connectedWallets = new Set();
    const dedupEdges = new Set();
    rawEdges.forEach((edge, idx) => {
      const key = `${edge.source}->${edge.target}`;
      if (dedupEdges.has(key)) return;
      dedupEdges.add(key);
      connectedWallets.add(edge.source);
      edgeElements.push({
        data: {
          id: `fof-edge-${idx}`,
          source: edge.source,
          target: edge.target,
          method: edge.method,
          edgeType: edge.type || 'transferred',
        }
      });
    });

    const nodeElements = [];
    allGraphNodes.forEach(node => {
      const isContract = node.type === 'contract';
      if (isContract && !STABLECOINS.has(node.id)) return;
      if (!isContract && !connectedWallets.has(node.id)) return;
      nodeElements.push({
        data: {
          id: node.id,
          label: isContract ? (STABLECOIN_LABELS[node.id] || 'CONTRACT') : (node.id.substring(0, 6) + '...' + node.id.slice(-4)),
          fullAddress: node.id,
          isContract: isContract ? 'yes' : 'no',
          nodeType: node.type || 'wallet',
        }
      });
    });

    if (nodeElements.length === 0) return;

    const container = flowOfFundsContainerRef.current;
    const cw = container.clientWidth || 600;
    const ch = container.clientHeight || 500;
    const centerX = cw / 2;
    const centerY = ch / 2;

    const walletNodes = nodeElements.filter(e => e.data.isContract === 'no');
    const contractNodes = nodeElements.filter(e => e.data.isContract === 'yes');

    const outerR = Math.min(centerX, centerY) - 12;

    const stablecoinList = [...STABLECOINS];
    contractNodes.forEach((n) => {
      const idx = stablecoinList.indexOf(n.data.id);
      const si = idx >= 0 ? idx : 0;
      const angle = (2 * Math.PI * si) / 3 - Math.PI / 2;
      const r = outerR * 0.22;
      n.position = { x: centerX + r * Math.cos(angle), y: centerY + r * Math.sin(angle) };
    });

    walletNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(walletNodes.length, 1) - Math.PI / 2;
      const band = i % 4;
      const r = band === 0 ? outerR
              : band === 1 ? outerR * 0.90
              : band === 2 ? outerR * 0.80
              : outerR * 0.70;
      n.position = { x: centerX + r * Math.cos(angle), y: centerY + r * Math.sin(angle) };
    });

    const cy = cytoscape({
      container,
      elements: [...contractNodes, ...walletNodes, ...edgeElements],
      userZoomingEnabled: true,
      userPanningEnabled: true,
      minZoom: 0.2,
      maxZoom: 5,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#da4b4b',
            'label': '',
            'width': 6,
            'height': 6,
            'border-width': 0
          }
        },
        {
          selector: 'node[isContract = "yes"]',
          style: {
            'background-color': '#4a7c59',
            'border-color': '#4a7c59',
            'border-width': 3,
            'width': 32,
            'height': 32,
            'shape': 'diamond',
            'label': 'data(label)',
            'font-size': 9,
            'color': '#ffffff',
            'text-valign': 'bottom',
            'text-margin-y': 6,
            'font-weight': 'bold',
            'text-background-color': '#0c0c0c',
            'text-background-opacity': 0.8,
            'text-background-padding': 2,
          }
        },
        {
          selector: 'edge',
          style: {
            'line-color': '#4a7c59',
            'width': 0.6,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#4a7c59',
            'curve-style': 'straight',
            'arrow-scale': 0.4,
            'opacity': 0.35
          }
        },
        {
          selector: 'node:selected',
          style: { 'border-width': 3, 'border-color': '#ffffff' }
        },
        {
          selector: 'edge:selected',
          style: { 'line-color': '#ffffff', 'target-arrow-color': '#ffffff', 'width': 2, 'opacity': 1 }
        }
      ],
      layout: { name: 'preset' }
    });

    cy.on('tap', 'node', function (evt) {
      const node = evt.target;
      const data = node.data();
      setFlowOfFundsSelectedNode({
        address: data.fullAddress,
        isContract: data.isContract === 'yes',
        connections: node.degree(),
        type: data.nodeType,
      });
      if (data.isContract !== 'yes') {
        fetchFlowOfFundsWalletDetails(data.fullAddress);
      } else {
        setFlowOfFundsNodeDetails(null);
      }
    });

    cy.on('tap', function (evt) {
      if (evt.target === cy) {
        setFlowOfFundsSelectedNode(null);
        setFlowOfFundsNodeDetails(null);
      }
    });

    cy.ready(() => { cy.fit(undefined, 30); });
    flowOfFundsCyRef.current = cy;

    return () => {
      if (flowOfFundsCyRef.current) {
        flowOfFundsCyRef.current.destroy();
        flowOfFundsCyRef.current = null;
      }
    };
  }, [flowOfFundsGraph]);

  // Fetch overall network stats from ClickHouse
  const fetchNetworkStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${graphApiUrl}/v1/network/plume/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNetworkStats(data);
      }
    } catch (err) {
      console.error('Error fetching network stats:', err);
      // Use fallback data if API not available
      setNetworkStats({
        totalWallets: 9716,
        totalClusters: 1,
        totalTransfers: 0,
        activityPeriod: { first: '2025-09-14', last: '2025-10-29' }
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch high commitment wallets (non-sybil)
  const fetchHighCommitWallets = async () => {
    setLoadingHighCommit(true);
    try {
      const [walletsRes, statsRes] = await Promise.all([
        fetch(`${graphApiUrl}/v1/network/plume/high-commit/wallets?limit=100`, { credentials: 'include' }),
        fetch(`${graphApiUrl}/v1/network/plume/high-commit/stats`, { credentials: 'include' })
      ]);

      if (walletsRes.ok) {
        const data = await walletsRes.json();
        if (data.success) {
          setHighCommitWallets(data.wallets || []);
        }
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        if (data.success) {
          setHighCommitStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching high commitment data:', err);
    } finally {
      setLoadingHighCommit(false);
    }
  };

  // Fetch Neo4j graph for a high commitment wallet
  const fetchHighCommitWalletGraph = async (walletAddress) => {
    setLoadingHighCommitGraph(true);
    setHighCommitGraph(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/high-commit/wallet/${encodeURIComponent(walletAddress)}/graph`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.graph) {
          setHighCommitGraph(data);
        }
      }
    } catch (err) {
      console.error('Error fetching high commit wallet graph:', err);
    } finally {
      setLoadingHighCommitGraph(false);
    }
  };

  // Handle high commitment wallet selection
  const handleHighCommitWalletSelect = (wallet) => {
    if (selectedHighCommitWallet?.address === wallet.address) {
      setSelectedHighCommitWallet(null);
      setHighCommitGraph(null);
    } else {
      setSelectedHighCommitWallet(wallet);
      fetchHighCommitWalletGraph(wallet.address);
    }
  };

  // Fetch available time periods for the cluster
  const fetchTimePeriods = async () => {
    setLoadingPeriods(true);
    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/cluster/alpha/time-periods`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.periods) {
          setTimePeriods(data.periods);
          // Auto-select first period
          if (data.periods.length > 0 && !selectedPeriod) {
            setSelectedPeriod(data.periods[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching time periods:', err);
    } finally {
      setLoadingPeriods(false);
    }
  };

  // Fetch Neo4j graph for Cluster Alpha (with time period filter)
  const fetchClusterGraph = async (periodId = null) => {
    setLoadingGraph(true);
    setClusterGraph(null);
    setSelectedNode(null);
    setSelectedEdge(null);
    setNodeContractHistory(null);
    setEdgeTransferData(null);

    try {
      // Build URL with optional period parameter
      let url = `${graphApiUrl}/v1/network/plume/cluster/alpha/graph`;
      if (periodId) {
        url += `?period=${periodId}&limit=500`;
      } else {
        url += '?limit=500';
      }

      const response = await fetch(url, { credentials: 'include' });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.graph) {
          const nodes = data.graph.nodes || [];
          const edges = data.graph.edges || [];

          setGraphStats({
            displayedNodes: nodes.length,
            displayedEdges: edges.length,
            totalNodes: data.stats?.totalNodes || nodes.length,
            centroidAddress: clusterAlpha.centroidAddress,
            period: data.period
          });

          setClusterGraph({ nodes, edges });
        }
      }
    } catch (err) {
      console.error('Error fetching cluster graph:', err);
      setError('Failed to load cluster graph');
    } finally {
      setLoadingGraph(false);
    }
  };

  // Fetch contract history for a node from plume_overlap_contract_history
  const fetchNodeContractHistory = async (walletAddress) => {
    setLoadingNodeData(true);
    setNodeContractHistory(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/wallet/${encodeURIComponent(walletAddress)}/contracts`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setNodeContractHistory(data);
      }
    } catch (err) {
      console.error('Error fetching node contract history:', err);
    } finally {
      setLoadingNodeData(false);
    }
  };

  // Fetch transfer data for an edge from sybil_clusters
  const fetchEdgeTransferData = async (hubAddress, spokeAddress) => {
    setLoadingEdgeData(true);
    setEdgeTransferData(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/transfers?hub=${encodeURIComponent(hubAddress)}&spoke=${encodeURIComponent(spokeAddress)}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setEdgeTransferData(data);
      }
    } catch (err) {
      console.error('Error fetching edge transfer data:', err);
    } finally {
      setLoadingEdgeData(false);
    }
  };

  // ============================================================================
  // USER DASHBOARD TABLE FUNCTIONS
  // ============================================================================

  // Fetch metadata for an assigned table
  const fetchTableMetadata = async (tableName) => {
    setLoadingTableMetadata(prev => ({ ...prev, [tableName]: true }));

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/metadata`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserTableMetadata(prev => ({ ...prev, [tableName]: data.metadata }));
        }
      }
    } catch (err) {
      console.error(`Error fetching metadata for ${tableName}:`, err);
    } finally {
      setLoadingTableMetadata(prev => ({ ...prev, [tableName]: false }));
    }
  };

  // Fetch graph for an assigned table
  const fetchTableGraph = async (tableName) => {
    setLoadingTableGraph(prev => ({ ...prev, [tableName]: true }));

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/graph?limit=500`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserTableGraphs(prev => ({ ...prev, [tableName]: data }));
        } else if (data.needsSetup) {
          // Graph not set up yet - trigger setup
          console.log(`Graph needs setup for ${tableName}`);
        }
      }
    } catch (err) {
      console.error(`Error fetching graph for ${tableName}:`, err);
    } finally {
      setLoadingTableGraph(prev => ({ ...prev, [tableName]: false }));
    }
  };

  // Fetch node details for user dashboard graph
  const fetchUserTableNodeDetails = async (tableName, nodeId) => {
    setLoadingNodeDetails(true);
    setSelectedTableNodeDetails(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/node/${encodeURIComponent(nodeId)}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedTableNodeDetails(data);
        }
      }
    } catch (err) {
      console.error(`Error fetching node details:`, err);
    } finally {
      setLoadingNodeDetails(false);
    }
  };

  // Fetch edge details for user dashboard graph
  const fetchUserTableEdgeDetails = async (tableName, source, target) => {
    setLoadingEdgeDetails(true);
    setSelectedTableEdgeDetails(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/edge?source=${encodeURIComponent(source)}&target=${encodeURIComponent(target)}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedTableEdgeDetails(data);
        }
      }
    } catch (err) {
      console.error(`Error fetching edge details:`, err);
    } finally {
      setLoadingEdgeDetails(false);
    }
  };

  // Fetch entities list for a table (sidebar)
  const fetchTableEntities = async (tableName) => {
    setLoadingEntities(prev => ({ ...prev, [tableName]: true }));

    try {
      // Special handling for high_commit table - use high-commit wallets API for full metadata
      if (tableName === 'high_commit') {
        const [walletsResponse, statsResponse] = await Promise.all([
          fetch(`${graphApiUrl}/v1/network/plume/high-commit/wallets?limit=100`, { credentials: 'include' }),
          fetch(`${graphApiUrl}/v1/network/plume/high-commit/stats`, { credentials: 'include' })
        ]);

        if (walletsResponse.ok) {
          const walletsData = await walletsResponse.json();
          const statsData = statsResponse.ok ? await statsResponse.json() : null;

          if (walletsData.success) {
            // Transform high-commit data to entity format with full metadata
            const entities = (walletsData.wallets || []).map(wallet => ({
              id: wallet.address,
              count: wallet.depositCount + wallet.withdrawalCount,
              // Include full wallet metadata for high_commit rendering
              isHighCommit: true,
              isSybil: false, // All wallets from this endpoint are non-sybil
              leaderboardPosition: wallet.leaderboardPosition,
              plumePoints: wallet.plumePoints,
              timeCommitmentDays: wallet.timeCommitmentDays,
              timeCommitmentHours: wallet.timeCommitmentHours,
              totalDeposits: wallet.totalDeposits,
              depositCount: wallet.depositCount,
              totalWithdrawals: wallet.totalWithdrawals,
              withdrawalCount: wallet.withdrawalCount,
              tvlUsd: wallet.tvlUsd,
              netPosition: wallet.netPosition
            }));

            setTableEntities(prev => ({
              ...prev,
              [tableName]: {
                success: true,
                primaryColumn: 'wallet_address',
                totalEntities: walletsData.totalCount || entities.length,
                entities,
                // Include stats for header display
                highCommitStats: statsData?.stats || null
              }
            }));
          }
        }
      } else {
        // Standard entity fetch for other tables
        const response = await fetch(
          `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/entities?limit=100`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setTableEntities(prev => ({ ...prev, [tableName]: data }));
          }
        }
      }
    } catch (err) {
      console.error(`Error fetching entities for ${tableName}:`, err);
    } finally {
      setLoadingEntities(prev => ({ ...prev, [tableName]: false }));
    }
  };

  // Fetch entity details
  const fetchEntityDetails = async (tableName, entityId) => {
    setLoadingEntityDetails(true);
    setEntityDetails(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/entity/${encodeURIComponent(entityId)}/details`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEntityDetails(data);

          // For wallet_profiles, use the embedded graph instead of fetching separately
          if (data.embeddedGraph) {
            setEntityGraph({
              success: true,
              graph: data.embeddedGraph
            });
          } else if (tableName === 'wallet_profiles') {
            // No embedded graph for this wallet_profiles entity - clear graph
            setEntityGraph(null);
          }
        }
      }
    } catch (err) {
      console.error(`Error fetching entity details:`, err);
    } finally {
      setLoadingEntityDetails(false);
      // For wallet_profiles, loading state was set in handleEntitySelect - clear it here
      if (tableName === 'wallet_profiles') {
        setLoadingEntityGraph(false);
      }
    }
  };

  // Fetch entity-specific graph
  const fetchEntityGraph = async (tableName, entityId, period = null) => {
    setLoadingEntityGraph(true);
    setEntityGraph(null);

    try {
      let url = `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/entity/${encodeURIComponent(entityId)}/graph?limit=500`;
      if (period) {
        url += `&period=${encodeURIComponent(period)}`;
      }

      const response = await fetch(url, { credentials: 'include' });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEntityGraph(data);
        }
      }
    } catch (err) {
      console.error(`Error fetching entity graph:`, err);
    } finally {
      setLoadingEntityGraph(false);
    }
  };

  // Fetch time periods for a table
  const fetchTableTimePeriods = async (tableName) => {
    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/table/${encodeURIComponent(tableName)}/time-periods`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTableTimePeriods(prev => ({ ...prev, [tableName]: data }));
        }
      }
    } catch (err) {
      console.error(`Error fetching time periods:`, err);
    }
  };

  // Handle entity selection
  const handleEntitySelect = (tableName, entity) => {
    if (selectedEntity?.id === entity.id && selectedEntityTable === tableName) {
      // Deselect
      setSelectedEntity(null);
      setSelectedEntityTable(null);
      setEntityDetails(null);
      setEntityGraph(null);
      setSybilNodeContractHistory(null);
      setSelectedGraphNode(null);
    } else {
      setSelectedEntity(entity);
      setSelectedEntityTable(tableName);
      setSybilNodeContractHistory(null);
      setSelectedGraphNode(null);
      setEntityGraph(null);  // Clear previous graph before fetching new data
      setEdgeTransactions(null);  // Clear previous edge transactions
      setSelectedGraphEdge(null);  // Clear previous edge selection
      fetchEntityDetails(tableName, entity.id);
      // For wallet_profiles, the graph comes from entityDetails, skip separate fetch
      if (tableName !== 'wallet_profiles') {
        // Use sybil period for sybil_clusters, regular period for others
        const period = tableName === 'sybil_clusters' ? selectedSybilPeriod : selectedTimePeriod;
        fetchEntityGraph(tableName, entity.id, period);
      } else {
        // For wallet_profiles, graph comes from entityDetails - set loading state
        setLoadingEntityGraph(true);
      }
    }
  };

  // Handle time period change
  const handleTimePeriodChange = (tableName, periodId) => {
    setSelectedTimePeriod(periodId);
    if (selectedEntity) {
      fetchEntityGraph(tableName, selectedEntity.id, periodId);
    }
  };

  // Fetch transactions for an edge (when edge is clicked)
  const fetchEdgeTransactions = async (tableName, sourceId, targetId, edgeType = 'transfer', embeddedTransactions = null) => {
    setLoadingEdgeTransactions(true);
    setEdgeTransactions(null);
    try {
      // For high_commit table with embedded transactions, use the embedded data directly
      if (tableName === 'high_commit' && embeddedTransactions && embeddedTransactions.length > 0) {
        setEdgeTransactions({
          transactions: embeddedTransactions,
          totals: {},
          edgeType: edgeType
        });
        setLoadingEdgeTransactions(false);
        return;
      }

      let response;

      // For wallet_profiles, use the dedicated wallet profile edge transactions endpoint
      if (tableName === 'wallet_profiles' && selectedEntity) {
        const apiEdgeType = edgeType === 'contract_call' ? 'contract_call' : 'transfer';
        const counterparty = sourceId === selectedEntity.id ? targetId : sourceId;
        response = await fetch(
          `${graphApiUrl}/v1/network/plume/wallet-profiles/${encodeURIComponent(selectedEntity.id)}/edge-transactions?counterparty=${encodeURIComponent(counterparty)}&type=${apiEdgeType}`,
          { credentials: 'include' }
        );
      } else if (tableName === 'high_commit' && selectedEntity) {
        // For high_commit, fetch from the dedicated endpoint
        response = await fetch(
          `${graphApiUrl}/v1/network/plume/high-commit/wallet/${encodeURIComponent(selectedEntity.id)}/transactions?type=${edgeType}`,
          { credentials: 'include' }
        );
      } else {
        response = await fetch(
          `${graphApiUrl}/v1/dashboard/table/${tableName}/edge/transactions?source=${encodeURIComponent(sourceId)}&target=${encodeURIComponent(targetId)}`,
          { credentials: 'include' }
        );
      }

      if (response.ok) {
        const data = await response.json();
        // Handle both response formats
        setEdgeTransactions({
          transactions: data.transactions || [],
          totals: data.totals || {},
          edgeType: data.edgeType || edgeType
        });
      }
    } catch (err) {
      console.error('Error fetching edge transactions:', err);
    } finally {
      setLoadingEdgeTransactions(false);
    }
  };

  // Fetch contract details (when contract node is clicked)
  const fetchContractDetails = async (contractAddress) => {
    setLoadingContractDetails(true);
    setContractDetails(null);
    try {
      const response = await fetch(
        `${graphApiUrl}/v1/dashboard/contract/${encodeURIComponent(contractAddress)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setContractDetails(data);
      }
    } catch (err) {
      console.error('Error fetching contract details:', err);
    } finally {
      setLoadingContractDetails(false);
    }
  };

  // Handle graph edge click
  const handleGraphEdgeClick = (tableName, edge) => {
    setSelectedGraphEdge(edge);
    setSelectedGraphNode(null);
    setContractDetails(null);
    // Pass embedded transactions if available (for high_commit edges)
    const embeddedTxs = edge.embeddedTransactions || null;
    fetchEdgeTransactions(tableName, edge.source, edge.target, edge.type || 'transfer', embeddedTxs);
  };

  // Handle graph node click
  const handleGraphNodeClick = (tableName, node) => {
    setSelectedGraphNode(node);
    setSelectedGraphEdge(null);
    setEdgeTransactions(null);
    setContractDetails(null);
    setSybilNodeContractHistory(null);

    if (node.type === 'contract') {
      fetchContractDetails(node.id);
    }
    // For sybil_clusters table, fetch contract history for the wallet node
    if (tableName === 'sybil_clusters' && node.type !== 'contract') {
      fetchSybilNodeContractHistory(node.id);
    }
  };

  // Fetch contract history for a sybil cluster node
  const fetchSybilNodeContractHistory = async (walletAddress) => {
    setLoadingSybilNodeData(true);
    setSybilNodeContractHistory(null);

    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/wallet/${encodeURIComponent(walletAddress)}/contracts`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setSybilNodeContractHistory(data);
      }
    } catch (err) {
      console.error('Error fetching sybil node contract history:', err);
    } finally {
      setLoadingSybilNodeData(false);
    }
  };

  // Fetch time periods for sybil_clusters (uses cluster alpha API)
  const fetchSybilClusterTimePeriods = async () => {
    setLoadingSybilPeriods(true);
    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/cluster/alpha/time-periods`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.periods) {
          setSybilClusterTimePeriods(data.periods);
          if (data.periods.length > 0 && !selectedSybilPeriod) {
            setSelectedSybilPeriod(data.periods[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching sybil cluster time periods:', err);
    } finally {
      setLoadingSybilPeriods(false);
    }
  };

  // Handle sybil period change
  const handleSybilPeriodChange = (periodId) => {
    setSelectedSybilPeriod(periodId);
    if (selectedEntity && selectedEntityTable === 'sybil_clusters') {
      fetchEntityGraph('sybil_clusters', selectedEntity.id, periodId);
    }
  };

  // Load metadata, entities, and time periods for assigned tables
  useEffect(() => {
    if (!alwaysShowPlume && assignedTables.length > 0) {
      assignedTables.forEach(tableName => {
        if (!userTableMetadata[tableName] && !loadingTableMetadata[tableName]) {
          fetchTableMetadata(tableName);
        }
        if (!tableEntities[tableName] && !loadingEntities[tableName]) {
          fetchTableEntities(tableName);
        }
        if (!tableTimePeriods[tableName]) {
          fetchTableTimePeriods(tableName);
        }
      });
      // Fetch sybil cluster time periods if sybil_clusters is assigned
      if (assignedTables.includes('sybil_clusters') && sybilClusterTimePeriods.length === 0 && !loadingSybilPeriods) {
        fetchSybilClusterTimePeriods();
      }
    }
  }, [assignedTables, alwaysShowPlume]);

  // Render cytoscape graph for a user dashboard table (legacy - kept for compatibility)
  useEffect(() => {
    if (alwaysShowPlume || !expandedUserTable) return;

    const graphData = userTableGraphs[expandedUserTable];
    if (!graphData || !graphData.graph) return;

    let isMounted = true;

    // Defer Cytoscape initialization to avoid React DOM conflicts
    const initTimeout = setTimeout(() => {
      if (!isMounted) return;

      const containerRef = userGraphContainerRefs.current[expandedUserTable];
      if (!containerRef) return;

      // Destroy existing graph safely
      try {
        if (userGraphCyRefs.current[expandedUserTable]) {
          userGraphCyRefs.current[expandedUserTable].destroy();
          userGraphCyRefs.current[expandedUserTable] = null;
        }
        // Clear container innerHTML instead of removeChild to avoid React conflicts
        containerRef.innerHTML = '';
      } catch (e) {
        console.warn('Error destroying previous graph:', e);
      }

      const elements = [];
      const { nodes, edges } = graphData.graph;

      // Add nodes
      (nodes || []).forEach(node => {
        elements.push({
          data: {
            id: node.id,
            label: node.label || (node.id.length > 10 ? node.id.slice(0, 6) + '...' + node.id.slice(-4) : node.id),
            type: node.type || 'wallet',
            fullAddress: node.id
          }
        });
      });

      // Add edges
      (edges || []).forEach((edge, idx) => {
        elements.push({
          data: {
            id: edge.id || `edge-${idx}`,
            source: edge.source,
            target: edge.target,
            type: edge.type || 'transfer'
          }
        });
      });

      if (elements.length === 0) return;

      try {
        const cy = cytoscape({
          container: containerRef,
          elements,
          userZoomingEnabled: true,
          userPanningEnabled: true,
          minZoom: 0.1,
          maxZoom: 3,
          style: [
            {
              selector: 'node',
              style: {
                'background-color': '#5a6f8e',
                'label': 'data(label)',
                'width': 40,
                'height': 40,
                'border-width': 2,
                'border-color': '#5a6f8e',
                'color': '#fff',
                'font-size': 10,
                'text-valign': 'bottom',
                'text-margin-y': 8
              }
            },
            {
              selector: 'edge',
              style: {
                'line-color': '#6c757d',
                'width': 1.5,
                'target-arrow-shape': 'triangle',
                'target-arrow-color': '#6c757d',
                'curve-style': 'bezier',
                'arrow-scale': 0.8
              }
            },
            {
              selector: 'node:selected',
              style: {
                'border-width': 4,
                'border-color': '#ffffff'
              }
            },
            {
              selector: 'edge:selected',
              style: {
                'line-color': '#ffffff',
                'target-arrow-color': '#ffffff',
                'width': 3
              }
            }
          ],
          layout: {
            name: 'fcose',
            animate: false,
            fit: true,
            padding: 50,
            quality: 'default',
            randomize: true,
            nodeSeparation: 150,
            idealEdgeLength: 100,
            nodeRepulsion: 10000
          }
        });

        // Node click handler
        cy.on('tap', 'node', function (evt) {
          const node = evt.target;
          const data = node.data();
          setSelectedTableEdge(null);
          setSelectedTableEdgeDetails(null);
          setSelectedTableNode({
            id: data.fullAddress || data.id,
            type: data.type,
            connections: node.degree()
          });
          fetchUserTableNodeDetails(expandedUserTable, data.fullAddress || data.id);
        });

        // Edge click handler
        cy.on('tap', 'edge', function (evt) {
          const edge = evt.target;
          const data = edge.data();
          setSelectedTableNode(null);
          setSelectedTableNodeDetails(null);
          setSelectedTableEdge({
            source: data.source,
            target: data.target,
            type: data.type
          });
          fetchUserTableEdgeDetails(expandedUserTable, data.source, data.target);
        });

        // Background click - deselect
        cy.on('tap', function (evt) {
          if (evt.target === cy) {
            setSelectedTableNode(null);
            setSelectedTableEdge(null);
            setSelectedTableNodeDetails(null);
            setSelectedTableEdgeDetails(null);
          }
        });

        cy.ready(() => {
          if (isMounted) {
            cy.fit(undefined, 30);
          }
        });

        userGraphCyRefs.current[expandedUserTable] = cy;
      } catch (e) {
        console.error('Error creating Cytoscape graph:', e);
      }
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      try {
        if (userGraphCyRefs.current[expandedUserTable]) {
          userGraphCyRefs.current[expandedUserTable].destroy();
          userGraphCyRefs.current[expandedUserTable] = null;
        }
      } catch (e) {
        console.warn('Error in cleanup:', e);
      }
    };
  }, [expandedUserTable, userTableGraphs, alwaysShowPlume]);

  // Render cytoscape graph for selected entity
  useEffect(() => {
    if (alwaysShowPlume || !entityGraph || !selectedEntity || !selectedEntityTable) return;

    const graphData = entityGraph.graph;
    if (!graphData || !graphData.nodes || graphData.nodes.length === 0) return;

    // Use the table that the selected entity belongs to
    const tableName = selectedEntityTable;

    const containerKey = `entity-graph-${tableName}`;
    let isMounted = true;

    // Defer Cytoscape initialization to avoid React DOM conflicts
    const initTimeout = setTimeout(() => {
      if (!isMounted) return;

      const containerRef = userGraphContainerRefs.current[containerKey];
      if (!containerRef) return;

      // Destroy existing graph safely
      try {
        if (userGraphCyRefs.current[containerKey]) {
          userGraphCyRefs.current[containerKey].destroy();
          userGraphCyRefs.current[containerKey] = null;
        }
      } catch (e) {
        console.warn('Error destroying previous graph:', e);
      }

      const elements = [];
      const { nodes, edges } = graphData;

      // Add nodes with styling based on type - show full addresses
      // For sybil_clusters, we hide labels
      const isSybilClusters = tableName === 'sybil_clusters';
      (nodes || []).forEach(node => {
        // Format address consistently: 0x prefix + first 6 + ... + last 4
        const addr = node.id || '';
        // For sybil_clusters, don't show labels
        const formattedLabel = isSybilClusters ? '' : (node.label || (addr.length > 16 ? `${addr.slice(0, 10)}...${addr.slice(-6)}` : addr));
        // Handle both 'type' and 'node_type' (wallet_profiles uses node_type)
        const nodeType = node.type || node.node_type || 'secondary';
        elements.push({
          data: {
            id: node.id,
            label: formattedLabel,
            type: nodeType,
            fullAddress: node.id
          }
        });
      });

      // Add edges with metadata
      (edges || []).forEach((edge, idx) => {
        elements.push({
          data: {
            id: edge.id || `edge-${idx}`,
            source: edge.source,
            target: edge.target,
            // Handle both 'type' and 'edge_type' (wallet_profiles uses edge_type)
            type: edge.type || edge.edge_type || 'transfer',
            // Handle both 'callCount' and 'count' (wallet_profiles uses count)
            callCount: edge.callCount || edge.count || 1,
            methods: edge.methods || [],
            totalValue: edge.total_value || edge.totalValue || '0',
            // For high_commit edges, include transaction data
            assets: edge.assets || null,
            txHash: edge.txHash || null,
            blockTime: edge.blockTime || null,
            blockNumber: edge.blockNumber || null,
            method: edge.method || null
          }
        });
      });

      if (elements.length === 0) return;

      // Determine node color based on table type
      // For sybil_clusters: use matte red for spoke nodes, matte blue for centroid (like Plume Dashboard)
      const nodeColor = isSybilClusters ? '#da4b4b' : '#5a6f8e'; // Matte red for sybil_clusters spokes, blue for others
      const primaryNodeColor = isSybilClusters ? '#5a6f8e' : '#5a6f8e'; // Matte blue for centroid (primary)

      try {
        const cy = cytoscape({
          container: containerRef,
          elements: elements,
          userZoomingEnabled: true,
          userPanningEnabled: true,
          minZoom: 0.1,
          maxZoom: 3,
          style: isSybilClusters ? [
            // Sybil clusters style - matching Plume Dashboard
            {
              selector: 'node',
              style: {
                'background-color': '#da4b4b',
                'label': '',
                'width': 20,
                'height': 20,
                'border-width': 2,
                'border-color': '#da4b4b'
              }
            },
            {
              // Centroid/Primary node - matte blue like Plume Dashboard
              selector: 'node[type="primary"]',
              style: {
                'background-color': '#5a6f8e',
                'border-color': '#5a6f8e',
                'border-width': 6,
                'width': 80,
                'height': 80,
                'font-size': 10,
                'font-weight': 'bold',
                'text-outline-width': 2,
                'text-outline-color': '#5a6f8e'
              }
            },
            {
              selector: 'edge',
              style: {
                'line-color': '#6c757d',
                'width': 1.5,
                'target-arrow-shape': 'triangle',
                'target-arrow-color': '#6c757d',
                'curve-style': 'bezier',
                'arrow-scale': 0.8
              }
            },
            {
              selector: 'node:selected',
              style: {
                'border-width': 4,
                'border-color': '#ffffff'
              }
            },
            {
              selector: 'edge:selected',
              style: {
                'line-color': '#ffffff',
                'target-arrow-color': '#ffffff',
                'width': 3
              }
            }
          ] : [
            // Standard style for other tables
            {
              // Default node style - wallets (solid circles)
              selector: 'node',
              style: {
                'width': 50,
                'height': 50,
                'background-color': nodeColor,
                'background-opacity': 1,
                'label': 'data(label)',
                'color': '#9ca3af',
                'font-size': '9px',
                'font-family': 'Satoshi, -apple-system, sans-serif',
                'text-valign': 'bottom',
                'text-halign': 'center',
                'text-margin-y': 10,
                'border-width': 0,
                'shape': 'ellipse'
              }
            },
            {
              // Primary wallet node (the selected entity) - solid, no border
              selector: 'node[type="primary"]',
              style: {
                'width': 50,
                'height': 50,
                'background-color': primaryNodeColor,
                'background-opacity': 1,
                'border-width': 0,
                'font-size': '9px',
                'font-weight': 'bold',
                'shape': 'ellipse'
              }
            },
            {
              // Contract nodes (diamonds, matte green)
              selector: 'node[type="contract"]',
              style: {
                'width': 50,
                'height': 50,
                'background-color': '#4a7c59',
                'background-opacity': 1,
                'border-width': 0,
                'shape': 'diamond'
              }
            },
            {
              // Secondary wallet nodes - solid, no border
              selector: 'node[type="secondary"]',
              style: {
                'width': 50,
                'height': 50,
                'background-color': nodeColor,
                'background-opacity': 1,
                'border-width': 0,
                'shape': 'ellipse'
              }
            },
            {
              selector: 'edge',
              style: {
                'width': 2,
                'line-color': '#4a4a4a',
                'target-arrow-color': '#4a4a4a',
                'target-arrow-shape': 'triangle',
                'curve-style': 'bezier',
                'arrow-scale': 0.8
              }
            },
            {
              // Deposit edges - green
              selector: 'edge[type="deposit"]',
              style: {
                'line-color': '#4a7c59',
                'target-arrow-color': '#4a7c59',
                'width': 2
              }
            },
            {
              // Withdraw edges - red
              selector: 'edge[type="withdraw"]',
              style: {
                'line-color': '#da4b4b',
                'target-arrow-color': '#da4b4b',
                'width': 2
              }
            },
            {
              selector: 'node:selected',
              style: {
                'border-color': '#ffffff',
                'border-width': 2
              }
            },
            {
              selector: 'edge:selected',
              style: {
                'line-color': '#ffffff',
                'target-arrow-color': '#ffffff',
                'width': 3
              }
            }
          ],
          layout: isSybilClusters ? {
            // Use fcose layout for sybil_clusters (like Plume Dashboard)
            name: 'fcose',
            animate: false,
            fit: true,
            padding: 50,
            quality: 'default',
            randomize: true,
            nodeSeparation: 200,
            idealEdgeLength: 150,
            edgeElasticity: 0.3,
            nestingFactor: 0.1,
            gravity: 0.08,
            gravityRange: 2.5,
            nodeRepulsion: 15000,
            numIter: 2500,
            tile: true,
            packComponents: true
          } : {
            name: 'cose',
            animate: false,
            fit: true,
            padding: 50,
            nodeRepulsion: function () { return 8000; },
            idealEdgeLength: function () { return 100; },
            edgeElasticity: function () { return 100; },
            gravity: 0.25
          }
        });

        cy.ready(() => {
          if (isMounted) {
            cy.fit(undefined, 30);
          }
        });

        // Edge click handler - show transaction details
        cy.on('tap', 'edge', function (evt) {
          const edge = evt.target;
          const edgeData = {
            id: edge.id(),
            source: edge.data('source'),
            target: edge.data('target'),
            type: edge.data('type'),
            callCount: edge.data('callCount'),
            methods: edge.data('methods'),
            // For high_commit, include the embedded transaction
            embeddedTransactions: edge.data('txHash') ? [{
              tx_id: edge.data('txHash'),
              block_time: edge.data('blockTime'),
              block_number: edge.data('blockNumber'),
              assets: edge.data('assets'),
              method: edge.data('method'),
              type: edge.data('type')
            }] : null
          };
          handleGraphEdgeClick(tableName, edgeData);
        });

        // Node click handler - show node details
        cy.on('tap', 'node', function (evt) {
          const node = evt.target;
          const nodeData = {
            id: node.data('id'),
            type: node.data('type'),
            label: node.data('label'),
            fullAddress: node.data('fullAddress') || node.data('id')
          };
          handleGraphNodeClick(tableName, nodeData);
        });

        userGraphCyRefs.current[containerKey] = cy;
      } catch (e) {
        console.error('Error creating Cytoscape graph:', e);
      }
    }, 50); // Small delay to let React finish DOM updates

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      try {
        if (userGraphCyRefs.current[containerKey]) {
          userGraphCyRefs.current[containerKey].destroy();
          userGraphCyRefs.current[containerKey] = null;
        }
      } catch (e) {
        console.warn('Error in cleanup:', e);
      }
    };
  }, [entityGraph, selectedEntity, selectedEntityTable, alwaysShowPlume]);

  // Handle cluster selection
  const handleClusterSelect = async (cluster) => {
    setSelectedCluster(cluster);
    // Fetch time periods first, then fetch graph for first period
    setLoadingPeriods(true);
    try {
      const response = await fetch(
        `${graphApiUrl}/v1/network/plume/cluster/alpha/time-periods`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.periods && data.periods.length > 0) {
          setTimePeriods(data.periods);
          const firstPeriod = data.periods[0].id;
          setSelectedPeriod(firstPeriod);
          fetchClusterGraph(firstPeriod);
        } else {
          // No periods, fetch without filter
          setTimePeriods([]);
          setSelectedPeriod(null);
          fetchClusterGraph();
        }
      }
    } catch (err) {
      console.error('Error fetching time periods:', err);
      fetchClusterGraph();
    } finally {
      setLoadingPeriods(false);
    }
  };

  // Handle time period change
  const handlePeriodChange = (periodId) => {
    setSelectedPeriod(periodId);
    fetchClusterGraph(periodId);
  };

  // Render cytoscape graph
  useEffect(() => {
    if (!clusterGraph || !containerRef.current) return;

    if (cyRef.current) {
      cyRef.current.destroy();
      cyRef.current = null;
    }

    const elements = [];
    const centroidLower = clusterAlpha.centroidAddress.toLowerCase();

    (clusterGraph.nodes || []).forEach(node => {
      const isCentroid = node.id.toLowerCase() === centroidLower;
      elements.push({
        data: {
          id: node.id,
          label: isCentroid ? 'CENTROID' : (node.id.substring(0, 6) + '...' + node.id.slice(-4)),
          fullAddress: node.id,
          isCentroid: isCentroid ? 'yes' : 'no',
          isSybil: 'yes'
        }
      });
    });

    (clusterGraph.edges || []).forEach((edge, idx) => {
      elements.push({
        data: {
          id: `edge-${idx}`,
          source: edge.source,
          target: edge.target,
          hubAddress: edge.hub || edge.source,
          spokeAddress: edge.spoke || edge.target
        }
      });
    });

    if (elements.length === 0) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      minZoom: 0.1,
      maxZoom: 3,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#da4b4b',
            'label': '',
            'width': 20,
            'height': 20,
            'border-width': 2,
            'border-color': '#da4b4b'
          }
        },
        {
          selector: 'node[isCentroid = "yes"]',
          style: {
            'background-color': '#5a6f8e',
            'border-color': '#5a6f8e',
            'border-width': 6,
            'width': 80,
            'height': 80,
            'font-size': 10,
            'font-weight': 'bold',
            'text-outline-width': 2,
            'text-outline-color': '#5a6f8e'
          }
        },
        {
          selector: 'edge',
          style: {
            'line-color': '#6c757d',
            'width': 1.5,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#6c757d',
            'curve-style': 'bezier',
            'arrow-scale': 0.8
          }
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 4,
            'border-color': '#ffffff'
          }
        },
        {
          selector: 'edge:selected',
          style: {
            'line-color': '#ffffff',
            'target-arrow-color': '#ffffff',
            'width': 3
          }
        }
      ],
      layout: {
        name: 'fcose',
        animate: false,
        fit: true,
        padding: 50,
        quality: 'default',
        randomize: true,
        nodeSeparation: 200,
        idealEdgeLength: 150,
        edgeElasticity: 0.3,
        nestingFactor: 0.1,
        gravity: 0.08,
        gravityRange: 2.5,
        nodeRepulsion: 15000,
        numIter: 2500,
        tile: true,
        packComponents: true
      }
    });

    // Node click handler
    cy.on('tap', 'node', function (evt) {
      const node = evt.target;
      const data = node.data();
      setSelectedEdge(null);
      setEdgeTransferData(null);
      setSelectedNode({
        address: data.fullAddress,
        isCentroid: data.isCentroid === 'yes',
        connections: node.degree()
      });
      fetchNodeContractHistory(data.fullAddress);
    });

    // Edge click handler
    cy.on('tap', 'edge', function (evt) {
      const edge = evt.target;
      const data = edge.data();
      setSelectedNode(null);
      setNodeContractHistory(null);
      setSelectedEdge({
        source: data.source,
        target: data.target,
        hubAddress: data.hubAddress,
        spokeAddress: data.spokeAddress
      });
      // For centroid graph, the hub is always the centroid
      fetchEdgeTransferData(clusterAlpha.centroidAddress, data.spokeAddress || data.target);
    });

    // Background click - deselect
    cy.on('tap', function (evt) {
      if (evt.target === cy) {
        setSelectedNode(null);
        setSelectedEdge(null);
        setNodeContractHistory(null);
        setEdgeTransferData(null);
      }
    });

    cy.ready(() => {
      cy.fit(undefined, 30);
    });

    cyRef.current = cy;

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [clusterGraph]);

  // Render high commitment wallet graph
  useEffect(() => {
    if (!highCommitGraph || !highCommitContainerRef.current) return;

    // Clear existing graph
    if (highCommitCyRef.current) {
      highCommitCyRef.current.destroy();
      highCommitCyRef.current = null;
    }

    const elements = [];
    const { nodes, edges } = highCommitGraph.graph;

    // Helper to abbreviate addresses
    const abbrevAddr = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

    // Add nodes
    (nodes || []).forEach((node) => {
      elements.push({
        data: {
          id: node.id,
          label: node.type === 'contract' ? (node.label || 'MetaMorpho') : abbrevAddr(node.address),
          type: node.type,
          fullAddress: node.address,
          isContract: node.type === 'contract',
          ...node
        }
      });
    });

    // Add edges
    (edges || []).forEach((edge, idx) => {
      elements.push({
        data: {
          id: `hc-edge-${idx}`,
          source: edge.source,
          target: edge.target,
          type: edge.type,
          method: edge.method,
          assets: edge.assets,
          txHash: edge.txHash,
          blockTime: edge.blockTime
        }
      });
    });

    if (elements.length === 0) return;

    const cy = cytoscape({
      container: highCommitContainerRef.current,
      elements,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      minZoom: 0.3,
      maxZoom: 3,
      style: [
        {
          selector: 'node[type = "wallet"]',
          style: {
            'background-color': '#4a7c59',
            'label': 'data(label)',
            'width': 60,
            'height': 60,
            'border-width': 3,
            'border-color': '#4a7c59',
            'color': '#fff',
            'font-size': 14,
            'font-weight': 'bold',
            'text-valign': 'bottom',
            'text-margin-y': 10
          }
        },
        {
          selector: 'node[type = "contract"]',
          style: {
            'background-color': '#5a6f8e',
            'label': 'data(label)',
            'width': 80,
            'height': 80,
            'border-width': 4,
            'border-color': '#5a6f8e',
            'shape': 'diamond',
            'color': '#fff',
            'font-size': 14,
            'font-weight': 'bold',
            'text-valign': 'bottom',
            'text-margin-y': 10
          }
        },
        {
          selector: 'edge[type = "deposit"]',
          style: {
            'line-color': '#4a7c59',
            'width': 2,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#4a7c59',
            'curve-style': 'bezier',
            'arrow-scale': 1
          }
        },
        {
          selector: 'edge[type = "withdraw"]',
          style: {
            'line-color': '#da4b4b',
            'width': 2,
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#da4b4b',
            'curve-style': 'bezier',
            'arrow-scale': 1
          }
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 5,
            'border-color': '#ffffff'
          }
        },
        {
          selector: 'edge:selected',
          style: {
            'width': 4
          }
        }
      ],
      layout: {
        name: 'cose',
        animate: false,
        fit: true,
        padding: 50,
        nodeRepulsion: 8000,
        idealEdgeLength: 150
      }
    });

    cy.ready(() => {
      cy.fit(undefined, 30);
    });

    highCommitCyRef.current = cy;

    return () => {
      if (highCommitCyRef.current) {
        highCommitCyRef.current.destroy();
        highCommitCyRef.current = null;
      }
    };
  }, [highCommitGraph]);

  const formatNumber = (num) => (num || 0).toLocaleString();
  const formatAddress = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  // Format pUSD amounts (6 decimals)
  const formatPUSD = (rawAmount) => {
    if (!rawAmount) return '0';
    try {
      // eslint-disable-next-line no-undef
      const raw = BigInt(rawAmount);
      const amount = Number(raw) / 1e6;
      return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch {
      return '0';
    }
  };

  const formatPlume = (weiStr) => {
    if (!weiStr) return '0';
    try {
      // eslint-disable-next-line no-undef
      const wei = BigInt(weiStr);
      const plume = Number(wei) / 1e18;
      return plume.toLocaleString(undefined, { maximumFractionDigits: 4 });
    } catch {
      return '0';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid my-4 px-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary">Loading network data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid my-4 px-4">
      {/* User Dashboard Header - Only show when viewing a specific user's dashboard */}
      {!alwaysShowPlume && (
        <div className="rounded p-4 mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div className="d-flex align-items-center gap-3 mb-2">
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#5a6f8e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}>
                  {(selectedUserDisplayName || (selectedUserEmail === 'signxy@proton.me' ? 'demo' : selectedUserEmail))?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <h2 className="text-white mb-0" style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                    {selectedUserDisplayName || (selectedUserEmail === 'signxy@proton.me' ? 'demo' : selectedUserEmail) || 'User Dashboard'}
                  </h2>
                  <p className="text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                    Customized dashboard view
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Tables Section */}
          {assignedTables.length > 0 ? (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #1a1a1a' }}>
              <h6 className="text-secondary mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <i className="bi bi-table me-2"></i>
                Assigned Table Views ({assignedTables.length})
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {assignedTables.map(tableName => (
                  <span
                    key={tableName}
                    className="badge"
                    style={{
                      backgroundColor: '#1a1a24',
                      color: '#5a6f8e',
                      border: '1px solid #2a2a34',
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    <i className="bi bi-database me-1"></i>
                    {tableName}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #1a1a1a' }}>
              <p className="text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                <i className="bi bi-info-circle me-2"></i>
                No table views assigned yet. Assign tables from the Query Console.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Incentive Farming Section - Only for signxy@proton.me */}
      {isSignxyUser && !showPlumeDashboard && (
        <div className="card text-light mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h5 className="card-title mb-2 text-white" style={{ fontWeight: 700, letterSpacing: '0.05rem' }}>
                  Incentive Farming
                </h5>
                <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#9ca3af' }}>
                  Ethereum Mainnet • Sybil cluster farming airdrop claim contracts
                </p>
              </div>
              <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>eip155:1</span>
            </div>

            {/* Stats Row */}
            {incentiveFarmingStats && (
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flagged Wallets</h6>
                    <h3 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{incentiveFarmingStats.totalWallets?.toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contract Anomaly</h6>
                    <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{incentiveFarmingStats.avgContractAnomaly?.toFixed(1)}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confidence</h6>
                    <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{incentiveFarmingStats.avgConfidence?.toFixed(0)}%</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activity Period</h6>
                    <h4 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {incentiveFarmingStats.activityPeriod?.first || '-'}
                    </h4>
                  </div>
                </div>
              </div>
            )}

            {/* Cluster Analysis */}
            <div className="row g-4">
              {/* Cluster List */}
              <div className="col-lg-4">
                <div className="rounded p-4" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                  <h5 className="text-white mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '1rem' }}>Detected Clusters (1)</h5>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div
                      className="p-3 mb-2 rounded shadow-sm"
                      style={{
                        backgroundColor: incentiveFarmingActive ? '#151515' : '#0c0c0c',
                        border: incentiveFarmingActive ? '1px solid #da4b4b' : '1px solid #1a1a1a',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        setIncentiveFarmingActive(true);
                        fetchIncentiveFarmingGraph();
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="text-white fw-bold" style={{ fontSize: '1.1rem' }}>
                          Cluster 916
                        </span>
                        <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>HIGH RISK</span>
                      </div>
                      <div className="mb-3">
                        <span style={{ color: '#da4b4b', fontSize: '1rem' }}>Sybil Incentive Farming Risk: High</span>
                      </div>
                      <div className="text-white" style={{ fontSize: '0.95rem' }}>
                        <div className="mb-2">
                          Target: <code style={{ fontSize: '0.85rem', color: '#5a6f8e' }}>0x96c6...e5af</code>
                        </div>
                        <div className="mb-2">
                          {incentiveFarmingStats?.totalWallets?.toLocaleString() || '~191'} wallets in cluster
                        </div>
                        <div className="mb-2">
                          Method: <code style={{ fontSize: '0.85rem', color: '#5a6f8e' }}>claim()</code>
                        </div>
                        <div className="mb-1">
                          Dec 2025
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="rounded p-3 mt-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                  <h6 className="text-secondary mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>Legend</h6>
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 18, height: 18, backgroundColor: '#4a7c59', marginRight: 10, transform: 'rotate(45deg)' }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Target Contract</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Sybil Wallet</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{ width: 14, height: 2, backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Claim Interaction</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph View & Details */}
              <div className="col-lg-8">
                {/* Node Details Panel */}
                {incentiveFarmingSelectedNode && (
                  <div className="rounded p-4 mb-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary small text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem' }}>
                      {incentiveFarmingSelectedNode.isContract ? 'Claim Contract' : 'Sybil Wallet'} Details
                    </h6>
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <span className="text-secondary small me-2">Address:</span>
                        <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{incentiveFarmingSelectedNode.address?.slice(0, 6)}...{incentiveFarmingSelectedNode.address?.slice(-4)}</code>
                        <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(incentiveFarmingSelectedNode.address)}>copy</button>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-secondary small me-2">Connections:</span>
                        <span className="text-white fw-bold">{incentiveFarmingSelectedNode.connections}</span>
                      </div>
                    </div>

                    {!incentiveFarmingSelectedNode.isContract && (
                      loadingIncentiveFarmingNode ? (
                        <div className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                          <span className="text-secondary">Loading wallet details...</span>
                        </div>
                      ) : incentiveFarmingNodeDetails ? (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Contract Anomaly</h6>
                              <h4 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{incentiveFarmingNodeDetails.contractAnomaly?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Transfer Anomaly</h6>
                              <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{incentiveFarmingNodeDetails.transferAnomaly?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Risk Score</h6>
                              <h4 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{incentiveFarmingNodeDetails.riskScore?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Confidence</h6>
                              <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{incentiveFarmingNodeDetails.confidence?.toFixed(0)}%</h4>
                            </div>
                          </div>
                          {incentiveFarmingNodeDetails.contracts && incentiveFarmingNodeDetails.contracts.length > 0 && (
                            <div className="col-12">
                              <h6 className="text-secondary text-uppercase fw-bold mt-2 mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                Contract Interactions ({incentiveFarmingNodeDetails.contracts.length})
                              </h6>
                              <div className="table-responsive rounded" style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.9rem', backgroundColor: '#0c0c0c' }}>
                                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0c0c0c', borderBottom: '1px solid #1a1a1a' }}>
                                    <tr>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Contract</th>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Method</th>
                                      <th className="text-secondary py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>Calls</th>
                                    </tr>
                                  </thead>
                                  <tbody style={{ backgroundColor: '#0c0c0c' }}>
                                    {incentiveFarmingNodeDetails.contracts.map((c, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #1a1a1a', backgroundColor: '#0c0c0c' }}>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          <code className="text-white" style={{ fontSize: '0.8rem' }}>{c.contract?.slice(0, 6)}...{c.contract?.slice(-4)}</code>
                                        </td>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          <code className="text-white" style={{ fontSize: '0.8rem' }}>{c.method || '-'}</code>
                                        </td>
                                        <td className="py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>
                                          <span className="text-white fw-bold">{c.count?.toLocaleString() || 1}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null
                    )}
                  </div>
                )}

                {/* Graph Container */}
                <div className="rounded" style={{ backgroundColor: '#050505', position: 'relative', overflow: 'hidden', border: '1px solid #1a1a1a' }}>
                  {!incentiveFarmingActive && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <h5 className="text-secondary mt-3">Select Cluster 916 to view the network graph</h5>
                        <p className="text-secondary small">Click on the cluster to visualize the airdrop claim farming network</p>
                      </div>
                    </div>
                  )}

                  {loadingIncentiveFarmingGraph && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <div className="spinner-border text-secondary"></div>
                        <p className="text-secondary mt-3 small">Loading cluster graph...</p>
                      </div>
                    </div>
                  )}

                  <div
                    ref={incentiveFarmingContainerRef}
                    style={{
                      height: '500px',
                      width: '100%',
                      display: incentiveFarmingGraph && !loadingIncentiveFarmingGraph ? 'block' : 'none'
                    }}
                  />

                  {incentiveFarmingActive && incentiveFarmingGraph && !loadingIncentiveFarmingGraph && incentiveFarmingGraphStats && (
                    <div className="position-absolute bottom-0 start-0 end-0 m-3 p-3 rounded" style={{ backgroundColor: 'rgba(12, 12, 12, 0.9)', border: '1px solid #1a1a1a' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="small">
                          <span className="text-white fw-bold">Cluster 916</span>
                          <span className="text-secondary ms-3">
                            {incentiveFarmingGraphStats.displayedNodes?.toLocaleString()} of {incentiveFarmingGraphStats.totalNodes?.toLocaleString() || '?'} nodes • {incentiveFarmingGraphStats.displayedEdges?.toLocaleString()} edges
                          </span>
                        </div>
                        <div className="small">
                          <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b' }}>
                            Sybil Incentive Farming Risk: High
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 small text-secondary d-flex align-items-center">
                  <span className="me-3">Click nodes for wallet details</span>
                  <span className="me-3">•</span>
                  <span>Drag to pan / Scroll to zoom</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wash Trading Section - Only for signxy@proton.me */}
      {isSignxyUser && !showPlumeDashboard && (
        <div className="card text-light mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h5 className="card-title mb-2 text-white" style={{ fontWeight: 700, letterSpacing: '0.05rem' }}>
                  Wash Trading
                </h5>
                <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#9ca3af' }}>
                  Ethereum Mainnet • Sybil cluster manipulating DEX swap volume
                </p>
              </div>
              <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>eip155:1</span>
            </div>

            {/* Stats Row */}
            {washTradingStats && (
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flagged Wallets</h6>
                    <h3 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{washTradingStats.totalWallets?.toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contract Anomaly</h6>
                    <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{washTradingStats.avgContractAnomaly?.toFixed(1)}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confidence</h6>
                    <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{washTradingStats.avgConfidence?.toFixed(0)}%</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activity Period</h6>
                    <h4 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {washTradingStats.activityPeriod?.first || '-'}
                    </h4>
                  </div>
                </div>
              </div>
            )}

            {/* Cluster Analysis */}
            <div className="row g-4">
              {/* Cluster List */}
              <div className="col-lg-4">
                <div className="rounded p-4" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                  <h5 className="text-white mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '1rem' }}>Detected Clusters (1)</h5>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div
                      className="p-3 mb-2 rounded shadow-sm"
                      style={{
                        backgroundColor: washTradingActive ? '#151515' : '#0c0c0c',
                        border: washTradingActive ? '1px solid #da4b4b' : '1px solid #1a1a1a',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        setWashTradingActive(true);
                        fetchWashTradingGraph();
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="text-white fw-bold" style={{ fontSize: '1.1rem' }}>
                          Cluster 2
                        </span>
                        <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>HIGH RISK</span>
                      </div>
                      <div className="mb-3">
                        <span style={{ color: '#da4b4b', fontSize: '1rem' }}>Sybil Wash Trading Risk: High</span>
                      </div>
                      <div className="text-white" style={{ fontSize: '0.95rem' }}>
                        <div className="mb-2">
                          Target: <code style={{ fontSize: '0.85rem', color: '#5a6f8e' }}>0x7a25...488d</code>
                        </div>
                        <div className="mb-2">
                          {washTradingStats?.totalWallets?.toLocaleString() || '~530'} wallets in cluster
                        </div>
                        <div className="mb-2">
                          Method: <code style={{ fontSize: '0.85rem', color: '#5a6f8e' }}>swap()</code>
                        </div>
                        <div className="mb-1">
                          Dec 2025
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="rounded p-3 mt-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                  <h6 className="text-secondary mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>Legend</h6>
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 18, height: 18, backgroundColor: '#4a7c59', marginRight: 10, transform: 'rotate(45deg)' }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Target Contract</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Sybil Wallet</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{ width: 14, height: 2, backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Swap Interaction</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph View & Details */}
              <div className="col-lg-8">
                {/* Node Details Panel */}
                {washTradingSelectedNode && (
                  <div className="rounded p-4 mb-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary small text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem' }}>
                      {washTradingSelectedNode.isContract ? 'Target Contract' : 'Sybil Wallet'} Details
                    </h6>
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <span className="text-secondary small me-2">Address:</span>
                        <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{washTradingSelectedNode.address?.slice(0, 6)}...{washTradingSelectedNode.address?.slice(-4)}</code>
                        <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(washTradingSelectedNode.address)}>copy</button>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-secondary small me-2">Connections:</span>
                        <span className="text-white fw-bold">{washTradingSelectedNode.connections}</span>
                      </div>
                    </div>

                    {!washTradingSelectedNode.isContract && (
                      loadingWashTradingNode ? (
                        <div className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                          <span className="text-secondary">Loading wallet details...</span>
                        </div>
                      ) : washTradingNodeDetails ? (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Contract Anomaly</h6>
                              <h4 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{washTradingNodeDetails.contractAnomaly?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Transfer Anomaly</h6>
                              <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{washTradingNodeDetails.transferAnomaly?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Risk Score</h6>
                              <h4 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{washTradingNodeDetails.riskScore?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Confidence</h6>
                              <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{washTradingNodeDetails.confidence?.toFixed(0)}%</h4>
                            </div>
                          </div>
                          {washTradingNodeDetails.contracts && washTradingNodeDetails.contracts.length > 0 && (
                            <div className="col-12">
                              <h6 className="text-secondary text-uppercase fw-bold mt-2 mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                Contract Interactions ({washTradingNodeDetails.contracts.length})
                              </h6>
                              <div className="table-responsive rounded" style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.9rem', backgroundColor: '#0c0c0c' }}>
                                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0c0c0c', borderBottom: '1px solid #1a1a1a' }}>
                                    <tr>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Contract</th>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Method</th>
                                      <th className="text-secondary py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>Calls</th>
                                    </tr>
                                  </thead>
                                  <tbody style={{ backgroundColor: '#0c0c0c' }}>
                                    {washTradingNodeDetails.contracts.map((c, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #1a1a1a', backgroundColor: '#0c0c0c' }}>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          <code className="text-white" style={{ fontSize: '0.8rem' }}>{c.contract?.slice(0, 6)}...{c.contract?.slice(-4)}</code>
                                        </td>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          <code className="text-white" style={{ fontSize: '0.8rem' }}>{c.method || '-'}</code>
                                        </td>
                                        <td className="py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>
                                          <span className="text-white fw-bold">{c.count?.toLocaleString() || 1}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null
                    )}
                  </div>
                )}

                {/* Graph Container */}
                <div className="rounded" style={{ backgroundColor: '#050505', position: 'relative', overflow: 'hidden', border: '1px solid #1a1a1a' }}>
                  {!washTradingActive && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <h5 className="text-secondary mt-3">Select Cluster 2 to view the network graph</h5>
                        <p className="text-secondary small">Click on the cluster to visualize the wash trading network</p>
                      </div>
                    </div>
                  )}

                  {loadingWashTradingGraph && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <div className="spinner-border text-secondary"></div>
                        <p className="text-secondary mt-3 small">Loading cluster graph...</p>
                      </div>
                    </div>
                  )}

                  <div
                    ref={washTradingContainerRef}
                    style={{
                      height: '500px',
                      width: '100%',
                      display: washTradingGraph && !loadingWashTradingGraph ? 'block' : 'none'
                    }}
                  />

                  {washTradingActive && washTradingGraph && !loadingWashTradingGraph && washTradingGraphStats && (
                    <div className="position-absolute bottom-0 start-0 end-0 m-3 p-3 rounded" style={{ backgroundColor: 'rgba(12, 12, 12, 0.9)', border: '1px solid #1a1a1a' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="small">
                          <span className="text-white fw-bold">Cluster 2</span>
                          <span className="text-secondary ms-3">
                            {washTradingGraphStats.displayedNodes?.toLocaleString()} of {washTradingGraphStats.totalNodes?.toLocaleString() || '?'} nodes • {washTradingGraphStats.displayedEdges?.toLocaleString()} edges
                          </span>
                        </div>
                        <div className="small">
                          <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b' }}>
                            Sybil Wash Trading Risk: High
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 small text-secondary d-flex align-items-center">
                  <span className="me-3">Click nodes for wallet details</span>
                  <span className="me-3">•</span>
                  <span>Drag to pan / Scroll to zoom</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Flow of Funds Section - Only for signxy@proton.me */}
      {isSignxyUser && !showPlumeDashboard && (
        <div className="card text-light mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h5 className="card-title mb-2 text-white" style={{ fontWeight: 700, letterSpacing: '0.05rem' }}>
                  Flow of Funds
                </h5>
                <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#9ca3af' }}>
                  Ethereum Mainnet • Sybil cluster coordinating stablecoin transfers
                </p>
              </div>
              <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>eip155:1</span>
            </div>

            {flowOfFundsStats && (
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flagged Wallets</h6>
                    <h3 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{flowOfFundsStats.totalWallets?.toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contract Anomaly</h6>
                    <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{flowOfFundsStats.avgContractAnomaly?.toFixed(1)}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confidence</h6>
                    <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{flowOfFundsStats.avgConfidence?.toFixed(0)}%</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="rounded p-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activity Period</h6>
                    <h4 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {flowOfFundsStats.activityPeriod?.first || '-'}
                    </h4>
                  </div>
                </div>
              </div>
            )}

            <div className="row g-4">
              <div className="col-lg-4">
                <div className="rounded p-4" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                  <h5 className="text-white mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '1rem' }}>Detected Clusters (1)</h5>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div
                      className="p-3 mb-2 rounded shadow-sm"
                      style={{
                        backgroundColor: flowOfFundsActive ? '#151515' : '#0c0c0c',
                        border: flowOfFundsActive ? '1px solid #da4b4b' : '1px solid #1a1a1a',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        setFlowOfFundsActive(true);
                        fetchFlowOfFundsGraph();
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="text-white fw-bold" style={{ fontSize: '1.1rem' }}>
                          Cluster 21
                        </span>
                        <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>HIGH RISK</span>
                      </div>
                      <div className="mb-3">
                        <span style={{ color: '#da4b4b', fontSize: '1rem' }}>Sybil Flow of Funds Risk: High</span>
                      </div>
                      <div className="text-white" style={{ fontSize: '0.95rem' }}>
                        <div className="mb-2">
                          Target: <code style={{ fontSize: '0.85rem', color: '#5a6f8e' }}>USDT, USDC, DAI</code>
                        </div>
                        <div className="mb-2">
                          {flowOfFundsStats?.totalWallets?.toLocaleString() || '~3,398'} wallets in cluster
                        </div>
                        <div className="mb-2">
                          Method: <code style={{ fontSize: '0.85rem', color: '#5a6f8e' }}>transfer()</code>
                        </div>
                        <div className="mb-1">
                          Dec 2025
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded p-3 mt-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                  <h6 className="text-secondary mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>Legend</h6>
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 18, height: 18, backgroundColor: '#4a7c59', marginRight: 10, transform: 'rotate(45deg)' }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Stablecoin Contract</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Sybil Wallet</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{ width: 14, height: 2, backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                      <span className="text-white" style={{ fontSize: '0.9rem' }}>Transfer Interaction</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-8">
                {flowOfFundsSelectedNode && (
                  <div className="rounded p-4 mb-3" style={{ backgroundColor: '#0a0a0f', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary small text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem' }}>
                      {flowOfFundsSelectedNode.isContract ? 'Stablecoin Contract' : 'Sybil Wallet'} Details
                    </h6>
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <span className="text-secondary small me-2">Address:</span>
                        <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{flowOfFundsSelectedNode.address?.slice(0, 6)}...{flowOfFundsSelectedNode.address?.slice(-4)}</code>
                        <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(flowOfFundsSelectedNode.address)}>copy</button>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-secondary small me-2">Connections:</span>
                        <span className="text-white fw-bold">{flowOfFundsSelectedNode.connections}</span>
                      </div>
                    </div>

                    {!flowOfFundsSelectedNode.isContract && (
                      loadingFlowOfFundsNode ? (
                        <div className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                          <span className="text-secondary">Loading wallet details...</span>
                        </div>
                      ) : flowOfFundsNodeDetails ? (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Contract Anomaly</h6>
                              <h4 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{flowOfFundsNodeDetails.contractAnomaly?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Transfer Anomaly</h6>
                              <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{flowOfFundsNodeDetails.transferAnomaly?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Risk Score</h6>
                              <h4 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{flowOfFundsNodeDetails.riskScore?.toFixed(2)}</h4>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Confidence</h6>
                              <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{flowOfFundsNodeDetails.confidence?.toFixed(0)}%</h4>
                            </div>
                          </div>
                          {flowOfFundsNodeDetails.contracts && flowOfFundsNodeDetails.contracts.length > 0 && (
                            <div className="col-12">
                              <h6 className="text-secondary text-uppercase fw-bold mt-2 mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                Contract Interactions ({flowOfFundsNodeDetails.contracts.length})
                              </h6>
                              <div className="table-responsive rounded" style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.9rem', backgroundColor: '#0c0c0c' }}>
                                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0c0c0c', borderBottom: '1px solid #1a1a1a' }}>
                                    <tr>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Contract</th>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Method</th>
                                      <th className="text-secondary py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>Calls</th>
                                    </tr>
                                  </thead>
                                  <tbody style={{ backgroundColor: '#0c0c0c' }}>
                                    {flowOfFundsNodeDetails.contracts.map((c, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #1a1a1a', backgroundColor: '#0c0c0c' }}>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          <code className="text-white" style={{ fontSize: '0.8rem' }}>{c.contract?.slice(0, 6)}...{c.contract?.slice(-4)}</code>
                                        </td>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          <code className="text-white" style={{ fontSize: '0.8rem' }}>{c.method || '-'}</code>
                                        </td>
                                        <td className="py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>
                                          <span className="text-white fw-bold">{c.count?.toLocaleString() || 1}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null
                    )}
                  </div>
                )}

                <div className="rounded" style={{ backgroundColor: '#050505', position: 'relative', overflow: 'hidden', border: '1px solid #1a1a1a' }}>
                  {!flowOfFundsActive && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <h5 className="text-secondary mt-3">Select Cluster 21 to view the network graph</h5>
                        <p className="text-secondary small">Click on the cluster to visualize the flow of funds network</p>
                      </div>
                    </div>
                  )}

                  {loadingFlowOfFundsGraph && (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <div className="spinner-border text-secondary"></div>
                        <p className="text-secondary mt-3 small">Loading cluster graph...</p>
                      </div>
                    </div>
                  )}

                  <div
                    ref={flowOfFundsContainerRef}
                    style={{
                      height: '500px',
                      width: '100%',
                      display: flowOfFundsGraph && !loadingFlowOfFundsGraph ? 'block' : 'none'
                    }}
                  />

                  {flowOfFundsActive && flowOfFundsGraph && !loadingFlowOfFundsGraph && flowOfFundsGraphStats && (
                    <div className="position-absolute bottom-0 start-0 end-0 m-3 p-3 rounded" style={{ backgroundColor: 'rgba(12, 12, 12, 0.9)', border: '1px solid #1a1a1a' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="small">
                          <span className="text-white fw-bold">Cluster 21</span>
                          <span className="text-secondary ms-3">
                            {flowOfFundsGraphStats.displayedNodes?.toLocaleString()} of {flowOfFundsGraphStats.totalNodes?.toLocaleString() || '?'} nodes • {flowOfFundsGraphStats.displayedEdges?.toLocaleString()} edges
                          </span>
                        </div>
                        <div className="small">
                          <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b' }}>
                            Sybil Flow of Funds Risk: High
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 small text-secondary d-flex align-items-center">
                  <span className="me-3">Click nodes for wallet details</span>
                  <span className="me-3">•</span>
                  <span>Drag to pan / Scroll to zoom</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plume Network Data - Only shown on standalone Plume Dashboard tab */}
      {showPlumeDashboard ? (
        <>
          {/* Network Header */}
          <div className="rounded p-4 mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h1 className="text-white mb-2" style={{ fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em', textTransform: 'lowercase' }}>plume network</h1>
                <p className="text-secondary mb-0" style={{ maxWidth: '800px', textTransform: 'lowercase' }}>
                  Chain ID: eip155:98866 • Native Token: PLUME
                </p>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          {networkStats && (
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card text-light h-100" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Flagged Wallets</h6>
                        <h3 className="mb-0" style={{ fontWeight: 700, color: '#da4b4b' }}>{formatNumber(networkStats.totalWallets)}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-light h-100" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Total Transfers</h6>
                        <h3 className="mb-0 text-white" style={{ fontWeight: 700 }}>{formatNumber(networkStats.totalTransfers)}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-light h-100" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary mb-1">Activity Period</h6>
                        <h4 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          {networkStats.activityPeriod?.first || '-'} → {networkStats.activityPeriod?.last || '-'}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cluster Analysis */}
          <div className="card text-light mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
            <div className="card-body">
              <h5 className="card-title mb-4 text-white" style={{ fontWeight: 700, letterSpacing: '0.05rem' }}>
                Cluster Analysis
              </h5>

              <div className="row g-4">
                {/* Cluster List */}
                <div className="col-lg-4">
                  <div className="rounded p-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                    <h5 className="text-white mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '1rem' }}>Detected Clusters (1)</h5>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {/* Cluster Alpha */}
                      <div
                        className="p-3 mb-2 rounded shadow-sm"
                        style={{
                          backgroundColor: selectedCluster?.id === 'alpha' ? '#151515' : '#0c0c0c',
                          border: selectedCluster?.id === 'alpha' ? '1px solid #da4b4b' : '1px solid #1a1a1a',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleClusterSelect(clusterAlpha)}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="text-white fw-bold" style={{ fontSize: '1.1rem' }}>
                            Cluster Alpha
                          </span>
                          <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b', fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>HIGH RISK</span>
                        </div>
                        <div className="mb-3">
                          <span style={{ color: '#da4b4b', fontSize: '1rem' }}>Sybil Incentive Farming Risk: High</span>
                        </div>
                        <div className="text-white" style={{ fontSize: '0.95rem' }}>
                          <div className="mb-2">
                            Centroid: {formatAddress(clusterAlpha.centroidAddress)}
                          </div>
                          <div className="mb-2">
                            {formatNumber(networkStats?.totalWallets || 9716)} wallets in cluster
                          </div>
                          <div className="mb-1">
                            Sep 2025 - Oct 2025
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="rounded p-3 mt-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>Legend</h6>
                    <div>
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#5a6f8e', marginRight: 10, border: '2px solid #5a6f8e' }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>Centroid (Hub) Wallet</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>Sybil Wallet (Spoke)</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <div style={{ width: 14, height: 2, backgroundColor: '#6c757d', marginRight: 10 }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>PLUME Transfer</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Graph View & Details Panel */}
                <div className="col-lg-8">
                  {/* Node/Edge Details Panel */}
                  {(selectedNode || selectedEdge) && (
                    <div className="rounded p-4 mb-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                      {/* Node Details */}
                      {selectedNode && (
                        <>
                          <h6 className="text-secondary small text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem' }}>
                            {selectedNode.isCentroid ? 'Centroid Wallet' : 'Spoke Wallet'} Details
                          </h6>
                          <div className="mb-4">
                            <div className="d-flex align-items-center mb-2">
                              <span className="text-secondary small me-2">Address:</span>
                              <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{selectedNode.address}</code>
                              <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(selectedNode.address)}>
                                copy
                              </button>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="text-secondary small me-2">Connections:</span>
                              <span className="text-white fw-bold">{selectedNode.connections}</span>
                            </div>
                          </div>

                          {/* Contract Interaction History */}
                          {loadingNodeData ? (
                            <div className="text-center py-4">
                              <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                              <span className="text-secondary">Loading contract history...</span>
                            </div>
                          ) : nodeContractHistory && nodeContractHistory.contracts && nodeContractHistory.contracts.length > 0 ? (
                            <>
                              <h6 className="text-secondary text-uppercase fw-bold mt-4 mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                Contract Interactions ({nodeContractHistory.contracts.length})
                              </h6>
                              <div className="table-responsive rounded" style={{ maxHeight: '300px', overflowY: 'auto', backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.9rem', backgroundColor: '#0c0c0c' }}>
                                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0c0c0c', borderBottom: '1px solid #1a1a1a' }}>
                                    <tr>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Contract</th>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Method</th>
                                      <th className="text-secondary py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>Calls</th>
                                      <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Date Range</th>
                                    </tr>
                                  </thead>
                                  <tbody style={{ backgroundColor: '#0c0c0c' }}>
                                    {nodeContractHistory.contracts.map((contract, idx) => {
                                      const contractInfo = getContractDisplayInfo(contract.contract_address);
                                      const methodInfo = getMethodDisplayInfo(contract.method_selector);
                                      return (
                                      <tr key={idx} style={{ borderBottom: '1px solid #1a1a1a', backgroundColor: '#0c0c0c' }}>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          {contractInfo ? (
                                            <div>
                                              <span className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>{contractInfo.name}</span>
                                              {contractInfo.category && (
                                                <span className="badge ms-2" style={{ backgroundColor: '#5a6f8e', fontSize: '0.6rem' }}>{contractInfo.category}</span>
                                              )}
                                              <code className="d-block text-secondary" style={{ fontSize: '0.7rem' }}>{contract.contract_address.slice(0, 10)}...{contract.contract_address.slice(-6)}</code>
                                            </div>
                                          ) : (
                                            <code className="text-white" style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{contract.contract_address}</code>
                                          )}
                                        </td>
                                        <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                          {methodInfo ? (
                                            <div>
                                              <span className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>{methodInfo.name}()</span>
                                              <code className="d-block text-secondary" style={{ fontSize: '0.7rem' }}>{contract.method_selector}</code>
                                            </div>
                                          ) : (
                                            <code className="text-white">{contract.method_selector}</code>
                                          )}
                                        </td>
                                        <td className="py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>
                                          <span className="text-white fw-bold">{contract.call_count}</span>
                                        </td>
                                        <td className="py-2 px-3 text-secondary" style={{ fontSize: '0.85rem', backgroundColor: '#0c0c0c' }}>
                                          {contract.first_call ? new Date(contract.first_call).toLocaleDateString() : '-'}
                                          {' → '}
                                          {contract.last_call ? new Date(contract.last_call).toLocaleDateString() : '-'}
                                        </td>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-4 text-secondary rounded" style={{ border: '1px dashed #333' }}>
                              No contract interaction data available
                            </div>
                          )}
                        </>
                      )}

                      {/* Edge Details */}
                      {selectedEdge && (
                        <>
                          <h6 className="text-secondary small text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem' }}>
                            Transfer Edge Details
                          </h6>
                          <div className="mb-4">
                            <div className="mb-2 d-flex align-items-center">
                              <span className="text-secondary small me-2">Hub:</span>
                              <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{clusterAlpha.centroidAddress}</code>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="text-secondary small me-2">Spoke:</span>
                              <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{selectedEdge.spokeAddress || selectedEdge.target}</code>
                            </div>
                          </div>

                          {/* Transfer Data */}
                          {loadingEdgeData ? (
                            <div className="text-center py-4">
                              <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                              <span className="text-secondary">Loading transfer data...</span>
                            </div>
                          ) : edgeTransferData ? (
                            <div className="row g-3">
                              <div className="col-md-6">
                                <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                  <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Net PLUME Transferred</h6>
                                  <h4 className={`mb-0 ${edgeTransferData.netPlume >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontWeight: 700 }}>
                                    {edgeTransferData.netPlume >= 0 ? '+' : ''}{formatPlume(edgeTransferData.netPlumeWei)}
                                  </h4>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="rounded p-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                  <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Total Transfers</h6>
                                  <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>{formatNumber(edgeTransferData.totalTransfers)}</h4>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="rounded p-3 h-100" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                  <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>First Interaction</h6>
                                  <span className="text-white fw-bold" style={{ fontSize: '0.95rem' }}>{edgeTransferData.firstInteraction ? new Date(edgeTransferData.firstInteraction).toLocaleString() : '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="rounded p-3 h-100" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                  <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>Last Interaction</h6>
                                  <span className="text-white fw-bold" style={{ fontSize: '0.95rem' }}>{edgeTransferData.lastInteraction ? new Date(edgeTransferData.lastInteraction).toLocaleString() : '-'}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-4 text-secondary rounded" style={{ border: '1px dashed #333' }}>
                              No transfer data available
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Time Period Selector */}
                  {selectedCluster && timePeriods.length > 0 && (
                    <div className="rounded p-3 mb-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="text-white mb-0 text-uppercase fw-bold" style={{ letterSpacing: '0.05rem', fontSize: '0.85rem' }}>
                          Time Period
                        </h6>
                        <span className="text-secondary small">
                          {timePeriods.length} segments • ~500 nodes each
                        </span>
                      </div>
                      <select
                        className="form-select form-select-sm"
                        value={selectedPeriod || ''}
                        onChange={(e) => handlePeriodChange(parseInt(e.target.value))}
                        style={{
                          backgroundColor: '#151515',
                          color: '#fff',
                          border: '1px solid #333',
                          fontSize: '0.9rem'
                        }}
                      >
                        {timePeriods.map((period) => (
                          <option key={period.id} value={period.id}>
                            Period {period.id}: {period.dateRange} ({period.nodeCount} nodes)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Graph Container */}
                  <div className="rounded" style={{ backgroundColor: '#0c0c0c', position: 'relative', overflow: 'hidden', border: '1px solid #1a1a1a' }}>
                    {!selectedCluster && (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                        <div className="text-center">
                          <h5 className="text-secondary mt-3">Select Cluster Alpha to view the network graph</h5>
                          <p className="text-secondary small">Click on the cluster to visualize the centroid-spoke relationship</p>
                        </div>
                      </div>
                    )}

                    {loadingGraph && (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                        <div className="text-center">
                          <div className="spinner-border text-secondary"></div>
                          <p className="text-secondary mt-3 small">Loading cluster graph...</p>
                        </div>
                      </div>
                    )}

                    <div
                      ref={containerRef}
                      style={{
                        height: '500px',
                        width: '100%',
                        display: clusterGraph && !loadingGraph ? 'block' : 'none'
                      }}
                    />

                    {selectedCluster && clusterGraph && !loadingGraph && graphStats && (
                      <div className="position-absolute bottom-0 start-0 end-0 m-3 p-3 rounded" style={{ backgroundColor: 'rgba(12, 12, 12, 0.9)', border: '1px solid #1a1a1a' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="small">
                            <span className="text-white fw-bold">Cluster Alpha</span>
                            <span className="text-secondary ms-3">
                              {graphStats.displayedNodes.toLocaleString()} of {graphStats.totalNodes?.toLocaleString() || '?'} nodes • {graphStats.displayedEdges.toLocaleString()} edges
                            </span>
                            {selectedPeriod && timePeriods.length > 0 && (
                              <span className="text-secondary ms-2">
                                (Period {selectedPeriod}/{timePeriods.length})
                              </span>
                            )}
                          </div>
                          <div className="small">
                            <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b' }}>
                              Sybil Incentive Farming Risk: High
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 small text-secondary d-flex align-items-center">
                    <span className="me-3">Click nodes for contract history</span>
                    <span className="me-3">•</span>
                    <span className="me-3">Click edges for transfer data</span>
                    <span className="me-3">•</span>
                    <span>Drag to pan / Scroll to zoom</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* High Commitment Section */}
          <div className="card text-light mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="card-title mb-2 text-white" style={{ fontWeight: 700, letterSpacing: '0.05rem' }}>
                    High Commitment Wallets
                  </h5>
                  <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#9ca3af' }}>
                    MetaMorpho depositors with &gt;10 days commitment • Not flagged as sybils
                  </p>
                </div>
                {highCommitStats && (
                  <div className="d-flex gap-3">
                    <div className="text-end">
                      <span className="text-secondary small d-block">Non-Sybil Wallets</span>
                      <span className="fw-bold" style={{ fontSize: '1.5rem', color: '#4a7c59' }}>{formatNumber(highCommitStats.nonSybilWallets)}</span>
                    </div>
                    <div className="text-end">
                      <span className="text-secondary small d-block">Avg Commitment</span>
                      <span className="text-white fw-bold" style={{ fontSize: '1.5rem' }}>{highCommitStats.avgTimeCommitmentDays} days</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="row g-4">
                {/* Wallet List */}
                <div className="col-lg-4">
                  <div className="rounded p-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-white mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.9rem' }}>
                      Committed Wallets ({highCommitWallets.length})
                    </h6>

                    {loadingHighCommit ? (
                      <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-secondary"></div>
                        <p className="text-secondary small mt-2">Loading wallets...</p>
                      </div>
                    ) : highCommitWallets.length > 0 ? (
                      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {highCommitWallets.map((wallet, idx) => (
                          <div
                            key={wallet.address}
                            className="p-3 mb-2 rounded"
                            style={{
                              backgroundColor: selectedHighCommitWallet?.address === wallet.address ? '#151515' : '#0c0c0c',
                              border: selectedHighCommitWallet?.address === wallet.address ? '1px solid #4a7c59' : '1px solid #1a1a1a',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => handleHighCommitWalletSelect(wallet)}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <code className="text-white" style={{ fontSize: '0.85rem' }}>
                                  {formatAddress(wallet.address)}
                                </code>
                                <span className="badge bg-transparent" style={{ border: '1px solid #4a7c59', color: '#4a7c59', fontSize: '0.65rem', padding: '2px 4px' }}>
                                  ✓ NOT SYBIL
                                </span>
                              </div>
                              <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.75rem' }}>
                                #{wallet.leaderboardPosition}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between text-secondary small">
                              <span>{wallet.timeCommitmentDays} days</span>
                              <span>{wallet.depositCount} deps / {wallet.withdrawalCount} with</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-secondary rounded" style={{ border: '1px dashed #333' }}>
                        No high commitment wallets found
                      </div>
                    )}
                  </div>

                  {/* Legend */}
                  <div className="rounded p-3 mt-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                    <h6 className="text-secondary mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>Legend</h6>
                    <div>
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>Wallet (Not Sybil)</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ width: 18, height: 18, backgroundColor: '#5a6f8e', transform: 'rotate(45deg)', marginRight: 10 }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>MetaMorpho Contract</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ width: 20, height: 3, backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>Deposit</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <div style={{ width: 20, height: 3, backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                        <span className="text-white" style={{ fontSize: '0.9rem' }}>Withdraw</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wallet Details & Graph */}
                <div className="col-lg-8">
                  {/* Selected Wallet Details */}
                  {selectedHighCommitWallet && (
                    <div className="rounded p-4 mb-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                      <h6 className="text-secondary text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.9rem' }}>
                        Wallet Details
                      </h6>
                      <div className="row g-3 mb-3">
                        <div className="col-12">
                          <div className="d-flex align-items-center">
                            <span className="text-secondary me-2" style={{ fontSize: '0.9rem' }}>Address:</span>
                            <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.95rem' }}>{formatAddress(selectedHighCommitWallet.address)}</code>
                            <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(selectedHighCommitWallet.address)}>
                              copy
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Leaderboard</h6>
                            <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>#{selectedHighCommitWallet.leaderboardPosition}</span>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Plume Points</h6>
                            <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>{formatNumber(selectedHighCommitWallet.plumePoints)}</span>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Time Commitment</h6>
                            <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>{selectedHighCommitWallet.timeCommitmentDays} days</span>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>TVL (USD)</h6>
                            <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>${formatNumber(Math.abs(Math.round(selectedHighCommitWallet.tvlUsd || 0)))}</span>
                          </div>
                        </div>
                      </div>
                      <div className="row g-3 mt-1">
                        <div className="col-md-6">
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #4a7c59' }}>
                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Total Deposits (pUSD)</h6>
                            <span className="fw-bold" style={{ fontSize: '1.2rem', color: '#4a7c59' }}>{formatPUSD(selectedHighCommitWallet.totalDeposits)}</span>
                            <span className="text-secondary ms-2" style={{ fontSize: '0.85rem' }}>({selectedHighCommitWallet.depositCount} txns)</span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #da4b4b' }}>
                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Total Withdrawals (pUSD)</h6>
                            <span className="fw-bold" style={{ fontSize: '1.2rem', color: '#da4b4b' }}>{formatPUSD(selectedHighCommitWallet.totalWithdrawals)}</span>
                            <span className="text-secondary ms-2" style={{ fontSize: '0.85rem' }}>({selectedHighCommitWallet.withdrawalCount} txns)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Graph Container */}
                  <div className="rounded" style={{ backgroundColor: '#0c0c0c', position: 'relative', overflow: 'hidden', border: '1px solid #1a1a1a' }}>
                    {!selectedHighCommitWallet && (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                        <div className="text-center">
                          <h5 className="text-secondary mt-3">Select a wallet to view Morpho interactions</h5>
                          <p className="text-secondary small">Click on a wallet to visualize deposit/withdraw activity</p>
                        </div>
                      </div>
                    )}

                    {loadingHighCommitGraph && (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                        <div className="text-center">
                          <div className="spinner-border text-secondary"></div>
                          <p className="text-secondary mt-3 small">Loading wallet graph...</p>
                        </div>
                      </div>
                    )}

                    <div
                      ref={highCommitContainerRef}
                      style={{
                        height: '300px',
                        width: '100%',
                        display: highCommitGraph && !loadingHighCommitGraph ? 'block' : 'none'
                      }}
                    />

                    {selectedHighCommitWallet && highCommitGraph && !loadingHighCommitGraph && (
                      <div className="position-absolute bottom-0 start-0 end-0 m-3 p-3 rounded" style={{ backgroundColor: 'rgba(12, 12, 12, 0.9)', border: '1px solid #1a1a1a' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="small">
                            <span className="text-white fw-bold">{formatAddress(selectedHighCommitWallet.address)}</span>
                            <span className="text-secondary ms-3">
                              {highCommitGraph.stats?.depositCount || 0} deposits • {highCommitGraph.stats?.withdrawCount || 0} withdrawals
                            </span>
                          </div>
                          <div className="small">
                            <span className="badge bg-transparent" style={{ border: '1px solid #4a7c59', color: '#4a7c59' }}>
                              ✓ High Commitment • Not Sybil
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 small text-secondary d-flex align-items-center">
                    <span className="me-3">Green edges = deposits</span>
                    <span className="me-3">•</span>
                    <span className="me-3">Red edges = withdrawals</span>
                    <span className="me-3">•</span>
                    <span>Drag to pan / Scroll to zoom</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* User-specific dashboard - Plume Dashboard style with entity sidebar */
        <div>
          {assignedTables.length > 0 ? (
            <>
              {assignedTables.map(tableName => {
                const metadata = userTableMetadata[tableName];
                const entities = tableEntities[tableName];
                const timePeriods = tableTimePeriods[tableName];
                const tableDesc = getTableDescription(tableName);
                const isLoadingMeta = loadingTableMetadata[tableName];
                const isLoadingEnts = loadingEntities[tableName];

                return (
                  <div key={tableName} className="mb-4">

                    {/* Table Header with Description */}
                    <div className="rounded p-4 mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h2 className="text-white mb-2" style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em', fontFamily: 'Satoshi, -apple-system, sans-serif' }}>
                            {tableDesc?.title || tableName.replace(/_/g, ' ')}
                          </h2>
                          {tableDesc && (
                            <div className="mb-3">
                              <p className="text-secondary mb-1" style={{ fontSize: '1rem', maxWidth: '700px' }}>
                                {tableDesc.description}
                              </p>
                              <span className="text-secondary small">
                                <i className="bi bi-database me-1"></i>Source: {tableDesc.source}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="d-flex align-items-start gap-4">
                          {/* API Toggle Control */}
                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="text-end">
                                <span className="small" style={{ color: isTableApiEnabled(tableName) ? '#4a9d6b' : '#666', textTransform: 'lowercase' }}>
                                  {isTableApiEnabled(tableName) ? 'api enabled' : 'api disabled'}
                                </span>
                              </div>
                              <div className="form-check form-switch" style={{ marginBottom: 0 }}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isTableApiEnabled(tableName)}
                                  onChange={() => handleTableApiToggle(tableName)}
                                  style={{
                                    width: '44px',
                                    height: '22px',
                                    backgroundColor: isTableApiEnabled(tableName) ? '#4a9d6b' : '#1a1a1a',
                                    borderColor: isTableApiEnabled(tableName) ? '#4a9d6b' : '#333',
                                    cursor: 'pointer'
                                  }}
                                />
                              </div>
                            </div>
                            {entities && (
                              <div className="mt-2 pt-2" style={{ borderTop: '1px solid #1a1a1a' }}>
                                <span className="text-secondary small" style={{ fontSize: '0.75rem', textTransform: 'lowercase' }}>
                                  {getEnabledEntityCount(tableName, entities)} of {entities.entities?.length || 0} entities in api
                                </span>
                              </div>
                            )}
                          </div>
                          {/* Stats - different for high_commit table */}
                          {tableName === 'high_commit' && entities?.highCommitStats ? (
                            <div className="d-flex gap-3">
                              <div className="text-end">
                                <span className="text-secondary small d-block">Non-Sybil Wallets</span>
                                <span className="fw-bold" style={{ fontSize: '1.5rem', color: '#4a7c59' }}>{formatNumber(entities.highCommitStats.nonSybilWallets)}</span>
                              </div>
                              <div className="text-end">
                                <span className="text-secondary small d-block">Avg Commitment</span>
                                <span className="text-white fw-bold" style={{ fontSize: '1.5rem' }}>{entities.highCommitStats.avgTimeCommitmentDays} days</span>
                              </div>
                            </div>
                          ) : metadata && (
                            <div className="d-flex gap-2">
                              <div className="text-center px-3 py-2 rounded" style={{ backgroundColor: '#1a2a40' }}>
                                <span className="text-secondary small d-block fw-bold" style={{ fontSize: '0.7rem' }}>Total Rows</span>
                                <span className="fw-bold text-white" style={{ fontSize: '1.1rem' }}>{formatNumber(metadata.rowCount)}</span>
                              </div>
                              {metadata.walletCount > 0 && (
                                <div className="text-center px-3 py-2 rounded" style={{ backgroundColor: '#1a2a40' }}>
                                  <span className="text-secondary small d-block fw-bold" style={{ fontSize: '0.7rem' }}>Entities</span>
                                  <span className="fw-bold text-white" style={{ fontSize: '1.1rem' }}>{formatNumber(metadata.walletCount)}</span>
                                </div>
                              )}
                              {metadata.timePeriod && metadata.timePeriod.first && metadata.timePeriod.last && (
                                <div className="text-center px-3 py-2 rounded" style={{ backgroundColor: '#1a2a40' }}>
                                  <span className="text-secondary small d-block fw-bold" style={{ fontSize: '0.7rem' }}>Period</span>
                                  <span className="fw-bold text-white" style={{ fontSize: '0.85rem' }}>
                                    {(() => {
                                      const formatTimePeriodDate = (dateStr) => {
                                        try {
                                          const date = new Date(dateStr);
                                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                        } catch {
                                          return dateStr?.slice(5, 10) || '';
                                        }
                                      };
                                      const first = formatTimePeriodDate(metadata.timePeriod.first);
                                      const last = formatTimePeriodDate(metadata.timePeriod.last);
                                      return first === last ? first : `${first} - ${last}`;
                                    })()}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Table Schema - Collapsible (default collapsed) */}
                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #1a1a1a' }}>
                        <div
                          className="d-flex align-items-center justify-content-between"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setCollapsedSchemas(prev => ({ ...prev, [tableName]: prev[tableName] === false ? true : false }))}
                        >
                          <h6 className="text-secondary mb-0 text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                            <i className={`bi ${collapsedSchemas[tableName] === false ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
                            <i className="bi bi-code-square me-1"></i>Schema ({metadata?.columns?.length || 0} columns)
                          </h6>
                          <span className="text-secondary small" style={{ fontSize: '0.7rem' }}>
                            {collapsedSchemas[tableName] === false ? 'click to collapse' : 'click to expand'}
                          </span>
                        </div>
                        {collapsedSchemas[tableName] === false && (
                          <div className="mt-2">
                            {metadata && metadata.columns ? (
                              <div className="d-flex flex-wrap gap-2">
                                {metadata.columns.map((col, idx) => (
                                  <span key={idx} className="badge" style={{ backgroundColor: '#1a1a24', border: '1px solid #2a2a34', fontSize: '0.75rem' }}>
                                    <code className="text-white">{col.name}</code>
                                    {col.is_primary_key && <span className="ms-1" style={{ color: '#da4b4b' }}>PK</span>}
                                    <span className="text-secondary ms-1">{col.type}</span>
                                  </span>
                                ))}
                              </div>
                            ) : isLoadingMeta ? (
                              <span className="text-secondary small"><span className="spinner-border spinner-border-sm me-2"></span>Loading...</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Main Content - Entity List + Graph (like Plume Dashboard) */}
                    <div className="card text-light mb-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                      <div className="card-body">
                        <div className="row g-4">
                          {/* Left Sidebar - Entity List */}
                          <div className="col-lg-4">
                            <div className="rounded p-4" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-white mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.9rem' }}>
                                {entities?.primaryColumn || 'Entities'} ({entities?.totalEntities || 0})
                              </h6>

                              {/* Search Bar */}
                              <div className="mb-3">
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text" style={{ backgroundColor: '#1a1a24', border: '1px solid #2a2a34', color: '#666' }}>
                                    <i className="bi bi-search"></i>
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search wallet, contract, or tx..."
                                    style={{ backgroundColor: '#1a1a24', border: '1px solid #2a2a34', color: '#fff' }}
                                    value={entitySearchQuery[tableName] || ''}
                                    onChange={(e) => setEntitySearchQuery(prev => ({ ...prev, [tableName]: e.target.value }))}
                                  />
                                  {entitySearchQuery[tableName] && (
                                    <button
                                      className="btn btn-outline-secondary btn-sm"
                                      style={{ borderColor: '#2a2a34' }}
                                      onClick={() => setEntitySearchQuery(prev => ({ ...prev, [tableName]: '' }))}
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  )}
                                </div>
                              </div>


                              {/* Time Period Filter - Standard for other tables */}
                              {tableName !== 'sybil_clusters' && timePeriods && timePeriods.hasTimestamp && timePeriods.periods?.length > 0 && (
                                <div className="mb-3">
                                  <label className="text-secondary small d-block mb-2">Filter by Period</label>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ backgroundColor: '#1a1a24', border: '1px solid #2a2a34', color: '#fff' }}
                                    value={selectedTimePeriod || ''}
                                    onChange={(e) => handleTimePeriodChange(tableName, e.target.value || null)}
                                  >
                                    <option value="">All Time</option>
                                    {timePeriods.periods.map(p => (
                                      <option key={p.id} value={p.id}>{p.label} ({formatNumber(p.count)} rows)</option>
                                    ))}
                                  </select>
                                </div>
                              )}

                              {isLoadingEnts ? (
                                <div className="text-center py-4">
                                  <div className="spinner-border spinner-border-sm text-secondary"></div>
                                  <p className="text-secondary small mt-2">Loading entities...</p>
                                </div>
                              ) : entities && entities.entities?.length > 0 ? (
                                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                  {entities.entities
                                    .filter(entity => {
                                      const searchTerm = (entitySearchQuery[tableName] || '').toLowerCase();
                                      if (!searchTerm) return true;
                                      return entity.id?.toLowerCase().includes(searchTerm);
                                    })
                                    .map((entity, idx) => {
                                      const entityApiEnabled = isEntityApiEnabled(tableName, entity.id);

                                      // Special rendering for high_commit entities (Plume Dashboard style)
                                      if (entity.isHighCommit) {
                                        return (
                                          <div
                                            key={entity.id}
                                            className="p-3 mb-2 rounded"
                                            style={{
                                              backgroundColor: selectedEntity?.id === entity.id ? '#151515' : '#0c0c0c',
                                              border: selectedEntity?.id === entity.id ? '1px solid #4a7c59' : '1px solid #1a1a1a',
                                              cursor: 'pointer',
                                              opacity: entityApiEnabled ? 1 : 0.6,
                                              transition: 'all 0.2s ease'
                                            }}
                                          >
                                            <div className="d-flex justify-content-between align-items-center">
                                              <div
                                                className="flex-grow-1"
                                                onClick={() => handleEntitySelect(tableName, entity)}
                                              >
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                  <div className="d-flex align-items-center gap-2">
                                                    <code className="text-white" style={{ fontSize: '0.85rem' }}>
                                                      {formatAddress(entity.id)}
                                                    </code>
                                                    <span className="badge bg-transparent" style={{ border: '1px solid #4a7c59', color: '#4a7c59', fontSize: '0.65rem', padding: '2px 4px' }}>
                                                      ✓ NOT SYBIL
                                                    </span>
                                                  </div>
                                                  <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.75rem' }}>
                                                    #{entity.leaderboardPosition}
                                                  </span>
                                                </div>
                                                <div className="d-flex justify-content-between text-secondary small">
                                                  <span>{entity.timeCommitmentDays} days</span>
                                                  <span>{entity.depositCount} deps / {entity.withdrawalCount} with</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                  <span
                                                    className="small"
                                                    style={{
                                                      color: entityApiEnabled ? '#4a9d6b' : '#555',
                                                      fontSize: '0.65rem',
                                                      textTransform: 'lowercase'
                                                    }}
                                                  >
                                                    {entityApiEnabled ? 'in api' : 'excluded'}
                                                  </span>
                                                </div>
                                              </div>
                                              {/* Entity API Toggle */}
                                              <div
                                                className="form-check form-switch ms-2"
                                                style={{ marginBottom: 0 }}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  checked={entityApiEnabled}
                                                  onChange={() => handleEntityApiToggle(tableName, entity.id)}
                                                  disabled={!isTableApiEnabled(tableName)}
                                                  style={{
                                                    width: '32px',
                                                    height: '16px',
                                                    backgroundColor: entityApiEnabled ? '#4a9d6b' : '#1a1a1a',
                                                    borderColor: entityApiEnabled ? '#4a9d6b' : '#333',
                                                    cursor: isTableApiEnabled(tableName) ? 'pointer' : 'not-allowed',
                                                    opacity: isTableApiEnabled(tableName) ? 1 : 0.5
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Special rendering for sybil_clusters entities (Cluster Alpha style with HIGH RISK)
                                      if (tableName === 'sybil_clusters') {
                                        return (
                                          <div
                                            key={entity.id}
                                            className="p-3 mb-2 rounded"
                                            style={{
                                              backgroundColor: selectedEntity?.id === entity.id ? '#151515' : '#0c0c0c',
                                              border: selectedEntity?.id === entity.id ? '1px solid #da4b4b' : '1px solid #1a1a1a',
                                              cursor: 'pointer',
                                              opacity: entityApiEnabled ? 1 : 0.6,
                                              transition: 'all 0.2s ease'
                                            }}
                                          >
                                            <div className="d-flex justify-content-between align-items-center">
                                              <div
                                                className="flex-grow-1"
                                                onClick={() => handleEntitySelect(tableName, entity)}
                                              >
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                  <div className="d-flex align-items-center gap-2">
                                                    <code className="text-white" style={{ fontSize: '0.85rem' }}>
                                                      {formatAddress(entity.id)}
                                                    </code>
                                                  </div>
                                                  <span className="badge bg-transparent" style={{ border: '1px solid #da4b4b', color: '#da4b4b', fontSize: '0.65rem', padding: '2px 4px' }}>
                                                    HIGH RISK
                                                  </span>
                                                </div>
                                                <div className="text-secondary small mb-1">
                                                  Sybil Farming Wallet
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                  {entity.count > 0 && (
                                                    <span className="badge bg-transparent" style={{ border: '1px solid #8b3a3a', color: '#8b3a3a', fontSize: '0.65rem', padding: '2px 6px' }}>
                                                      {formatNumber(entity.count)} transfers
                                                    </span>
                                                  )}
                                                  <span
                                                    className="small"
                                                    style={{
                                                      color: entityApiEnabled ? '#4a9d6b' : '#555',
                                                      fontSize: '0.65rem',
                                                      textTransform: 'lowercase'
                                                    }}
                                                  >
                                                    {entityApiEnabled ? 'in api' : 'excluded'}
                                                  </span>
                                                </div>
                                              </div>
                                              {/* Entity API Toggle */}
                                              <div
                                                className="form-check form-switch ms-2"
                                                style={{ marginBottom: 0 }}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  checked={entityApiEnabled}
                                                  onChange={() => handleEntityApiToggle(tableName, entity.id)}
                                                  disabled={!isTableApiEnabled(tableName)}
                                                  style={{
                                                    width: '32px',
                                                    height: '16px',
                                                    backgroundColor: entityApiEnabled ? '#4a9d6b' : '#1a1a1a',
                                                    borderColor: entityApiEnabled ? '#4a9d6b' : '#333',
                                                    cursor: isTableApiEnabled(tableName) ? 'pointer' : 'not-allowed',
                                                    opacity: isTableApiEnabled(tableName) ? 1 : 0.5
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Default rendering for other entities
                                      return (
                                        <div
                                          key={entity.id}
                                          className="p-3 mb-2 rounded"
                                          style={{
                                            backgroundColor: selectedEntity?.id === entity.id ? '#151515' : '#0c0c0c',
                                            border: selectedEntity?.id === entity.id ? '1px solid #5a6f8e' : '1px solid #1a1a1a',
                                            opacity: entityApiEnabled ? 1 : 0.6,
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div
                                              className="flex-grow-1"
                                              style={{ cursor: 'pointer' }}
                                              onClick={() => handleEntitySelect(tableName, entity)}
                                            >
                                              <code className="text-white" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                                {entity.id.length > 20 ? `${entity.id.slice(0, 10)}...${entity.id.slice(-8)}` : entity.id}
                                              </code>
                                              <div className="d-flex align-items-center gap-2 mt-1">
                                                <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.65rem', padding: '2px 6px' }}>
                                                  {formatNumber(entity.count)} calls
                                                </span>
                                                <span
                                                  className="small"
                                                  style={{
                                                    color: entityApiEnabled ? '#4a9d6b' : '#555',
                                                    fontSize: '0.65rem',
                                                    textTransform: 'lowercase'
                                                  }}
                                                >
                                                  {entityApiEnabled ? 'in api' : 'excluded'}
                                                </span>
                                              </div>
                                            </div>
                                            {/* Entity API Toggle */}
                                            <div
                                              className="form-check form-switch ms-2"
                                              style={{ marginBottom: 0 }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={entityApiEnabled}
                                                onChange={() => handleEntityApiToggle(tableName, entity.id)}
                                                disabled={!isTableApiEnabled(tableName)}
                                                style={{
                                                  width: '32px',
                                                  height: '16px',
                                                  backgroundColor: entityApiEnabled ? '#4a9d6b' : '#1a1a1a',
                                                  borderColor: entityApiEnabled ? '#4a9d6b' : '#333',
                                                  cursor: isTableApiEnabled(tableName) ? 'pointer' : 'not-allowed',
                                                  opacity: isTableApiEnabled(tableName) ? 1 : 0.5
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              ) : (
                                <div className="text-center py-4 text-secondary rounded" style={{ border: '1px dashed #333' }}>
                                  No entities found
                                </div>
                              )}
                            </div>

                            {/* Legend - different for high_commit and sybil_clusters tables */}
                            <div className="rounded p-3 mt-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              <h6 className="text-secondary mb-3 text-uppercase fw-bold" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>Legend</h6>
                              {tableName === 'high_commit' ? (
                                <div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Wallet (Not Sybil)</span>
                                  </div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 18, height: 18, backgroundColor: '#5a6f8e', transform: 'rotate(45deg)', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>MetaMorpho Contract</span>
                                  </div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 20, height: 3, backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Deposit</span>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <div style={{ width: 20, height: 3, backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Withdraw</span>
                                  </div>
                                </div>
                              ) : tableName === 'sybil_clusters' ? (
                                <div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#5a6f8e', marginRight: 10, border: '2px solid #5a6f8e' }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Centroid (Hub) Wallet</span>
                                  </div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#da4b4b', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Sybil Wallet (Spoke)</span>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <div style={{ width: 14, height: 2, backgroundColor: '#6c757d', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>PLUME Transfer</span>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#5a6f8e', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Wallet</span>
                                  </div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#4a7c59', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Contract</span>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <div style={{ width: 20, height: 2, backgroundColor: '#6c757d', marginRight: 10 }}></div>
                                    <span className="text-white" style={{ fontSize: '0.9rem' }}>Relationship</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right - Entity Details & Graph */}
                          <div className="col-lg-8">
                            {/* Entity Details */}
                            {selectedEntity && (
                              <div className="rounded p-4 mb-3" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                <h6 className="text-secondary text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.9rem' }}>
                                  Entity Details
                                </h6>
                                <div className="row g-3 mb-3">
                                  <div className="col-12">
                                    <div className="d-flex align-items-center">
                                      <span className="text-secondary me-2" style={{ fontSize: '0.9rem' }}>ID:</span>
                                      <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.9rem' }}>{selectedEntity.id}</code>
                                      <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(selectedEntity.id)}>
                                        copy
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Special rendering for high_commit entity details */}
                                {selectedEntity?.isHighCommit ? (
                                  <div>
                                    {/* NOT SYBIL Badge */}
                                    <div className="mb-3">
                                      <span className="badge bg-transparent" style={{ border: '1px solid #4a7c59', color: '#4a7c59', fontSize: '0.8rem', padding: '4px 8px' }}>
                                        ✓ NOT SYBIL
                                      </span>
                                      <span className="text-secondary small ms-2">MetaMorpho depositor with &gt;10 days commitment</span>
                                    </div>

                                    {/* Key Metrics Row */}
                                    <div className="row g-3">
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Leaderboard</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>#{selectedEntity.leaderboardPosition}</span>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Plume Points</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>{formatNumber(selectedEntity.plumePoints)}</span>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Time Commitment</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>{selectedEntity.timeCommitmentDays} days</span>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>TVL (USD)</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.3rem' }}>${formatNumber(Math.abs(Math.round(selectedEntity.tvlUsd || 0)))}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Deposits & Withdrawals Row */}
                                    <div className="row g-3 mt-1">
                                      <div className="col-md-6">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #4a7c59' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Total Deposits (pUSD)</h6>
                                          <span className="fw-bold" style={{ fontSize: '1.2rem', color: '#4a7c59' }}>{formatPUSD(selectedEntity.totalDeposits)}</span>
                                          <span className="text-secondary ms-2" style={{ fontSize: '0.85rem' }}>({selectedEntity.depositCount} txns)</span>
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #da4b4b' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Total Withdrawals (pUSD)</h6>
                                          <span className="fw-bold" style={{ fontSize: '1.2rem', color: '#da4b4b' }}>{formatPUSD(selectedEntity.totalWithdrawals)}</span>
                                          <span className="text-secondary ms-2" style={{ fontSize: '0.85rem' }}>({selectedEntity.withdrawalCount} txns)</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : loadingEntityDetails ? (
                                  <div className="text-secondary small">
                                    <span className="spinner-border spinner-border-sm me-2"></span>Loading details...
                                  </div>
                                ) : entityDetails && entityDetails.walletProfile ? (
                                  /* Wallet Profile specific display */
                                  <div>
                                    {/* Labels with Source Tables - restructured with Category and Sub Categories */}
                                    {entityDetails.walletProfile.labels?.length > 0 && (() => {
                                      // Map labels to their source tables (display names)
                                      const labelToTable = {
                                        'centroid_spoke': 'sybil_clusters',
                                        'high_commit': 'high_commit',
                                        'bridger': 'bridge_to_plume',
                                        'defi_user': 'wallet_contract_interactions',
                                        'high_volume': 'wallet_transfers'
                                      };
                                      // Matte color palette matching the dashboard theme
                                      const labelColors = {
                                        'centroid_spoke': '#8b5a5a',
                                        'high_commit': '#4a7c59',
                                        'bridger': '#5a6f8e',
                                        'defi_user': '#7c5a8b',
                                        'contract_deployer': '#8b7a5a',
                                        'high_volume': '#5a8b7c',
                                        'nft_trader': '#8b5a7c'
                                      };
                                      // Display name overrides
                                      const labelDisplayNames = {
                                        'centroid_spoke': 'SYBIL INCENTIVE FARMER'
                                      };
                                      // Sub-category labels
                                      const subCategoryLabels = ['high_volume', 'defi_user', 'bridger'];
                                      // Main category is centroid_spoke
                                      const mainCategory = entityDetails.walletProfile.labels.find(l => l === 'centroid_spoke');
                                      // Sub categories
                                      const subCategories = entityDetails.walletProfile.labels.filter(l => subCategoryLabels.includes(l));
                                      // Other labels (like high_commit)
                                      const otherLabels = entityDetails.walletProfile.labels.filter(l => l !== 'centroid_spoke' && !subCategoryLabels.includes(l));

                                      return (
                                        <div className="mb-3">
                                          {/* Main Category */}
                                          {mainCategory && (
                                            <div className="mb-3">
                                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Category</h6>
                                              <div className="rounded p-2" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a', display: 'inline-block' }}>
                                                <span
                                                  className="badge bg-transparent text-uppercase"
                                                  style={{
                                                    border: `1px solid ${labelColors[mainCategory]}`,
                                                    color: labelColors[mainCategory],
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.03rem',
                                                    padding: '4px 8px'
                                                  }}
                                                >
                                                  {labelDisplayNames[mainCategory] || mainCategory.replace(/_/g, ' ')}
                                                </span>
                                                <span className="text-secondary small ms-2" style={{ fontSize: '0.7rem' }}>
                                                  from <span style={{ color: '#6c757d', fontFamily: 'monospace', fontSize: '0.65rem' }}>{labelToTable[mainCategory]}</span>
                                                </span>
                                              </div>
                                            </div>
                                          )}

                                          {/* Sub Categories */}
                                          {subCategories.length > 0 && (
                                            <div className="mb-3">
                                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Sub Categories</h6>
                                              <div className="d-flex flex-wrap gap-2">
                                                {subCategories.map(label => {
                                                  const sourceTable = labelToTable[label] || null;
                                                  const color = labelColors[label] || '#6c757d';
                                                  return (
                                                    <div key={label} className="rounded p-2" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                                      <span
                                                        className="badge bg-transparent text-uppercase"
                                                        style={{
                                                          border: `1px solid ${color}`,
                                                          color: color,
                                                          fontSize: '0.7rem',
                                                          fontWeight: 600,
                                                          letterSpacing: '0.03rem',
                                                          padding: '4px 8px'
                                                        }}
                                                      >
                                                        {label.replace(/_/g, ' ')}
                                                      </span>
                                                      {sourceTable && (
                                                        <span className="text-secondary small ms-2" style={{ fontSize: '0.7rem' }}>
                                                          from <span style={{ color: '#6c757d', fontFamily: 'monospace', fontSize: '0.65rem' }}>{sourceTable}</span>
                                                        </span>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}

                                          {/* Other Labels */}
                                          {otherLabels.length > 0 && (
                                            <div className="mb-3">
                                              <h6 className="text-secondary text-uppercase fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Other Labels</h6>
                                              <div className="d-flex flex-wrap gap-2">
                                                {otherLabels.map(label => {
                                                  const sourceTable = labelToTable[label] || null;
                                                  const color = labelColors[label] || '#6c757d';
                                                  return (
                                                    <div key={label} className="rounded p-2" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                                      <span
                                                        className="badge bg-transparent text-uppercase"
                                                        style={{
                                                          border: `1px solid ${color}`,
                                                          color: color,
                                                          fontSize: '0.7rem',
                                                          fontWeight: 600,
                                                          letterSpacing: '0.03rem',
                                                          padding: '4px 8px'
                                                        }}
                                                      >
                                                        {label.replace(/_/g, ' ')}
                                                      </span>
                                                      {sourceTable && (
                                                        <span className="text-secondary small ms-2" style={{ fontSize: '0.7rem' }}>
                                                          from <span style={{ color: '#6c757d', fontFamily: 'monospace', fontSize: '0.65rem' }}>{sourceTable}</span>
                                                        </span>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {/* Stats */}
                                    <div className="row g-3">
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Transactions</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.2rem' }}>{formatNumber(entityDetails.walletProfile.txCount)}</span>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Contracts</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.2rem' }}>{formatNumber(entityDetails.walletProfile.contractCount)}</span>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                          <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Counterparties</h6>
                                          <span className="text-white fw-bold" style={{ fontSize: '1.2rem' }}>{formatNumber(entityDetails.walletProfile.counterpartyCount)}</span>
                                        </div>
                                      </div>
                                      {entityDetails.walletProfile.firstTx && (
                                        <div className="col-md-3">
                                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.05rem' }}>Activity</h6>
                                            <span className="text-white" style={{ fontSize: '0.8rem' }}>
                                              {entityDetails.walletProfile.firstTx?.slice(0, 10)} → {entityDetails.walletProfile.lastTx?.slice(0, 10)}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : entityDetails && entityDetails.stats ? (
                                  <div className="row g-3">
                                    <div className="col-md-3">
                                      <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                        <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Total Transactions</h6>
                                        <span className="text-white fw-bold" style={{ fontSize: '1.2rem' }}>{formatNumber(entityDetails.stats.totalRows)}</span>
                                      </div>
                                    </div>
                                    {entityDetails.stats.firstSeen && (
                                      <>
                                        <div className="col-md-4">
                                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem' }}>First Seen</h6>
                                            <span className="text-white" style={{ fontSize: '0.9rem' }}>{entityDetails.stats.firstSeen?.slice(0, 10)}</span>
                                          </div>
                                        </div>
                                        <div className="col-md-4">
                                          <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Last Seen</h6>
                                            <span className="text-white" style={{ fontSize: '0.9rem' }}>{entityDetails.stats.lastSeen?.slice(0, 10)}</span>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ) : null}

                                {/* Edge Transactions - when an edge is clicked */}
                                {selectedGraphEdge && (
                                  <div className="mt-4">
                                    <h6 className="text-secondary text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                      <i className="bi bi-arrow-right me-2"></i>
                                      Transactions ({edgeTransactions?.transactions?.length || selectedGraphEdge.callCount || 0})
                                    </h6>
                                    {loadingEdgeTransactions ? (
                                      <div className="text-secondary">
                                        <span className="spinner-border spinner-border-sm me-2"></span>Loading transactions...
                                      </div>
                                    ) : edgeTransactions?.transactions?.length > 0 ? (
                                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {edgeTransactions.transactions.map((tx, idx) => (
                                          <div key={idx} className="rounded p-3 mb-2" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                              {/* Transaction Hash with copy button */}
                                              <div className="d-flex align-items-center gap-2">
                                                <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Tx:</span>
                                                <code className="text-white" style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                                                  {tx.tx_id ? `${tx.tx_id.slice(0, 10)}...${tx.tx_id.slice(-8)}` : 'N/A'}
                                                </code>
                                                {tx.tx_id && (
                                                  <button
                                                    className="btn btn-link btn-sm p-0 text-secondary"
                                                    style={{ fontSize: '0.75rem' }}
                                                    onClick={() => navigator.clipboard.writeText(tx.tx_id)}
                                                    title="Copy full hash"
                                                  >
                                                    <i className="bi bi-clipboard"></i>
                                                  </button>
                                                )}
                                              </div>

                                              {/* Transaction Type for high_commit (deposit/withdraw) */}
                                              {tx.type && (tx.type === 'deposit' || tx.type === 'withdraw') && (
                                                <div className="d-flex align-items-center gap-2">
                                                  <span
                                                    className="badge bg-transparent"
                                                    style={{
                                                      border: `1px solid ${tx.type === 'deposit' ? '#4a7c59' : '#da4b4b'}`,
                                                      color: tx.type === 'deposit' ? '#4a7c59' : '#da4b4b',
                                                      fontSize: '0.75rem',
                                                      padding: '0.25rem 0.5rem'
                                                    }}
                                                  >
                                                    {tx.type === 'deposit' ? '↓ DEPOSIT' : '↑ WITHDRAW'}
                                                  </span>
                                                </div>
                                              )}

                                              {/* Method */}
                                              {(tx.method_name || tx.method_id || tx.method) && (() => {
                                                const methodInfo = getMethodDisplayInfo(tx.method_id || tx.method);
                                                const displayMethodName = methodInfo?.name || tx.method_name || tx.method || 'Unknown';
                                                return (
                                                <div className="d-flex align-items-center gap-2">
                                                  <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Method:</span>
                                                  <span className="badge bg-transparent" style={{ border: '1px solid #5a6f8e', color: '#5a6f8e', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                                                    {displayMethodName}()
                                                  </span>
                                                </div>
                                                );
                                              })()}

                                              {/* Assets/Amount for high_commit */}
                                              {tx.assets && (
                                                <div className="d-flex align-items-center gap-2">
                                                  <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Amount:</span>
                                                  <span className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>
                                                    {(parseFloat(tx.assets) / 1e6).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pUSD
                                                  </span>
                                                </div>
                                              )}

                                              {/* Date/Time */}
                                              {tx.block_time && (
                                                <div className="d-flex align-items-center gap-2">
                                                  <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Time:</span>
                                                  <span className="text-white" style={{ fontSize: '0.85rem' }}>
                                                    {tx.block_time.slice(0, 19).replace('T', ' ')}
                                                  </span>
                                                </div>
                                              )}

                                              {/* Block - only show if non-zero */}
                                              {tx.block_number > 0 && (
                                                <div className="d-flex align-items-center gap-2">
                                                  <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Block:</span>
                                                  <span className="text-white" style={{ fontSize: '0.85rem' }}>
                                                    {tx.block_number.toLocaleString()}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-secondary">No transaction details available</div>
                                    )}
                                  </div>
                                )}

                                {/* Contract Node Details - when a contract node is clicked */}
                                {selectedGraphNode && selectedGraphNode.type === 'contract' && (() => {
                                  const knownContract = getContractDisplayInfo(selectedGraphNode.id);
                                  return (
                                  <div className="mt-4">
                                    <h6 className="text-secondary text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                      <i className="bi bi-file-earmark-code me-2"></i>
                                      Contract Details
                                    </h6>
                                    {loadingContractDetails && !knownContract ? (
                                      <div className="text-secondary small">
                                        <span className="spinner-border spinner-border-sm me-2"></span>Loading contract info...
                                      </div>
                                    ) : knownContract || contractDetails ? (
                                      <div className="rounded p-3" style={{ backgroundColor: '#151515', border: '1px solid #1a1a1a' }}>
                                        <div className="mb-2">
                                          <span className="text-secondary small">Name:</span>
                                          <span className="text-white ms-2 fw-bold">{knownContract?.name || contractDetails?.name || 'Unknown Contract'}</span>
                                          {(knownContract || contractDetails?.known) && (
                                            <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>Verified</span>
                                          )}
                                        </div>
                                        {knownContract?.category && (
                                          <div className="mb-2">
                                            <span className="text-secondary small">Category:</span>
                                            <span className="badge ms-2" style={{ backgroundColor: '#5a6f8e', fontSize: '0.7rem' }}>{knownContract.category}</span>
                                          </div>
                                        )}
                                        <div className="mb-2">
                                          <span className="text-secondary small">Chain:</span>
                                          <span className="text-white ms-2">Plume Network</span>
                                        </div>
                                        {(knownContract?.description || contractDetails?.description) && (
                                          <div className="mb-2">
                                            <span className="text-secondary small d-block mb-1">Description:</span>
                                            <span className="text-white small">{knownContract?.description || contractDetails?.description}</span>
                                          </div>
                                        )}
                                        {contractDetails?.methods && contractDetails.methods.length > 0 && (
                                          <div>
                                            <span className="text-secondary small d-block mb-1">Methods:</span>
                                            <div className="d-flex flex-wrap gap-1">
                                              {contractDetails.methods.map((m, idx) => {
                                                const methodInfo = getMethodDisplayInfo(m.selector);
                                                return (
                                                  <span key={idx} className="badge" style={{ backgroundColor: '#4a7c59', fontSize: '0.7rem' }}>
                                                    {methodInfo?.name || m.name}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                        <div className="mt-2">
                                          <code className="text-secondary" style={{ fontSize: '0.7rem' }}>{selectedGraphNode.id}</code>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-secondary small">Contract not found in taxonomy</div>
                                    )}
                                  </div>
                                  );
                                })()}

                                {/* Sybil Cluster Node Contract History - when a wallet node is clicked in sybil_clusters */}
                                {selectedEntityTable === 'sybil_clusters' && selectedGraphNode && selectedGraphNode.type !== 'contract' && (
                                  <div className="mt-4">
                                    <h6 className="text-secondary text-uppercase fw-bold mb-3" style={{ letterSpacing: '0.1rem', fontSize: '0.85rem' }}>
                                      <i className="bi bi-wallet2 me-2"></i>
                                      Contract Interaction History
                                    </h6>
                                    <div className="mb-2">
                                      <span className="text-secondary small me-2">Wallet:</span>
                                      <code className="text-white bg-dark px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>{selectedGraphNode.fullAddress || selectedGraphNode.id}</code>
                                      <button className="btn btn-link btn-sm p-0 ms-2 text-secondary" onClick={() => navigator.clipboard.writeText(selectedGraphNode.fullAddress || selectedGraphNode.id)}>
                                        copy
                                      </button>
                                    </div>
                                    {loadingSybilNodeData ? (
                                      <div className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                                        <span className="text-secondary">Loading contract history...</span>
                                      </div>
                                    ) : sybilNodeContractHistory && sybilNodeContractHistory.contracts && sybilNodeContractHistory.contracts.length > 0 ? (
                                      <div className="table-responsive rounded" style={{ maxHeight: '300px', overflowY: 'auto', backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                                        <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.9rem', backgroundColor: '#0c0c0c' }}>
                                          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0c0c0c', borderBottom: '1px solid #1a1a1a' }}>
                                            <tr>
                                              <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Contract</th>
                                              <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Method</th>
                                              <th className="text-secondary py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>Calls</th>
                                              <th className="text-secondary py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>Date Range</th>
                                            </tr>
                                          </thead>
                                          <tbody style={{ backgroundColor: '#0c0c0c' }}>
                                            {sybilNodeContractHistory.contracts.map((contract, idx) => {
                                              const contractInfo = getContractDisplayInfo(contract.contract_address);
                                              const methodInfo = getMethodDisplayInfo(contract.method_selector);
                                              return (
                                              <tr key={idx} style={{ borderBottom: '1px solid #1a1a1a', backgroundColor: '#0c0c0c' }}>
                                                <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                                  {contractInfo ? (
                                                    <div>
                                                      <span className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>{contractInfo.name}</span>
                                                      {contractInfo.category && (
                                                        <span className="badge ms-2" style={{ backgroundColor: '#5a6f8e', fontSize: '0.6rem' }}>{contractInfo.category}</span>
                                                      )}
                                                      <code className="d-block text-secondary" style={{ fontSize: '0.7rem' }}>{contract.contract_address.slice(0, 10)}...{contract.contract_address.slice(-6)}</code>
                                                    </div>
                                                  ) : (
                                                    <code className="text-white" style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{contract.contract_address}</code>
                                                  )}
                                                </td>
                                                <td className="py-2 px-3" style={{ backgroundColor: '#0c0c0c' }}>
                                                  {methodInfo ? (
                                                    <div>
                                                      <span className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>{methodInfo.name}()</span>
                                                      <code className="d-block text-secondary" style={{ fontSize: '0.7rem' }}>{contract.method_selector}</code>
                                                    </div>
                                                  ) : (
                                                    <code className="text-white">{contract.method_selector}</code>
                                                  )}
                                                </td>
                                                <td className="py-2 px-3 text-end" style={{ backgroundColor: '#0c0c0c' }}>
                                                  <span className="text-white fw-bold">{contract.call_count}</span>
                                                </td>
                                                <td className="py-2 px-3 text-secondary" style={{ fontSize: '0.85rem', backgroundColor: '#0c0c0c' }}>
                                                  {contract.first_call ? new Date(contract.first_call).toLocaleDateString() : '-'}
                                                  {' → '}
                                                  {contract.last_call ? new Date(contract.last_call).toLocaleDateString() : '-'}
                                                </td>
                                              </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-center py-4 text-secondary rounded" style={{ border: '1px dashed #333' }}>
                                        No contract interaction data available for this wallet
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Graph View */}
                            <div className="rounded position-relative" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                              {/* Time Period Filter Overlay - for sybil_clusters (positioned over graph like Plume Dashboard) */}
                              {tableName === 'sybil_clusters' && sybilClusterTimePeriods.length > 0 && (
                                <div className="position-absolute d-flex align-items-center gap-3 px-3 py-2 rounded" style={{ top: 10, left: 10, zIndex: 20, backgroundColor: 'rgba(12, 12, 12, 0.9)', border: '1px solid #1a1a1a' }}>
                                  <span className="text-secondary small" style={{ fontSize: '0.75rem' }}>
                                    Time Period
                                  </span>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{
                                      backgroundColor: '#151515',
                                      border: '1px solid #333',
                                      color: '#fff',
                                      fontSize: '0.8rem',
                                      minWidth: '220px'
                                    }}
                                    value={selectedSybilPeriod || ''}
                                    onChange={(e) => handleSybilPeriodChange(parseInt(e.target.value))}
                                  >
                                    {sybilClusterTimePeriods.map((period) => (
                                      <option key={period.id} value={period.id}>
                                        Period {period.id}: {period.dateRange} ({period.nodeCount} nodes)
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}

                              {/* Always render the graph container to avoid React DOM conflicts */}
                              <div style={{ height: '400px', backgroundColor: '#050505', position: 'relative' }}>
                                {/* Cytoscape container - always mounted, always visible for proper rendering */}
                                <div
                                  ref={el => { if (el) userGraphContainerRefs.current[`entity-graph-${tableName}`] = el; }}
                                  style={{
                                    height: '100%',
                                    width: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 1
                                  }}
                                />

                                {/* Overlay states - positioned above graph with higher z-index */}
                                {!selectedEntity && (
                                  <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, backgroundColor: '#050505' }}>
                                    <div className="text-center text-secondary">
                                      <i className="bi bi-hand-index fs-1 mb-2 d-block"></i>
                                      <div>Select an entity from the list</div>
                                      <div className="small">to view its relationship graph</div>
                                    </div>
                                  </div>
                                )}

                                {selectedEntity && loadingEntityGraph && (
                                  <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, backgroundColor: '#050505' }}>
                                    <div className="text-center">
                                      <div className="spinner-border text-secondary mb-2"></div>
                                      <div className="text-secondary small">Loading graph...</div>
                                    </div>
                                  </div>
                                )}

                                {selectedEntity && !loadingEntityGraph && (!entityGraph || !entityGraph.graph?.nodes?.length) && (
                                  <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, backgroundColor: '#050505' }}>
                                    <div className="text-center text-secondary">
                                      <i className="bi bi-diagram-3 fs-1 mb-2 d-block"></i>
                                      <div>No graph data for this entity</div>
                                    </div>
                                  </div>
                                )}
                              </div>

                            </div>

                            <div className="mt-3 small text-secondary d-flex align-items-center">
                              <span className="me-3">Click entity in list to view graph</span>
                              <span className="me-3">•</span>
                              <span>Drag to pan / Scroll to zoom</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="rounded p-5 text-center" style={{ backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a' }}>
              <i className="bi bi-diagram-3 fs-1 text-secondary mb-3 d-block"></i>
              <h5 className="text-white mb-2">No Tables Assigned</h5>
              <p className="text-secondary mb-0" style={{ maxWidth: '500px', margin: '0 auto' }}>
                This user doesn't have any table views assigned yet.
                Go to the <strong>Query Console</strong> and assign tables to this user's dashboard.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedDashboard;
